import React, { useState, useEffect, useRef, useMemo } from 'react';
import ForceGraph3D from 'react-force-graph-3d'; // 3D Network view (Part 2); pulls in three
import { APIProvider, Map, AdvancedMarker, useMap, useMapsLibrary } from '@vis.gl/react-google-maps';
import { MapPin, Map as MapIcon, Database, Camera, X, CheckCircle, ChevronRight, Navigation, RefreshCw, RotateCcw, Save, ScanLine, Users, Building, Star, Crosshair, Loader2, Search as SearchIcon, Hammer, Shield, Layers, MessageCircle, Mic, Plus, Globe, Mail, Ban, Edit2, Network, Trash2, DollarSign, Send } from 'lucide-react';

// 🔥 FIREBASE CORE INJECTED HERE 🔥
import { initializeApp, getApps, getApp } from "firebase/app";
import { getFirestore, collection, addDoc, doc, getDoc, getDocs, query, where, setDoc, deleteDoc, onSnapshot, serverTimestamp } from "firebase/firestore";
import { getStorage, ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { getFunctions, httpsCallable } from "firebase/functions"; // server-side OCR + classify endpoints
import heic2any from "heic2any"; // convert iPhone HEIC/HEIF photos to JPEG in the browser before upload
import { auth, ROLES, preauthorizeEmail } from "./firebase"; // shared user + roles
import { signOut } from "firebase/auth";
// ONE canonical phase list shared with App.jsx — see src/phases.js (no divergent copies).
import { PHASES, PHASE_LABEL, CANONICAL_PHASE_VERSION, isCanonicalPhased } from "./phases";
import BrandLogo from "./BrandLogo";

// TerraDraw replaces Google's removed DrawingManager (deprecated Aug 2025, removed
// May 2026). terra-draw is the drawing engine; the adapter binds it to the Google map.
import { TerraDraw, TerraDrawPolygonMode, TerraDrawLineStringMode, TerraDrawSelectMode, TerraDrawModeUndoRedo } from "terra-draw";
import { TerraDrawGoogleMapsAdapter } from "terra-draw-google-maps-adapter";

// Config now comes from environment variables (.env). Note: a browser Maps/Firebase
// key is always visible in the shipped bundle — the real protection is the HTTP
// referrer restriction in the Google Cloud Console, not hiding the value here.
const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
  measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID
};

// Guarded so this shares ONE Firebase app with firebase.js (no duplicate-app error).
const app = getApps().length ? getApp() : initializeApp(firebaseConfig);
const db = getFirestore(app);
const storage = getStorage(app);
// Callable Cloud Function for on-demand Vision OCR — runs server-side (ADC, no key
// in the browser). Region must match the deployed function (us-central1).
const functions = getFunctions(app, 'us-central1');
const ocrPhotoFn = httpsCallable(functions, 'ocrPhoto');
const classifyBusinessesFn = httpsCallable(functions, 'classifyBusinesses');
const transcribeAudioFn = httpsCallable(functions, 'transcribeAudio');
const fetchCityPermitFn = httpsCallable(functions, 'fetchCityPermit');
const enrichBusinessFn = httpsCallable(functions, 'enrichBusiness');

// iPhone photos are HEIC/HEIF — Storage accepts them but browsers can't render them and
// Vision can't OCR them. Detect by MIME type or file extension so we can convert to JPEG.
const isHeic = (file, name) => {
  const t = ((file && file.type) || '').toLowerCase();
  const n = (name || (file && file.name) || '').toLowerCase();
  return t === 'image/heic' || t === 'image/heif' || /\.(heic|heif)$/.test(n);
};

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

const doSignOut = () => signOut(auth); // real Firebase sign-out -> returns to login screen

// Reverse-geocode a lat/lng to a street address. Surfaces Google's real status
// (e.g. REQUEST_DENIED, OVER_QUERY_LIMIT) and error_message instead of swallowing
// it, so a failing address lookup tells us exactly why (billing, key, referrer...).
const reverseGeocode = async (lat, lng) => {
  const url = `https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lng}&key=${GOOGLE_MAPS_API_KEY}`;
  const response = await fetch(url);
  const data = await response.json();
  if (data.status !== "OK") {
    const reason = `Geocoding failed: ${data.status}${data.error_message ? " — " + data.error_message : ""}`;
    console.error(reason, data);
    throw new Error(reason);
  }
  return data.results?.[0]?.formatted_address || "";
};

// Forward-geocode an address string → { lat, lng, address }. Throws with the real status.
const geocodeAddress = async (address) => {
  const url = `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(address)}&key=${GOOGLE_MAPS_API_KEY}`;
  const res = await fetch(url);
  const data = await res.json();
  if (data.status !== "OK" || !data.results || !data.results.length) {
    throw new Error(`Address lookup failed: ${data.status}${data.error_message ? " — " + data.error_message : ""}`);
  }
  const r = data.results[0];
  return { lat: r.geometry.location.lat, lng: r.geometry.location.lng, address: r.formatted_address };
};

// Length of a route's points via the Google Maps geometry library (spherical). Returns ""
// until that library has loaded. Used for live "· 1.4 km" labels (never stored, so never stale).
const formatDistance = (pts) => {
  const g = window.google && window.google.maps && window.google.maps.geometry;
  if (!g || !pts || pts.length < 2) return '';
  const path = pts.map(p => new window.google.maps.LatLng(p.lat, p.lng));
  const m = g.spherical.computeLength(path);
  return m >= 1000 ? `${(m / 1000).toFixed(1)} km` : `${Math.round(m)} m`;
};
// Display label for a route: "Route N · 12 pts · 1.4 km" (distance computed live).
const routeLabel = (i, pts) => { const d = formatDistance(pts); return `Route ${i + 1} · ${pts.length} pts${d ? ' · ' + d : ''}`; };
// Label including the given name: "Route 2 · North Sweep · 12 pts · 1.4 km" (routePtsOf defined below).
const routeLabelNamed = (i, r) => { const pts = routePtsOf(r); const d = formatDistance(pts); const nm = (r && r.name && r.name !== `Route ${i + 1}`) ? ` · ${r.name}` : ''; return `Route ${i + 1}${nm} · ${pts.length} pts${d ? ' · ' + d : ''}`; };

// Normalize a saved route to its point array (handles {points} objects + legacy [[...]]).
const routePtsOf = (r) => Array.isArray(r) ? r : (r && r.points) || [];

// Pure-JS haversine (km) — works everywhere, even where the Google geometry lib hasn't
// loaded (e.g. the collector profile / manager stats panels render outside a map). Stays
// within ~0.5% of Google's spherical length, so it matches the "Route 1 · 7.3 km" labels.
const haversineKm = (a, b) => {
  const R = 6371, toRad = (x) => (x * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat), dLng = toRad(b.lng - a.lng);
  const s = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(s));
};
const routeKmNum = (pts) => { if (!pts || pts.length < 2) return 0; let km = 0; for (let i = 1; i < pts.length; i++) km += haversineKm(pts[i - 1], pts[i]); return km; };
// Aggregate distance/route/block math for the stats panels (Change C).
const aggregateBlockStats = (blocks) => {
  const list = (blocks || []).map((b) => {
    const routes = Array.isArray(b.routes) ? b.routes : [];
    const routeStats = routes.map((r, i) => { const pts = routePtsOf(r); return { name: (r && r.name && r.name !== `Route ${i + 1}`) ? r.name : `Route ${i + 1}`, pts: pts.length, km: routeKmNum(pts) }; });
    return { id: b.id, name: b.name, routeCount: routes.length, km: routeStats.reduce((a, r) => a + r.km, 0), routes: routeStats };
  });
  return { blocks: list, totalBlocks: list.length, totalRoutes: list.reduce((a, b) => a + b.routeCount, 0), totalKm: list.reduce((a, b) => a + b.km, 0) };
};

// Shared Block & Route stats panel (rendered in both manager + collector views).
const BlockRouteStats = ({ blocks, dark }) => {
  const s = aggregateBlockStats(blocks);
  const cardCls = dark ? 'bg-gray-800 border-gray-700 text-gray-200' : 'bg-white border-gray-200 text-gray-800';
  const subCls = dark ? 'text-gray-400' : 'text-gray-500';
  const headCls = dark ? 'text-gray-400' : 'text-gray-400';
  if (!s.totalBlocks) return <p className={`text-xs ${subCls}`}>No blocks published yet.</p>;
  return (
    <div className="space-y-2">
      <div className="grid grid-cols-3 gap-2">
        <div className={`rounded-xl border p-2 text-center ${cardCls}`}><div className="text-lg font-black">{s.totalBlocks}</div><div className={`text-[10px] font-bold uppercase ${headCls}`}>blocks</div></div>
        <div className={`rounded-xl border p-2 text-center ${cardCls}`}><div className="text-lg font-black">{s.totalRoutes}</div><div className={`text-[10px] font-bold uppercase ${headCls}`}>routes</div></div>
        <div className={`rounded-xl border p-2 text-center ${cardCls}`}><div className="text-lg font-black">{s.totalKm.toFixed(1)}</div><div className={`text-[10px] font-bold uppercase ${headCls}`}>total km</div></div>
      </div>
      {s.blocks.map((b) => (
        <div key={b.id} className={`rounded-xl border p-2 ${cardCls}`}>
          <div className="flex items-center justify-between"><span className="font-bold text-sm truncate">{b.name}</span><span className={`text-[11px] font-bold ${subCls}`}>{b.routeCount} route{b.routeCount === 1 ? '' : 's'} · {b.km.toFixed(1)} km</span></div>
          <div className="mt-1 space-y-0.5">
            {b.routes.map((r, i) => (<div key={i} className={`flex items-center justify-between text-[11px] ${subCls}`}><span className="truncate">{r.name} · {r.pts} pts</span><span>{r.km.toFixed(1)} km</span></div>))}
            {b.routes.length === 0 && <div className={`text-[11px] ${subCls}`}>No routes drawn.</div>}
          </div>
        </div>
      ))}
    </div>
  );
};

// Km along a route polyline from its start up to point `idx` (inclusive), via geometry lib.
const kmAlong = (pts, idx) => {
  const g = window.google && window.google.maps && window.google.maps.geometry;
  if (!g || !pts || pts.length < 2 || idx < 1) return 0;
  const sub = pts.slice(0, idx + 1).map(p => new window.google.maps.LatLng(p.lat, p.lng));
  return g.spherical.computeLength(sub) / 1000;
};

// ── SWAPPABLE PROGRESS CORE ────────────────────────────────────────────────────
// Snap `coord` to the nearest route point and advance progress (forward only). Returns
// the new { progressIndex, kmPassed }. The collector's site-tap calls this NOW; a GPS
// feed will call the EXACT same function with a GPS coord in Phase 2 — nothing else changes.
const advanceAlongRoute = (routePts, coord, currentIndex) => {
  const cur = currentIndex || 0;
  if (!routePts || routePts.length === 0) return { progressIndex: cur, kmPassed: 0 };
  const g = window.google && window.google.maps && window.google.maps.geometry;
  let nearest = 0, best = Infinity;
  routePts.forEach((p, i) => {
    let d;
    if (g) d = g.spherical.computeDistanceBetween(new window.google.maps.LatLng(p.lat, p.lng), new window.google.maps.LatLng(coord.lat, coord.lng));
    else { const dy = p.lat - coord.lat, dx = p.lng - coord.lng; d = dy * dy + dx * dx; }
    if (d < best) { best = d; nearest = i; }
  });
  // Ignore a tap far off the route (>150 m) so a stray tap can't jump progress.
  if (g && best > 150) return { progressIndex: cur, kmPassed: kmAlong(routePts, cur) };
  const progressIndex = Math.max(cur, nearest);
  return { progressIndex, kmPassed: kmAlong(routePts, progressIndex) };
};

// GPS smoothing/outlier gates for live distance tracking (Fix #1 — kills phantom jumps
// that over-counted km and corrupted collector pay). Tuned to drop only clearly-bad fixes:
const GPS_ACCURACY_GATE_M = 35;     // ignore fixes whose reported accuracy is worse than this
const GPS_MAX_WALK_SPEED_MS = 3.3;  // ≈12 km/h between fixes ⇒ a noisy leap, not real walking
const GPS_MIN_MOVE_M = 2;           // sub-2 m change = jitter (don't treat as a moved leg)

// Deterministic route_session doc id. NOTE: route identity = block_id + routeIndex.
// ⚠️ routeIndex is the route's position in the block's `routes` array — it BREAKS if routes
// are deleted/reordered within a block. Phase 1.5 (real route ids) must replace this.
const sessionKey = (collector, blockId, routeIndex) =>
  `${(collector || '').trim().toLowerCase().replace(/[^a-z0-9]+/g, '-')}__${blockId}__${routeIndex}`;

// --- 1. ADMIN DRAWING ENGINE ---
const AdminDrawingEngine = ({ onShapesChange, onReady }) => {
  const map = useMap();
  useMapsLibrary('geometry'); // load geometry lib so formatDistance() works for route labels
  useEffect(() => {
    if (!map || !window.google) return;
    let draw;
    let cancelled = false;
    let rafId;
    let idleListener;

    // Create exactly ONE TerraDraw instance. Deferring past React StrictMode's synchronous
    // mount→cleanup→mount (see bottom) plus this guard prevents a second "ghost" drawing
    // layer — the cause of the duplicate line color and dropped points.
    const startDraw = () => {
      if (cancelled || draw) return;
      try {
        const adapter = new TerraDrawGoogleMapsAdapter({ lib: window.google.maps, map });
        // Keep mode references so Finish can call close() directly (reliable on terra-draw
        // 1.31 — the synthetic-keypress path was unreliable). BLOCK = closed polygon,
        // ROUTE = open linestring (linestring mode never auto-closes).
        const polygonMode = new TerraDrawPolygonMode({ keyEvents: { finish: 'Enter', cancel: 'Escape' }, styles: { fillColor: '#8b5cf6', fillOpacity: 0.3, outlineColor: '#8b5cf6', outlineWidth: 3 } });
        // pointerDistance:1 stops a tap NEAR the line's start/last point from being read as a
        // click on the "closing point" (default 40px), which was ending the route early and
        // making the next taps a second route. The Finish button (close() directly) still works.
        const lineMode = new TerraDrawLineStringMode({ keyEvents: { finish: 'Enter', cancel: 'Escape' }, pointerDistance: 1, styles: { lineStringColor: '#10b981', lineStringWidth: 4 } });
        const selectMode = new TerraDrawSelectMode({
          // Drag to edit, but NEVER delete geometry by click or key — Clear/Undo are the
          // only removal paths (deletable:false on coords, delete key disabled).
          keyEvents: { delete: null },
          flags: {
            polygon: { feature: { draggable: true, coordinates: { midpoints: true, draggable: true, deletable: false } } },
            linestring: { feature: { draggable: true, coordinates: { midpoints: true, draggable: true, deletable: false } } }
          }
        });
        draw = new TerraDraw({ adapter, undoRedo: { modeLevel: new TerraDrawModeUndoRedo() }, modes: [polygonMode, lineMode, selectMode] });
        draw.start();
        draw.setMode('polygon');
        console.log('TerraDraw started (single instance) — mode:', draw.getMode());

        // Emit block {id, points} + routes [{id, points}] — ids let Edit target one shape.
        // Block polygon ring drops its duplicate closing point; routes stay open.
        const emitShapes = () => {
          const snap = draw.getSnapshot();
          const polys = snap.filter(f => f.geometry && f.geometry.type === 'Polygon');
          const lines = snap.filter(f => f.geometry && f.geometry.type === 'LineString');
          let block = null;
          if (polys.length) {
            const f = polys[polys.length - 1];
            const ring = f.geometry.coordinates[0] || [];
            let pts = ring.map(([lng, lat]) => ({ lat, lng }));
            if (pts.length > 1) { const a = pts[0], b = pts[pts.length - 1]; if (a.lat === b.lat && a.lng === b.lng) pts = pts.slice(0, -1); }
            block = { id: f.id, points: pts };
          }
          const routes = lines.map(l => ({ id: l.id, points: (l.geometry.coordinates || []).map(([lng, lat]) => ({ lat, lng })) }));
          const modeMap = { polygon: 'block', linestring: 'route', select: 'edit' };
          onShapesChange({ block, routes, mode: modeMap[draw.getMode()] || 'edit' });
        };

        // Keep only ONE block (dedup polygons on finish). Routes are NEVER deduped — a block
        // holds many. Live edits/drags come through 'change'.
        draw.on('finish', (id) => {
          try {
            const snap = draw.getSnapshot();
            const fin = snap.find(f => f.id === id);
            if (fin && fin.geometry.type === 'Polygon') {
              const olderIds = snap.filter(f => f.geometry.type === 'Polygon' && f.id !== id).map(f => f.id);
              if (olderIds.length) draw.removeFeatures(olderIds);
            }
            // A finished route must STOP drawing so the next tap can't begin a second route
            // (the split bug). Drawing another route requires re-selecting Route.
            if (fin && fin.geometry.type === 'LineString' && draw.getMode() === 'linestring') draw.setMode('select');
          } catch (e) { console.error('finish cleanup failed:', e); }
          emitShapes();
        });
        draw.on('change', () => emitShapes());

        // Finalize the in-progress shape (close ring / lock line). Used before a mode switch
        // AND by Finish, so toggling modes never discards work.
        const finalizeCurrent = () => {
          const m = draw.getMode();
          if (m === 'polygon') { try { polygonMode.close(); } catch (e) { console.error('block finalize failed:', e); } }
          else if (m === 'linestring') { try { lineMode.close(); } catch (e) { console.error('route finalize failed:', e); } }
        };

        if (onReady) onReady({
          // 'block' | 'route' | 'edit'. Auto-finalizes the current shape first so switching
          // modes never loses an in-progress block or route.
          setMode: (which) => {
            try {
              const target = which === 'block' ? 'polygon' : which === 'route' ? 'linestring' : 'select';
              if (draw.getMode() !== target) finalizeCurrent();
              draw.setMode(target);
              emitShapes();
            } catch (e) { console.error('setMode failed:', e); }
          },
          // Remove the last dropped point; drawing stays active so the next click extends it.
          undo: () => { try { draw.undo(); } catch (e) { console.error('Undo failed:', e); } },
          // Finalize the active shape, then stop drawing (switch to select/edit).
          finish: () => {
            try {
              const m = draw.getMode();
              console.log('[Finish] called — mode:', m, '| features before:', draw.getSnapshot().length);
              if (m !== 'polygon' && m !== 'linestring') { console.log('[Finish] not drawing — nothing to finalize'); return; }
              finalizeCurrent();
              draw.setMode('select');
              emitShapes();
              console.log('[Finish] finalized — now in', draw.getMode(), 'mode | features:', draw.getSnapshot().length);
            } catch (e) { console.error('[Finish] failed:', e); }
          },
          // Discard current shape: 'block' removes the polygon, 'route' removes the latest
          // line (other routes survive), 'all' wipes everything.
          clear: (which) => {
            try {
              if (!which || which === 'all') { draw.clear(); }
              else if (which === 'block') {
                const ids = draw.getSnapshot().filter(f => f.geometry && f.geometry.type === 'Polygon').map(f => f.id);
                if (ids.length) draw.removeFeatures(ids);
              } else {
                const lines = draw.getSnapshot().filter(f => f.geometry && f.geometry.type === 'LineString');
                if (lines.length) draw.removeFeatures([lines[lines.length - 1].id]);
              }
              emitShapes();
            } catch (e) { console.error('Clear failed:', e); }
          },
          // Highlight a feature so its points become draggable in edit mode.
          selectFeature: (id) => { try { draw.selectFeature(id); } catch (e) { console.error('select failed:', e); } },
          // Remove the LAST point of a specific feature, in place (id + order preserved).
          trimFeature: (id) => {
            try {
              const f = draw.getSnapshot().find(x => x.id === id);
              if (!f) return;
              if (f.geometry.type === 'LineString') {
                const c = f.geometry.coordinates;
                if (c.length > 2) draw.updateFeatureGeometry(id, { type: 'LineString', coordinates: c.slice(0, -1) });
                else draw.removeFeatures([id]);
              } else if (f.geometry.type === 'Polygon') {
                const distinct = (f.geometry.coordinates[0] || []).slice(0, -1); // drop closing dup
                if (distinct.length > 3) { const nd = distinct.slice(0, -1); draw.updateFeatureGeometry(id, { type: 'Polygon', coordinates: [[...nd, nd[0]]] }); }
              }
              emitShapes();
            } catch (e) { console.error('trim failed:', e); }
          },
          // Delete a whole feature by id (routes only; block protected in the UI).
          deleteFeature: (id) => { try { draw.removeFeatures([id]); emitShapes(); } catch (e) { console.error('delete failed:', e); } },
          // Load a saved block + routes into the canvas for editing.
          loadShapes: (blockPts, routesArr) => {
            try {
              draw.clear();
              const feats = [];
              if (blockPts && blockPts.length >= 3) {
                const ring = blockPts.map(p => [p.lng, p.lat]);
                ring.push([blockPts[0].lng, blockPts[0].lat]); // close the polygon ring
                feats.push({ type: 'Feature', properties: { mode: 'polygon' }, geometry: { type: 'Polygon', coordinates: [ring] } });
              }
              (routesArr || []).forEach(r => {
                const pts = Array.isArray(r) ? r : (r && r.points);
                if (pts && pts.length >= 2) feats.push({ type: 'Feature', properties: { mode: 'linestring' }, geometry: { type: 'LineString', coordinates: pts.map(p => [p.lng, p.lat]) } });
              });
              if (feats.length) draw.addFeatures(feats);
              draw.setMode('select');
              emitShapes();
            } catch (e) { console.error('loadShapes failed:', e); }
          }
        });
      } catch (e) {
        console.error("TerraDraw init failed:", e);
      }
    };

    // Defer past StrictMode's synchronous mount→cleanup→mount so the discarded first pass is
    // cancelled before it creates anything — only the surviving mount draws. (Map must also
    // be idle/projection-ready or start() throws on a null DOM element.)
    const begin = () => {
      if (cancelled) return;
      if (map.getProjection()) startDraw();
      else idleListener = window.google.maps.event.addListenerOnce(map, 'idle', startDraw);
    };
    rafId = requestAnimationFrame(begin);

    return () => {
      cancelled = true;
      if (rafId) cancelAnimationFrame(rafId);
      if (idleListener) window.google.maps.event.removeListener(idleListener);
      if (draw) { try { draw.clear(); } catch (e) {} try { draw.stop(); } catch (e) {} }
    };
  }, [map]);
  return null;
};
// Manager overview: every route across all blocks with its worked status, collector, km, leads.
// Manager account management: set each signed-in user's role (pending/collector/manager).
const ManagerAccounts = ({ onDraw, goBack }) => {
  const [users, setUsers] = useState([]);
  const [newEmail, setNewEmail] = useState('');
  const [newRole, setNewRole] = useState('collector');
  const [invited, setInvited] = useState(null);
  useEffect(() => {
    const unsub = onSnapshot(collection(db, "users"),
      (snap) => setUsers(snap.docs.map(d => ({ uid: d.id, ...d.data() }))),
      (err) => console.error("Failed to load users:", err));
    return () => unsub();
  }, []);
  const setUserRole = (u, role) => setDoc(doc(db, "users", u.uid), { role }, { merge: true });
  const createAccount = async () => {
    if (!newEmail.trim()) return;
    await preauthorizeEmail(newEmail, newRole);
    setInvited(`${newEmail.trim().toLowerCase()} → ${newRole}`); setNewEmail('');
  };
  return (
    <div className="h-full flex flex-col">
      <div className="flex items-center justify-between p-4 border-b border-gray-800">
        <div className="flex items-center"><Shield className="text-orange-500 mr-2" size={20} /><span className="font-bold tracking-widest uppercase text-white text-sm">Accounts</span></div>
        <div className="flex items-center gap-3 text-xs font-bold"><button onClick={onDraw} className="text-gray-300 hover:text-white">Blocks</button><button onClick={goBack} className="text-gray-400 hover:text-white">Exit</button></div>
      </div>
      <div className="flex-1 overflow-y-auto p-3 space-y-3">
        {/* Create an account by email (no need for them to sign in first) */}
        <div className="bg-gray-800 border border-gray-700 rounded-lg p-3">
          <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Create account by email</span>
          <div className="flex gap-2 mt-2">
            <input type="email" value={newEmail} onChange={e => setNewEmail(e.target.value)} placeholder="person@email.com" className="flex-1 min-w-0 bg-gray-900 text-white border border-gray-700 rounded-lg p-2 text-sm outline-none" />
            <select value={newRole} onChange={e => setNewRole(e.target.value)} className="bg-gray-900 text-white border border-gray-700 rounded-lg p-2 text-sm outline-none shrink-0">
              {ROLES.map(r => <option key={r} value={r}>{r.charAt(0).toUpperCase() + r.slice(1)}</option>)}
            </select>
            <button onClick={createAccount} className="bg-orange-600 hover:bg-orange-700 text-white font-bold rounded-lg px-3 text-sm shrink-0">Add</button>
          </div>
          <p className="text-[10px] text-gray-500 mt-1">They get this role automatically when they sign in with that email.</p>
          {invited && <p className="text-[10px] text-green-400 mt-1">✓ Invited {invited}</p>}
        </div>
        {/* People who have signed in */}
        <span className="text-[10px] font-bold text-gray-500 uppercase tracking-wider px-1">Signed-in users</span>
        {users.length === 0 && <p className="text-center text-gray-500 text-sm py-4">No one has signed in yet.</p>}
        {users.map(u => (
          <div key={u.uid} className="bg-gray-800 border border-gray-700 rounded-lg p-3 flex items-center justify-between gap-3">
            <div className="min-w-0"><div className="text-white font-bold text-sm truncate">{u.name || '(no name)'}</div><div className="text-xs text-gray-400 truncate">{u.email}</div></div>
            <select value={u.role || 'pending'} onChange={e => setUserRole(u, e.target.value)} className="bg-gray-900 text-white border border-gray-700 rounded-lg p-2 text-sm outline-none shrink-0">
              <option value="pending">Pending</option>
              {ROLES.map(r => <option key={r} value={r}>{r.charAt(0).toUpperCase() + r.slice(1)}</option>)}
            </select>
          </div>
        ))}
      </div>
    </div>
  );
};

// Manager block list: every drawn block with its in-charge collector + its routes.
const ManagerBlocks = ({ activeBlocks, onCreate, onEdit, onDelete, onAccounts, onActivity, goBack }) => {
  return (
    // ONE scroll column: header (sticky) → blocks → stats → Accounts/Activity footer.
    // The whole panel scrolls within its height, so no section can be clipped regardless
    // of how tall the stats grow or sub-pixel flex rounding.
    <div className="h-full overflow-y-auto">
      <div className="sticky top-0 z-10 bg-gray-900 flex items-center justify-between p-3 border-b border-gray-800">
        <span className="font-bold tracking-widest uppercase text-white text-sm">Blocks</span>
        <button onClick={onCreate} className="bg-orange-600 hover:bg-orange-700 text-white text-xs font-bold px-3 py-1.5 rounded-lg flex items-center"><Plus size={14} className="mr-1" /> Create Block</button>
      </div>
      <div className="p-3 space-y-2">
        {activeBlocks.length === 0 && <p className="text-center text-gray-500 text-sm py-8">No blocks yet. Tap "Create Block" to draw one.</p>}
        {activeBlocks.map(block => { const n = Array.isArray(block.routes) ? block.routes.length : 0; return (
          <div key={block.id} className="bg-gray-800 border border-gray-700 rounded-lg p-3 flex items-center justify-between gap-2">
            <div className="min-w-0">
              <div className="text-white font-bold text-sm truncate">{block.name}</div>
              <div className="text-[11px] text-gray-400 truncate">{block.assignedCollectorName ? block.assignedCollectorName + ' · ' : ''}{n} route{n === 1 ? '' : 's'}</div>
            </div>
            <div className="flex items-center gap-1.5 shrink-0">
              <button onClick={() => onEdit(block)} className="text-xs font-bold text-blue-300 hover:text-blue-200 px-2 py-1 rounded hover:bg-gray-700">Edit</button>
              <button onClick={() => onDelete(block)} className="text-xs font-bold text-red-400 hover:text-red-300 px-2 py-1 rounded hover:bg-gray-700">Delete</button>
            </div>
          </div>
        ); })}
        {/* Block & Route stats (Change C) */}
        {activeBlocks.length > 0 && (
          <div className="pt-2 mt-1 border-t border-gray-800">
            <div className="text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-2">Block &amp; route stats</div>
            <BlockRouteStats blocks={activeBlocks} dark={true} />
          </div>
        )}
      </div>
      <div className="border-t border-gray-800 p-3 flex items-center gap-3 text-xs font-bold">
        <button onClick={onAccounts} className="text-gray-300 hover:text-white">Accounts</button>
        <button onClick={onActivity} className="text-gray-300 hover:text-white">Activity</button>
        <button onClick={goBack} className="text-gray-400 hover:text-white ml-auto">Exit</button>
      </div>
    </div>
  );
};

const ManagerActivity = ({ activeBlocks, onDraw, goBack }) => {
  const [sessions, setSessions] = useState([]);
  useEffect(() => {
    const unsub = onSnapshot(collection(db, "route_session"),
      (snap) => setSessions(snap.docs.map(d => ({ id: d.id, ...d.data() }))),
      (err) => console.error("Failed to load route sessions:", err));
    return () => unsub();
  }, []);
  const rows = [];
  activeBlocks.forEach(block => {
    (Array.isArray(block.routes) ? block.routes : []).forEach((r, i) => {
      const matches = sessions.filter(s => s.block_id === block.id && s.routeIndex === i);
      const latest = matches.slice().sort((a, b) => ((b.updatedAt && b.updatedAt.seconds) || 0) - ((a.updatedAt && a.updatedAt.seconds) || 0))[0];
      rows.push({ key: block.id + '_' + i, blockName: block.name, routeName: (r && r.name) || `Route ${i + 1}`, session: latest });
    });
  });
  const badgeCls = (st) => st === 'done' ? 'bg-green-100 text-green-700 border-green-200' : st === 'in_progress' ? 'bg-amber-100 text-amber-700 border-amber-200' : 'bg-gray-200 text-gray-600 border-gray-300';
  const badgeTxt = (st) => st === 'done' ? 'Done' : st === 'in_progress' ? 'In progress' : 'Not started';
  return (
    <div className="h-full flex flex-col">
      <div className="flex items-center justify-between p-4 border-b border-gray-800">
        <div className="flex items-center"><Shield className="text-orange-500 mr-2" size={20} /><span className="font-bold tracking-widest uppercase text-white text-sm">Activity</span></div>
        <div className="flex items-center gap-3 text-xs font-bold"><button onClick={onDraw} className="text-gray-300 hover:text-white">Blocks</button><button onClick={goBack} className="text-gray-400 hover:text-white">Exit</button></div>
      </div>
      <div className="flex-1 overflow-y-auto p-3 space-y-2">
        {rows.length === 0 && <p className="text-center text-gray-500 text-sm py-8">No routes published yet.</p>}
        {rows.map(row => { const s = row.session; const st = s ? s.status : 'not_started'; return (
          <div key={row.key} className="bg-gray-800 border border-gray-700 rounded-lg p-3">
            <div className="flex items-center justify-between mb-1"><span className="text-white font-bold text-sm">{row.blockName} · {row.routeName}</span><span className={`text-[10px] font-bold px-2 py-0.5 rounded border ${badgeCls(st)}`}>{badgeTxt(st)}</span></div>
            <div className="text-xs text-gray-400">{s ? `${s.collector_name || '—'} · ${(s.kmPassed || 0).toFixed(1)} km · ${s.leadsGenerated || 0} leads` : 'No collector yet'}</div>
          </div>
        ); })}
      </div>
    </div>
  );
};

// ── EVALUATION & SORTING (manager) — manual-first node flow over real `leads` ──────────
const fmtDate = (ts) => { try { return ts && ts.toDate ? ts.toDate().toLocaleString() : ''; } catch (e) { return ''; } };
// PHASES / PHASE_LABEL now come from the shared ./phases module (imported above).
// rating = INTERNAL team score (staff-only). googleRating/googleReviewCount = EXTERNAL
// public Google rating, filled only by enrichment — kept strictly separate, never mixed.
const blankSocials = () => ({ instagram: '', facebook: '', linkedin: '', youtube: '', other: '' });
const blankBiz = () => ({ businessName: '', trade: '', roleCode: '', officeLocation: '', website: '', phone: '', rating: '', googleRating: '', googleReviewCount: '', placeId: '', contacts: [], gatekeepers: [], socials: blankSocials(), nationality: 'Unknown' });

// ── Nationality tag (manual, extensible) — shared option list in ai_config/nationalities ──
const SEED_NATIONALITIES = ['Persian', 'Chinese', 'Italian', 'Portuguese'];
// Live-loaded, user-extensible nationality options (default "Unknown" is implicit).
const useNationalities = () => {
  const [options, setOptions] = useState(SEED_NATIONALITIES);
  useEffect(() => {
    const u = onSnapshot(doc(db, 'ai_config', 'nationalities'), s => {
      if (s.exists() && Array.isArray(s.data().options) && s.data().options.length) setOptions(s.data().options);
    }, e => console.error('nationalities load:', e));
    return () => u();
  }, []);
  const add = async (raw) => {
    const v = (raw || '').trim();
    if (!v || options.some(o => o.toLowerCase() === v.toLowerCase()) || v.toLowerCase() === 'unknown') return;
    try { await setDoc(doc(db, 'ai_config', 'nationalities'), { options: [...options, v], updatedAt: serverTimestamp() }, { merge: true }); }
    catch (e) { console.error('add nationality:', e); }
  };
  return { options, add };
};
// Social links: normalize a stored value to a tappable href.
const socialHref = (v) => { const s = (v || '').trim(); if (!s) return ''; return /^https?:\/\//i.test(s) ? s : 'https://' + s.replace(/^\/+/, ''); };
const SOCIAL_FIELDS = [['instagram', 'IG'], ['facebook', 'FB'], ['linkedin', 'LinkedIn'], ['youtube', 'YT'], ['other', 'Web']];
// Read-only tappable social chips for a business.
const SocialChips = ({ socials }) => {
  const s = socials || {};
  const present = SOCIAL_FIELDS.filter(([k]) => (s[k] || '').trim());
  if (!present.length) return null;
  return <div className="flex flex-wrap gap-1">{present.map(([k, lbl]) => <a key={k} href={socialHref(s[k])} target="_blank" rel="noreferrer" onClick={e => e.stopPropagation()} className="text-[10px] font-bold text-sky-300 bg-sky-500/10 border border-sky-500/30 rounded px-1.5 py-0.5 hover:bg-sky-500/20">{lbl}</a>)}</div>;
};
// side: 'demand' -> code prefix 1 ; 'supply' -> 2. priorityCode = 0{priority}.{side}.{title}
const SIDE_NUM = { demand: 1, supply: 2 };
const blankGatekeeper = (side, priority) => ({ name: '', role: '', phone: '', email: '', sameAsOwner: false, priorityCode: `0${priority}.${SIDE_NUM[side] || 1}.1` });

// ONE business-card form, used for BOTH demand and supply (differ only by side label/prefix).
const BusinessCard = ({ biz, side, onChange, onRemove, onEnrich, nationalities = SEED_NATIONALITIES }) => {
  const ic = "w-full bg-gray-950 text-white border border-gray-700 rounded px-2 py-1.5 text-sm outline-none focus:ring-1 focus:ring-orange-500";
  const up = (k, v) => onChange({ ...biz, [k]: v });
  const upSocial = (k, v) => onChange({ ...biz, socials: { ...(biz.socials || {}), [k]: v } });
  const natOpts = ['Unknown', ...nationalities.filter(o => o.toLowerCase() !== 'unknown')];
  const [enriching, setEnriching] = useState(false);
  const [enrichNote, setEnrichNote] = useState('');
  // Web-enrichment: fill ONLY empty fields; write Google rating to its OWN fields.
  // Never touches the internal `rating`. "No Google listing" is a normal outcome.
  const enrich = async () => {
    if (!onEnrich) return;
    setEnriching(true); setEnrichNote('');
    try {
      const r = await onEnrich(biz);
      if (!r || !r.ok) { setEnrichNote(`⚠ ${(r && r.error) || 'Enrichment failed.'}`); }
      else if (!r.match) { setEnrichNote(r.note || `No Google listing found for "${biz.businessName || 'this business'}".`); }
      else {
        const m = r.match;
        onChange({
          ...biz,
          officeLocation: biz.officeLocation || m.officeLocation || '',
          phone: cleanPhone(m.phone) || biz.phone || '',
          website: m.website || biz.website || '',
          googleRating: (m.googleRating ?? '') === null ? '' : (m.googleRating ?? ''),
          googleReviewCount: (m.googleReviewCount ?? '') === null ? '' : (m.googleReviewCount ?? ''),
          placeId: m.placeId || '',
          // Pre-fill social links ONLY if the enrichment result carried them (fill empties).
          socials: { ...blankSocials(), ...(biz.socials || {}), ...(m.socials || {}) },
          enriched: true,
        });
        setEnrichNote(r.note || `Enriched from Google${m.name ? ` · ${m.name}` : ''}.`);
      }
    } catch (e) { setEnrichNote(`⚠ ${e.message || 'Enrichment failed.'}`); }
    finally { setEnriching(false); }
  };
  const gks = biz.gatekeepers || [];
  const addGk = () => onChange({ ...biz, gatekeepers: [...gks, blankGatekeeper(side, gks.length + 1)] });
  const upGk = (i, k, v) => onChange({ ...biz, gatekeepers: gks.map((g, idx) => idx === i ? { ...g, [k]: v } : g) });
  const rmGk = (i) => onChange({ ...biz, gatekeepers: gks.filter((_, idx) => idx !== i) });
  const toggleSame = (i, checked) => {
    if (!checked) { upGk(i, 'sameAsOwner', false); return; }
    const c = (biz.contacts && biz.contacts[0]) || {};
    onChange({ ...biz, gatekeepers: gks.map((g, idx) => idx === i ? { ...g, sameAsOwner: true, name: c.name || biz.businessName || '', role: c.role || 'Owner', phone: c.phone || biz.phone || '', email: c.email || '' } : g) });
  };
  return (
    <div className="bg-gray-900 border border-gray-700 rounded p-2 mb-2 relative">
      <button onClick={onRemove} className="absolute top-1 right-1 text-gray-500 hover:text-red-400"><X size={14} /></button>
      <div className="text-[10px] font-bold text-gray-500 uppercase mb-1 flex items-center gap-2 flex-wrap">{side} business · code {SIDE_NUM[side]}.x{biz.suggested && <span className="text-[9px] text-amber-400 border border-amber-500/40 rounded px-1 py-0.5 normal-case">suggested — review</span>}{biz.enriched && <span className="text-[9px] text-cyan-300 border border-cyan-500/40 rounded px-1 py-0.5 normal-case">enriched — review</span>}</div>
      <input className={ic + ' mb-1'} placeholder="Business name" value={biz.businessName || ''} onChange={e => up('businessName', e.target.value)} />
      <div className="grid grid-cols-2 gap-1 mb-1"><input className={ic} placeholder="Trade / title" value={biz.trade || ''} onChange={e => up('trade', e.target.value)} /><input className={ic} placeholder={`Role code (${SIDE_NUM[side]}.x${side === 'supply' ? '.x' : ''})`} value={biz.roleCode || ''} onChange={e => up('roleCode', e.target.value)} /></div>
      <input className={ic + ' mb-1'} placeholder="Office location * (dedup key)" value={biz.officeLocation || ''} onChange={e => up('officeLocation', e.target.value)} />
      <div className="grid grid-cols-2 gap-1 mb-1"><input className={ic} placeholder="Website" value={biz.website || ''} onChange={e => up('website', e.target.value)} /><input className={ic} placeholder="Phone" value={biz.phone || ''} onChange={e => up('phone', e.target.value)} /></div>
      {/* TWO separate ratings: internal team score (editable) vs external Google (read-only) */}
      <div className="grid grid-cols-2 gap-1 mb-1">
        <input className={ic} placeholder="Our rating (internal)" value={biz.rating || ''} onChange={e => up('rating', e.target.value)} />
        <div className="w-full bg-gray-950 border border-gray-800 rounded px-2 py-1.5 text-sm text-gray-400 flex items-center">{biz.googleRating !== '' && biz.googleRating != null ? <span>Google ★{biz.googleRating}{biz.googleReviewCount !== '' && biz.googleReviewCount != null ? ` (${biz.googleReviewCount})` : ''}</span> : <span className="text-gray-600">Google ★ —</span>}</div>
      </div>
      <div className="flex items-center gap-2 mb-1">
        <button onClick={enrich} disabled={enriching || (!biz.businessName && !biz.phone)} className="text-[11px] font-bold text-cyan-400 hover:text-cyan-300 disabled:opacity-40 inline-flex items-center">{enriching ? <><Loader2 size={12} className="animate-spin mr-1" /> Enriching…</> : '🌐 Enrich'}</button>
        {enrichNote && <span className={`text-[10px] ${enrichNote.startsWith('⚠') ? 'text-red-400' : 'text-gray-500'}`}>{enrichNote}</span>}
      </div>
      {/* Nationality (manual) + social links */}
      <div className="grid grid-cols-2 gap-1 mb-1">
        <select className={ic} value={natOpts.includes(biz.nationality) ? biz.nationality : 'Unknown'} onChange={e => up('nationality', e.target.value)}>{natOpts.map(o => <option key={o} value={o}>{o}</option>)}</select>
        <input className={ic} placeholder="Instagram" value={(biz.socials || {}).instagram || ''} onChange={e => upSocial('instagram', e.target.value)} />
      </div>
      <div className="grid grid-cols-2 gap-1 mb-1">
        <input className={ic} placeholder="Facebook" value={(biz.socials || {}).facebook || ''} onChange={e => upSocial('facebook', e.target.value)} />
        <input className={ic} placeholder="LinkedIn" value={(biz.socials || {}).linkedin || ''} onChange={e => upSocial('linkedin', e.target.value)} />
      </div>
      <div className="grid grid-cols-2 gap-1 mb-1">
        <input className={ic} placeholder="YouTube" value={(biz.socials || {}).youtube || ''} onChange={e => upSocial('youtube', e.target.value)} />
        <input className={ic} placeholder="Other URL" value={(biz.socials || {}).other || ''} onChange={e => upSocial('other', e.target.value)} />
      </div>
      {/* Gatekeepers / in-charge (priority-ranked) */}
      <div className="mt-2 border-t border-gray-700 pt-2">
        <div className="flex items-center justify-between mb-1"><span className="text-[10px] font-bold text-gray-500 uppercase">Gatekeepers / in-charge ({gks.length})</span><button onClick={addGk} className="text-[11px] font-bold text-blue-400 hover:text-blue-300">+ Add gatekeeper</button></div>
        {gks.map((g, i) => (
          <div key={i} className="bg-gray-800 border border-gray-700 rounded p-2 mb-1 relative">
            <button onClick={() => rmGk(i)} className="absolute top-1 right-1 text-gray-500 hover:text-red-400"><X size={12} /></button>
            <label className="flex items-center gap-1.5 text-[11px] font-bold text-gray-300 mb-1"><input type="checkbox" checked={!!g.sameAsOwner} onChange={e => toggleSame(i, e.target.checked)} /> Same as owner</label>
            <div className="grid grid-cols-2 gap-1 mb-1"><input className={ic} placeholder="Name" value={g.name || ''} onChange={e => upGk(i, 'name', e.target.value)} /><input className={ic} placeholder="Role / title" value={g.role || ''} onChange={e => upGk(i, 'role', e.target.value)} /></div>
            <div className="grid grid-cols-2 gap-1 mb-1"><input className={ic} placeholder="Phone" value={g.phone || ''} onChange={e => upGk(i, 'phone', e.target.value)} /><input className={ic} placeholder="Email" value={g.email || ''} onChange={e => upGk(i, 'email', e.target.value)} /></div>
            <input className={ic} placeholder="Priority code 0p.side.title" value={g.priorityCode || ''} onChange={e => upGk(i, 'priorityCode', e.target.value)} />
          </div>
        ))}
      </div>
    </div>
  );
};

// ── MARKET (system of record) — dedup + commit helpers ─────────────────────────────
// Normalizers used for matching businesses across leads.
const normName = (s) => (s || '').toLowerCase().replace(/[^a-z0-9 ]/g, ' ')
  .replace(/\b(inc|incorporated|ltd|limited|llc|corp|corporation|co|company|the)\b/g, '')
  .replace(/\s+/g, ' ').trim();
const normPhone = (s) => (s || '').replace(/\D/g, '');
const normDomain = (s) => {
  if (!s) return '';
  let u = String(s).trim();
  try { if (!/^https?:\/\//i.test(u)) u = 'http://' + u; return new URL(u).hostname.replace(/^www\./, '').toLowerCase(); }
  catch (e) { return u.toLowerCase().replace(/^https?:\/\//, '').replace(/^www\./, '').split('/')[0]; }
};

// Composite-fallback dedup: website domain → phone → normalized name → office location.
// Returns { biz, strong } where strong=false means a NAME-ONLY match (flag, don't hard-merge).
const matchBusiness = (existing, card) => {
  const dom = normDomain(card.website), ph = normPhone(card.phone), nm = normName(card.businessName);
  const loc = (card.officeLocation || '').trim().toLowerCase();
  if (dom) { const m = existing.find(b => b.websiteDomain && b.websiteDomain === dom); if (m) return { biz: m, strong: true }; }
  if (ph) { const m = existing.find(b => normPhone(b.phone) && normPhone(b.phone) === ph); if (m) return { biz: m, strong: true }; }
  if (nm) { const m = existing.find(b => b.normalizedName && b.normalizedName === nm); if (m) return { biz: m, strong: false }; }
  if (loc) { const m = existing.find(b => (b.officeLocation || '').trim().toLowerCase() === loc); if (m) return { biz: m, strong: true }; }
  return null;
};
const uniqGatekeepers = (existing, incoming) => {
  const out = [...(existing || [])];
  (incoming || []).forEach(g => {
    const key = (g.name || '') + '|' + normPhone(g.phone);
    if (key !== '|' && !out.some(e => ((e.name || '') + '|' + normPhone(e.phone)) === key)) out.push(g);
  });
  return out;
};

// Commit an approved lead into the Market: dedup businesses, accumulate jobsites,
// create the project, store relationship edges, and stamp the lead. Idempotent on sourceLeadId.
const commitInject = async (lead) => {
  // Idempotency: reuse the project already created for this lead, else mint a new id.
  const existingProjSnap = await getDocs(query(collection(db, "projects"), where("sourceLeadId", "==", lead.id)));
  const projectRef = existingProjSnap.empty ? doc(collection(db, "projects")) : existingProjSnap.docs[0].ref;
  const projectId = projectRef.id;

  // Load current businesses once; we mutate this local list so multiple cards on the
  // same lead can match a business created earlier in this same commit.
  const bizSnap = await getDocs(collection(db, "businesses"));
  const existing = bizSnap.docs.map(d => ({ id: d.id, ...d.data() }));

  const jobsite = { projectId, address: lead.address || '', phase: lead.phase || 'p0', block_id: lead.block_id || null, zoneName: lead.zoneName || '', seenAt: new Date().toISOString() };
  const demandBusinessIds = [], supplyBusinessIds = [];

  const processCard = async (card, side) => {
    if (!card || (!card.businessName && !card.trade)) return null;
    const roleCodes = splitRoleCodes(card.roleCode);
    const match = matchBusiness(existing, card);
    let bizId;
    if (match) {
      bizId = match.biz.id;
      const b = match.biz;
      const mergedJobsites = (b.jobsites || []).some(j => j.projectId === projectId) ? (b.jobsites || []) : [...(b.jobsites || []), jobsite];
      // A weak (name-only) match is only a REAL possible-duplicate when a strong identifier
      // actually CONFLICTS (different website domain or phone). Same-business consolidation
      // (matching or empty identifiers) must NOT be flagged. A strong match clears the flag.
      const strongConflict = !match.strong && (
        (normDomain(card.website) && b.websiteDomain && normDomain(card.website) !== b.websiteDomain) ||
        (normPhone(card.phone) && normPhone(b.phone) && normPhone(card.phone) !== normPhone(b.phone))
      );
      const merged = {
        // Fill empty fields only; never overwrite existing strong data.
        businessName: b.businessName || card.businessName || '',
        normalizedName: b.normalizedName || normName(card.businessName),
        side: b.side || side,
        trade: b.trade || card.trade || '',
        phone: b.phone || card.phone || '',
        website: b.website || card.website || '',
        websiteDomain: b.websiteDomain || normDomain(card.website),
        officeLocation: b.officeLocation || card.officeLocation || '',
        roleCodes: splitRoleCodes([...(b.roleCodes || []), ...roleCodes]),
        gatekeepers: uniqGatekeepers(b.gatekeepers, card.gatekeepers),
        jobsites: mergedJobsites,
        // External Google rating + place id carried over (internal `rating` stays untouched).
        googleRating: (b.googleRating ?? '') !== '' ? b.googleRating : (card.googleRating ?? ''),
        googleReviewCount: (b.googleReviewCount ?? '') !== '' ? b.googleReviewCount : (card.googleReviewCount ?? ''),
        placeId: b.placeId || card.placeId || '',
        // Social links: fill empty fields only. Nationality: keep an existing set value.
        socials: { ...blankSocials(), ...(card.socials || {}), ...Object.fromEntries(Object.entries(b.socials || {}).filter(([, v]) => (v || '').trim())) },
        nationality: (b.nationality && b.nationality !== 'Unknown') ? b.nationality : (card.nationality || 'Unknown'),
        possibleDuplicate: match.strong ? false : (strongConflict ? true : !!b.possibleDuplicate), // only a real conflict flags
        updatedAt: serverTimestamp(),
      };
      await setDoc(doc(db, "businesses", bizId), merged, { merge: true });
      Object.assign(match.biz, merged, { jobsites: mergedJobsites }); // keep local copy in sync
    } else {
      const newBiz = {
        businessName: card.businessName || '', normalizedName: normName(card.businessName),
        side, trade: card.trade || '', phone: card.phone || '',
        website: card.website || '', websiteDomain: normDomain(card.website),
        officeLocation: card.officeLocation || '', roleCodes,
        googleRating: card.googleRating ?? '', googleReviewCount: card.googleReviewCount ?? '', placeId: card.placeId || '',
        socials: { ...blankSocials(), ...(card.socials || {}) }, nationality: card.nationality || 'Unknown',
        gatekeepers: card.gatekeepers || [], jobsites: [jobsite],
        possibleDuplicate: false, createdAt: serverTimestamp(), updatedAt: serverTimestamp(),
      };
      const refNew = await addDoc(collection(db, "businesses"), newBiz);
      bizId = refNew.id;
      existing.push({ id: bizId, ...newBiz });
    }
    (side === 'demand' ? demandBusinessIds : supplyBusinessIds).push(bizId);
    return bizId;
  };

  for (const c of (lead.demand || [])) await processCard(c, 'demand');
  for (const c of (lead.supply || [])) await processCard(c, 'supply');

  // Project record (system of record for the jobsite).
  await setDoc(projectRef, {
    address: lead.address || '', coords: lead.coords || null, phase: lead.phase || 'p0',
    phaseSystem: lead.phaseSystem || null, // carry the canonical stamp through to the Market
    block_id: lead.block_id || null, zoneName: lead.zoneName || '',
    cityData: lead.cityData || {}, // carry city/permit data onto the project (site-level)
    status: lead.siteStatus === 'stopped' ? 'stopped' : 'active', sourceLeadId: lead.id,
    outcome: lead.outcome || null, // approved | needs_eval | other — drives Market "Eval" status
    isProspect: !!lead.isProspect,
    demandBusinessIds, supplyBusinessIds,
    photos: Array.isArray(lead.photos) ? lead.photos : [],
    enrichmentData: lead.enrichmentData || {},
    ...(existingProjSnap.empty ? { createdAt: serverTimestamp() } : {}),
    updatedAt: serverTimestamp(),
  }, { merge: true });

  // Relationship edges: every pair of businesses on this jobsite is connected;
  // weight = number of shared jobsites. Idempotent via projectIds[] guard.
  const allIds = [...demandBusinessIds, ...supplyBusinessIds];
  const nameOf = (id) => { const b = existing.find(x => x.id === id); return b ? b.businessName : ''; };
  for (let i = 0; i < allIds.length; i++) {
    for (let j = i + 1; j < allIds.length; j++) {
      const pair = [allIds[i], allIds[j]].sort();
      const edgeId = pair.join('__');
      const edgeRef = doc(db, "relationships", edgeId);
      const snap = await getDoc(edgeRef);
      const prev = snap.exists() ? snap.data() : null;
      const projectIds = (prev && prev.projectIds) || [];
      if (projectIds.includes(projectId)) continue; // already counted this jobsite
      await setDoc(edgeRef, {
        a: pair[0], b: pair[1], aName: nameOf(pair[0]), bName: nameOf(pair[1]),
        weight: (prev && prev.weight ? prev.weight : 0) + 1,
        projectIds: [...projectIds, projectId], updatedAt: serverTimestamp(),
        ...(prev ? {} : { createdAt: serverTimestamp() }),
      }, { merge: true });
    }
  }

  // Stamp the lead.
  await setDoc(doc(db, "leads", lead.id), { status: 'injected', injected: true, projectId, updatedAt: serverTimestamp() }, { merge: true });
  return projectId;
};

// ── AI CONTROL TOWER — editable classification map (seed mirrors classifyBusinesses) ──
const SEED_ROLE_MAP = [
  { trade: 'framing', title: 15, phase: 2, side: 'supply', code: '2.1.215' },
  { trade: 'concrete', title: 10, phase: 1, side: 'supply', code: '2.1.110' },
  { trade: 'rebar', title: 11, phase: 1, side: 'supply', code: '2.1.111' },
  { trade: 'roof/trusses', title: 17, phase: 2, side: 'supply', code: '2.1.217' },
  { trade: 'plumber', title: 21, phase: 3, side: 'supply', code: '2.1.321' },
  { trade: 'electrician', title: 22, phase: 3, side: 'supply', code: '2.1.322' },
  { trade: 'HVAC', title: 23, phase: 3, side: 'supply', code: '2.1.323' },
  { trade: 'drywall', title: 25, phase: 3, side: 'supply', code: '2.1.325' },
  { trade: 'insulation', title: 27, phase: 3, side: 'supply', code: '2.1.327' },
  { trade: 'painter', title: 28, phase: 4, side: 'supply', code: '2.1.428' },
  { trade: 'roofer', title: 35, phase: 5, side: 'supply', code: '2.1.535' },
  { trade: 'mason', title: 36, phase: 5, side: 'supply', code: '2.1.536' },
  { trade: 'landscaping', title: 40, phase: 5, side: 'supply', code: '2.1.540' },
  { trade: 'fencing / site services / temporary fencing', title: 91, phase: 0, side: 'supply', code: '2.1.091' },
];
const SEED_DEMAND_FIXED = [
  { role: 'developer', code: '1.1' }, { role: 'custom home builder', code: '1.2' },
  { role: 'general contractor', code: '1.3' }, { role: 'homeowner', code: '1.4' },
  { role: 'realtor', code: '1.5' }, { role: 'creditor', code: '1.6' },
];

// Activity tiers from a business's accumulated jobsite count (thresholds editable in
// the AI Control Tower → ai_config/activity_tiers; these are the fallback defaults).
const SEED_TIERS = { active: 5, veryActive: 8, superActive: 10 };
const computeTier = (count, t) => {
  const tiers = t || SEED_TIERS;
  if (count >= tiers.superActive) return 'Super Active';
  if (count >= tiers.veryActive) return 'Very Active';
  if (count >= tiers.active) return 'Active';
  return '';
};

// ── COLLECTOR ECONOMICS — pay from work we control (km + leads gen + leads updated) ──
// Rates are editable in the AI Control Tower → ai_config/collector_rates; these are the
// fallback defaults (CAD). The Cost view + collector profile read the live rates.
const SEED_RATES = { perKm: 2, perLeadGenerated: 5, perLeadUpdated: 1 };
const money = (n) => '$' + (Number(n) || 0).toFixed(2);
// earnings = km×perKm + generated×perLeadGenerated + updated×perLeadUpdated (breakdown kept).
const computeEarnings = ({ km = 0, generated = 0, updated = 0 }, rates) => {
  const r = rates || SEED_RATES;
  const kmPay = km * (Number(r.perKm) || 0);
  const genPay = generated * (Number(r.perLeadGenerated) || 0);
  const updPay = updated * (Number(r.perLeadUpdated) || 0);
  return { km, generated, updated, kmPay, genPay, updPay, total: kmPay + genPay + updPay };
};
// Roll sessions + leads + update-events into per-collector counts (name-keyed).
// Generic shape ({ name, km, generated, updated }) so the Cost view can grow into the
// fuller per-entity financial dashboard (other staff, income, clients, providers) later.
const collectorStats = (sessions, leads, updateEvents) => {
  const map = {};
  const ensure = (name) => { if (!name) return null; if (!map[name]) map[name] = { name, km: 0, generated: 0, updated: 0 }; return map[name]; };
  (sessions || []).forEach(s => { const e = ensure(s.collector_name); if (e) e.km += (s.kmPassed || 0); });
  (leads || []).forEach(l => { const e = ensure(l.submittedBy || l.collector_name); if (e) e.generated += 1; });
  (updateEvents || []).forEach(u => { const e = ensure(u.collectorName); if (e) e.updated += 1; });
  return Object.values(map).sort((a, b) => a.name.localeCompare(b.name));
};

// ── ONE reusable AI orchestration — ENRICH ONLY, never changes status ───────────────
// Runs the full chain on a lead and merges results back to the lead doc. Each sub-step
// is isolated so one failure doesn't abort the rest. Re-running drops prior AI
// Locality token for Web-enrichment — biases the business-NAME search to the site's area
// (e.g. "North York" from "…, North York, ON M2R 1B3"), falling back to the zone/borough.
const enrichLocality = (src) => {
  const a = (src && src.address) || '';
  const m = a.match(/,\s*([^,]+?),\s*(?:ON|Ontario)\b/i);
  return (m && m[1].trim()) || (src && src.zoneName) || 'North York';
};
// Best available NAME to enrich a business by — fixes the demand-side path, whose name
// often lives in `company` or the first contact/gatekeeper rather than `businessName`.
const enrichName = (b) => ((b && (b.businessName || b.company))
  || (b && (b.gatekeepers || [])[0] && b.gatekeepers[0].name)
  || (b && (b.contacts || [])[0] && b.contacts[0].name) || '').trim();
// Clean a phone to (NNN) NNN-NNNN, or '' if not a plausible 10-digit NA number.
const cleanPhone = (s) => {
  const d = String(s || '').replace(/\D/g, '');
  const t = (d.length === 11 && d[0] === '1') ? d.slice(1) : d;
  return t.length === 10 ? `(${t.slice(0, 3)}) ${t.slice(3, 6)}-${t.slice(6)}` : '';
};
// Split concatenated/mangled role codes (e.g. "1.52.1.091") back into a clean list
// (["1.5","2.1.091"]); accepts a string or array; dedupes.
const splitRoleCodes = (v) => {
  const arr = Array.isArray(v) ? v : (v ? [v] : []);
  const out = [];
  arr.forEach((x) => {
    const s = String(x || '').trim();
    const m = s.match(/[12]\.\d(?:\.\d{2,3})?/g);
    (m && m.length ? m : (s ? [s] : [])).forEach((c) => { if (!out.includes(c)) out.push(c); });
  });
  return out;
};

// suggestions (badged) and regenerates them — human-entered cards are kept.
const processLeadAI = async (lead, onStep) => {
  const errors = [];
  const step = (s) => { try { onStep && onStep(s); } catch (e) {} };

  // 1. OCR every photo → combined text
  step('OCR photos');
  let ocrText = '';
  for (const p of (lead.photos || [])) {
    if (!p || !p.url) continue;
    try { const r = await ocrPhotoFn({ url: p.url }); const d = (r && r.data) || {}; if (d.ok && d.text) ocrText += (ocrText ? '\n\n' : '') + d.text; else if (!d.ok) errors.push('OCR: ' + d.error); }
    catch (e) { errors.push('OCR: ' + (e.message || e)); }
  }

  // 2. Classify → pre-filled business cards (keep human cards, replace prior AI suggestions)
  step('Classify businesses');
  let demand = (Array.isArray(lead.demand) ? lead.demand : []).filter(b => !b.suggested);
  let supply = (Array.isArray(lead.supply) ? lead.supply : []).filter(b => !b.suggested);
  if (ocrText.trim()) {
    try {
      const r = await classifyBusinessesFn({ text: ocrText, phase: lead.phase || '' });
      const d = (r && r.data) || {};
      if (d.ok) (d.businesses || []).forEach(b => {
        const card = { ...blankBiz(), businessName: b.businessName || '', trade: b.trade || '', roleCode: b.roleCode || '', phone: b.phone || '', website: b.website || '', suggested: true };
        if (b.side === 'demand') demand.push(card); else supply.push(card);
      });
      else errors.push('Classify: ' + d.error);
    } catch (e) { errors.push('Classify: ' + (e.message || e)); }
  }

  // 3. Enrich every business (Google Places) — fill empties + external rating
  step('Enrich businesses');
  const enrichArr = (arr) => Promise.all(arr.map(async (b) => {
    if (!enrichName(b) && !b.phone) return b;
    try {
      const r = await enrichBusinessFn({ businessName: enrichName(b), phone: b.phone, website: b.website, coords: lead.coords || null, city: enrichLocality(lead) });
      const d = (r && r.data) || {};
      // website + phone come from the matched result; phone validated (never mangled).
      if (d.ok && d.match) { const m = d.match; return { ...b, officeLocation: b.officeLocation || m.officeLocation || '', phone: cleanPhone(m.phone) || b.phone || '', website: m.website || b.website || '', googleRating: m.googleRating == null ? '' : m.googleRating, googleReviewCount: m.googleReviewCount == null ? '' : m.googleReviewCount, placeId: m.placeId || '', enriched: true }; }
      else if (!d.ok) errors.push('Enrich: ' + d.error);
    } catch (e) { errors.push('Enrich: ' + (e.message || e)); }
    return b;
  }));
  demand = await enrichArr(demand); supply = await enrichArr(supply);

  // 4. Transcribe the voice note (if any)
  step('Transcribe voice');
  const enrichmentData = { ...(lead.enrichmentData || {}) };
  if (lead.audioUrl) {
    try { const r = await transcribeAudioFn({ audioUrl: lead.audioUrl }); const d = (r && r.data) || {}; if (d.ok) enrichmentData.transcript = d.transcript || enrichmentData.transcript || ''; else errors.push('Voice: ' + d.error); }
    catch (e) { errors.push('Voice: ' + (e.message || e)); }
  }

  // 5. City permit by address
  step('City permit');
  let cityData = lead.cityData || {};
  if (lead.address) {
    // Use the auto-extracted MAIN building permit (builder + value), not permits[0].
    try { const r = await fetchCityPermitFn({ address: lead.address }); const d = (r && r.data) || {}; if (d.ok && d.cityData) cityData = d.cityData; else if (d.ok && (d.permits || []).length) cityData = { ...d.permits[0], _source: 'BuildData / City of Toronto Open Data' }; else if (!d.ok) errors.push('City: ' + d.error); }
    catch (e) { errors.push('City: ' + (e.message || e)); }
  }

  // Merge results back — status stays untouched (still new_lead).
  const result = { demand, supply, enrichmentData, cityData };
  try { await setDoc(doc(db, "leads", lead.id), { ...result, updatedAt: serverTimestamp() }, { merge: true }); }
  catch (e) { errors.push('Save: ' + (e.message || e)); }
  return { ok: errors.length === 0, errors, result };
};

const ManagerEvaluation = ({ onBack, goBack }) => {
  const nat = useNationalities();
  const [allLeads, setAllLeads] = useState([]);
  const [selId, setSelId] = useState(null);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const [ocr, setOcr] = useState({}); // { [photoIdx]: { loading, text, error } } — on-demand Vision OCR
  const [classifying, setClassifying] = useState(false);
  const [classifyError, setClassifyError] = useState('');
  const [injectingId, setInjectingId] = useState(null);
  const [injectError, setInjectError] = useState('');
  const [transcribing, setTranscribing] = useState(false);
  const [transcribeError, setTranscribeError] = useState('');
  const [cityLoading, setCityLoading] = useState(false);
  const [cityError, setCityError] = useState('');
  const [permits, setPermits] = useState(null); // null = not fetched yet; [] = fetched, no match
  const [procLead, setProcLead] = useState({ running: false, step: '', note: '' }); // per-lead AI chain
  const [bulk, setBulk] = useState({ running: false, done: 0, total: 0, failed: 0, summary: '' }); // queue-wide
  const bulkCancel = useRef(false);
  const [source, setSource] = useState('field'); // 'field' (live leads) | 'mls' (reserved) | '2023'
  const [records2023, setRecords2023] = useState([]);
  const [inserting, setInserting] = useState(false);
  const [dateFrom, setDateFrom] = useState(''); const [dateTo, setDateTo] = useState('');   // input calendar
  const [outFrom, setOutFrom] = useState(''); const [outTo, setOutTo] = useState('');       // output calendar
  const [outcomeF, setOutcomeF] = useState('all'); // output outcome filter
  const [showDetails, setShowDetails] = useState(false); // #2 process-inspection panel
  const [lightbox, setLightbox] = useState(null); // { i } — zoom eval photos
  const [confirmDel, setConfirmDel] = useState(false); const [deleting, setDeleting] = useState(false);
  // REAL Firestore read of the whole leads collection (split client-side into the queues).
  useEffect(() => {
    const unsub = onSnapshot(collection(db, "leads"),
      (snap) => setAllLeads(snap.docs.map(d => ({ id: d.id, ...d.data() }))),
      (err) => console.error("Failed to load leads:", err));
    return () => unsub();
  }, []);
  // 2023 archive (separate collection) — only used when the 2023 source is selected.
  useEffect(() => {
    const unsub = onSnapshot(collection(db, "market_2023"),
      (snap) => setRecords2023(snap.docs.map(d => ({ id: d.id, ...d.data() }))),
      (err) => console.error("Failed to load market_2023:", err));
    return () => unsub();
  }, []);
  const is2023 = source === '2023';
  // Firestore Timestamp → ms (0 if missing). Used for sort + calendar filtering.
  const tsMs = (x) => { try { return x && x.toMillis ? x.toMillis() : (x && x.seconds ? x.seconds * 1000 : 0); } catch (e) { return 0; } };
  const inRange = (ms, from, to) => {
    if (from && ms < new Date(from + 'T00:00:00').getTime()) return false;
    if (to && ms > new Date(to + 'T23:59:59').getTime()) return false;
    return true;
  };
  const leadTs = (l) => tsMs(l.updatedAt || l.createdAt);
  const byRecent = (a, b) => leadTs(b) - leadTs(a); // most-recent first

  // Base per-source sets (for counts) + the filtered/sorted views actually shown.
  const fieldNew = allLeads.filter(l => l.status === 'new_lead');
  const rec2023New = records2023.filter(r => !r.evaluated2023);
  const queueBase = source === 'field' ? fieldNew : is2023 ? rec2023New : [];
  const queue = queueBase.filter(l => inRange(leadTs(l), dateFrom, dateTo)).sort(byRecent);

  // Output = every PROCESSED lead (left the new_lead queue), grouped by outcome. `outcome`
  // is stamped at gate time so it survives commitInject (which flips status→injected).
  const outcomeOf = (l) => l.outcome || (l.status === 'needs_eval' ? 'needs_eval' : l.status === 'other' ? 'other' : 'approved');
  const outputBase = source === 'field'
    ? allLeads.filter(l => ['enriched', 'needs_eval', 'other', 'injected'].includes(l.status))
    : is2023 ? records2023.filter(r => r.evaluated2023) : [];
  const outputFiltered = outputBase.filter(l => inRange(leadTs(l), outFrom, outTo));
  const outCounts = { approved: 0, other: 0, needs_eval: 0 };
  if (source === 'field') outputFiltered.forEach(l => { const o = outcomeOf(l); if (outCounts[o] != null) outCounts[o]++; });
  const output = (source === 'field' ? outputFiltered.filter(l => outcomeF === 'all' || outcomeOf(l) === outcomeF) : outputFiltered).sort(byRecent);

  // Calendar quick-chip date strings.
  const _pad = (n) => String(n).padStart(2, '0');
  const ymd = (dt) => `${dt.getFullYear()}-${_pad(dt.getMonth() + 1)}-${_pad(dt.getDate())}`;
  const _now = new Date();
  const todayStr = ymd(_now);
  const yestStr = ymd(new Date(_now.getTime() - 86400000));
  const monthStartStr = ymd(new Date(_now.getFullYear(), _now.getMonth(), 1));

  const selectLead = (l) => {
    setSelId(l.id);
    setOcr({}); // clear OCR results when switching leads
    setClassifyError('');
    setTranscribeError(''); setCityError(''); setPermits(null);
    setShowDetails(false); setConfirmDel(false); setLightbox(null);
    if (is2023) {
      // Shape a market_2023 archive card into the same editable draft the node-flow uses.
      setDraft({
        ...l,
        _source: '2023',
        address: l.formattedAddress || l.name || '',
        phase: l.phase || 'p0',
        enrichmentData: { ocr: '', initialComment: l.desc || '', phase: l.phase || 'p0', transcript: '', deficiencyNotes: '', ...(l.enrichmentData || {}) },
        demand: Array.isArray(l.demand) ? l.demand.map(b => ({ ...blankBiz(), ...b })) : [],
        supply: Array.isArray(l.supply) ? l.supply.map(b => ({ ...blankBiz(), ...b })) : [],
        photos: (Array.isArray(l.photos) ? l.photos : []).map(p => typeof p === 'string' ? { url: p, name: '', takenAt: '' } : p),
        cityData: l.cityData || {},
        audioUrl: l.audioUrl || '',
      });
      return;
    }
    setDraft({
      ...l,
      phase: l.phase || 'p0',
      siteStatus: l.siteStatus || 'active',
      enrichmentData: { ocr: '', initialComment: '', phase: l.phase || 'p0', transcript: '', deficiencyNotes: '', ...(l.enrichmentData || {}) },
      demand: Array.isArray(l.demand)
        ? l.demand.map(b => ({ ...blankBiz(), ...b, businessName: b.businessName || b.company || '' }))
        : (l.demand && (l.demand.businessName || l.demand.officeLocation || (l.demand.contacts && l.demand.contacts.length)) ? [{ ...blankBiz(), ...l.demand, businessName: l.demand.businessName || l.demand.company || '' }] : []),
      supply: (Array.isArray(l.supply) ? l.supply : []).map(b => ({ ...blankBiz(), ...b, businessName: b.businessName || b.company || '' })),
      photos: Array.isArray(l.photos) ? l.photos : [],
    });
  };
  const up = (patch) => setDraft(d => ({ ...d, ...patch }));
  const upEnrich = (k, v) => setDraft(d => ({ ...d, enrichmentData: { ...d.enrichmentData, [k]: v } }));
  const addDemand = () => setDraft(d => ({ ...d, demand: [...(d.demand || []), blankBiz()] }));
  const upDemandBiz = (i, nb) => setDraft(d => ({ ...d, demand: d.demand.map((b, idx) => idx === i ? nb : b) }));
  const rmDemandBiz = (i) => setDraft(d => ({ ...d, demand: d.demand.filter((_, idx) => idx !== i) }));
  const addSupply = () => setDraft(d => ({ ...d, supply: [...(d.supply || []), blankBiz()] }));
  const upSupplyBiz = (i, nb) => setDraft(d => ({ ...d, supply: d.supply.map((b, idx) => idx === i ? nb : b) }));
  const rmSupplyBiz = (i) => setDraft(d => ({ ...d, supply: d.supply.filter((_, idx) => idx !== i) }));

  // On-demand Vision OCR — calls the server-side callable; raw text only (no auto-fill).
  const readPhoto = async (i, url) => {
    if (!url) { setOcr(o => ({ ...o, [i]: { error: 'No image URL on this photo.' } })); return; }
    setOcr(o => ({ ...o, [i]: { loading: true } }));
    try {
      const res = await ocrPhotoFn({ url });
      const d = (res && res.data) || {};
      if (d.ok) setOcr(o => ({ ...o, [i]: { text: d.text ? d.text : '(no text detected)' } }));
      else setOcr(o => ({ ...o, [i]: { error: d.error || 'OCR failed.' } }));
    } catch (e) {
      setOcr(o => ({ ...o, [i]: { error: e.message || 'OCR call failed.' } }));
    }
  };
  const readAllPhotos = () => (draft.photos || []).forEach((p, i) => readPhoto(i, p.url));

  // Combine all per-photo OCR text, then ask Claude to PRE-FILL business cards.
  const ocrCombinedText = () => Object.keys(ocr).sort().map(k => ocr[k] && ocr[k].text).filter(Boolean).join('\n\n');
  const hasOcrText = () => Object.values(ocr).some(o => o && typeof o.text === 'string' && o.text.trim() && o.text !== '(no text detected)');
  const suggestBusinesses = async () => {
    const text = ocrCombinedText();
    if (!text.trim()) { setClassifyError('Run OCR on a photo first.'); return; }
    setClassifying(true); setClassifyError('');
    try {
      const res = await classifyBusinessesFn({ text, phase: (draft && draft.phase) || '' });
      const d = (res && res.data) || {};
      if (!d.ok) { setClassifyError(d.error || 'Classification failed.'); setClassifying(false); return; }
      const dem = [], sup = [];
      (d.businesses || []).forEach(b => {
        const card = { ...blankBiz(), businessName: b.businessName || '', trade: b.trade || '', roleCode: b.roleCode || '', phone: b.phone || '', website: b.website || '', suggested: true };
        if (b.side === 'demand') dem.push(card); else sup.push(card);
      });
      if (!dem.length && !sup.length) { setClassifyError('Claude found no businesses in the text.'); setClassifying(false); return; }
      setDraft(prev => ({ ...prev, demand: [...(prev.demand || []), ...dem], supply: [...(prev.supply || []), ...sup] }));
      setClassifying(false);
    } catch (e) {
      console.error('classifyBusinesses call failed:', e);
      setClassifyError(e.message || 'Classification call failed.');
      setClassifying(false);
    }
  };

  // Voice → Whisper transcription (writes into the editable enrichmentData.transcript).
  const transcribe = async () => {
    if (!draft.audioUrl) { setTranscribeError('No voice note on this lead.'); return; }
    setTranscribing(true); setTranscribeError('');
    try {
      const res = await transcribeAudioFn({ audioUrl: draft.audioUrl });
      const d = (res && res.data) || {};
      if (!d.ok) { setTranscribeError(d.error || 'Transcription failed.'); }
      else { upEnrich('transcript', d.transcript || '(no speech detected)'); }
    } catch (e) {
      console.error('transcribeAudio call failed:', e);
      setTranscribeError(e.message || 'Transcription call failed.');
    } finally { setTranscribing(false); }
  };

  // City Data → BuildData permit lookup (display-only; never touches business cards).
  const fetchCityPermit = async () => {
    if (!draft.address) { setCityError('Lead has no address to match.'); return; }
    setCityLoading(true); setCityError(''); setPermits(null);
    try {
      const res = await fetchCityPermitFn({ address: draft.address });
      const d = (res && res.data) || {};
      if (!d.ok) { setCityError(d.error || 'City permit lookup failed.'); }
      else {
        setPermits(d.permits || []);
        // Auto-apply the MAIN building permit's extracted fields (builder + value + phase
        // signal). The per-permit "Use this permit" buttons below still allow a manual override.
        if (d.cityData) up({ cityData: d.cityData });
      }
    } catch (e) {
      console.error('fetchCityPermit call failed:', e);
      setCityError(e.message || 'City permit call failed.');
    } finally { setCityLoading(false); }
  };
  // Store a chosen permit's FULL field set into cityData (flexible — keeps every key).
  const applyCityPermit = (permit) => up({ cityData: { ...permit, _source: 'BuildData / City of Toronto Open Data' } });

  // Web-enrichment (Google Places). Per-card: BusinessCard calls this and applies the
  // match to its own empty fields. "No match" returns { ok:true, match:null } (normal).
  const enrichOne = async (biz) => {
    try {
      const res = await enrichBusinessFn({ businessName: enrichName(biz), phone: biz.phone, website: biz.website, coords: draft.coords || null, city: enrichLocality(draft) });
      return (res && res.data) || { ok: false, error: 'No response from enrichment.' };
    } catch (e) { console.error('enrichBusiness call failed:', e); return { ok: false, error: e.message || 'Enrich call failed.' }; }
  };
  const [enrichingAll, setEnrichingAll] = useState(false);
  const enrichAll = async () => {
    setEnrichingAll(true);
    const fill = (arr) => Promise.all((arr || []).map(async (b) => {
      if (!enrichName(b) && !b.phone) return b;
      const r = await enrichOne(b);
      if (r && r.ok && r.match) {
        const m = r.match;
        return { ...b,
          officeLocation: b.officeLocation || m.officeLocation || '',
          phone: cleanPhone(m.phone) || b.phone || '',
          website: m.website || b.website || '',
          googleRating: m.googleRating == null ? '' : m.googleRating,
          googleReviewCount: m.googleReviewCount == null ? '' : m.googleReviewCount,
          placeId: m.placeId || '', enriched: true };
      }
      return b;
    }));
    try {
      const [dem, sup] = await Promise.all([fill(draft.demand), fill(draft.supply)]);
      setDraft(prev => ({ ...prev, demand: dem, supply: sup }));
    } catch (e) { console.error('enrichAll failed:', e); }
    finally { setEnrichingAll(false); }
  };

  // One-click full AI chain on the SELECTED lead (enrich only — status unchanged).
  const processThisLead = async () => {
    if (!draft || procLead.running) return;
    setProcLead({ running: true, step: 'starting…', note: '' });
    const res = await processLeadAI(draft, (s) => setProcLead(p => ({ ...p, step: s })));
    setDraft(prev => prev ? { ...prev, ...res.result } : prev); // reflect results in the open editor
    setProcLead({ running: false, step: '', note: res.errors.length ? `Done with ${res.errors.length} step issue(s): ${res.errors.slice(0, 2).join('; ')}` : 'Done — review and approve manually.' });
  };

  // One-click full AI chain across the WHOLE input queue, sequentially (enrich only).
  const processAllInput = async () => {
    const leads = queue; // status === 'new_lead'
    if (!leads.length || bulk.running) return;
    bulkCancel.current = false;
    setBulk({ running: true, done: 0, total: leads.length, failed: 0, summary: '' });
    let failed = 0;
    for (let i = 0; i < leads.length; i++) {
      if (bulkCancel.current) break;
      setBulk(b => ({ ...b, done: i }));
      try { const res = await processLeadAI(leads[i]); if (!res.ok) failed++; }
      catch (e) { failed++; console.error('bulk process failed for', leads[i].id, e); }
      setBulk(b => ({ ...b, done: i + 1, failed }));
    }
    const cancelled = bulkCancel.current;
    setBulk(b => ({ ...b, running: false, summary: `${cancelled ? 'Cancelled — ' : ''}Processed ${b.done} of ${leads.length}${failed ? `, ${failed} had issues` : ''}. All stay in the queue for manual review + approval.` }));
  };
  const cancelBulk = () => { bulkCancel.current = true; };

  // Write edited fields + new status back to the real lead doc (leaves the new_lead queue).
  const writeStatus = async (status) => {
    if (!draft) return;
    setSaving(true);
    try {
      const { id, ...fields } = draft;
      // Drop the UI-only "suggested" flag so it never lands in the stored lead schema.
      const strip = (arr) => (arr || []).map(({ suggested, enriched, ...b }) => b);
      // Stamp a stable `outcome` (survives commitInject flipping status→injected) so the
      // output can group Approved / Others / Eval even after a lead is injected.
      const outcome = status === 'enriched' ? 'approved' : status === 'needs_eval' ? 'needs_eval' : status === 'other' ? 'other' : null;
      // Editing here uses the canonical phase dropdown → stamp the version (clears the
      // "needs review" flag) and persist the site Stopped/active flag.
      await setDoc(doc(db, "leads", id), { ...fields, demand: strip(fields.demand), supply: strip(fields.supply), status, outcome, phaseSystem: CANONICAL_PHASE_VERSION, siteStatus: fields.siteStatus || 'active', updatedAt: serverTimestamp() }, { merge: true });
      setSelId(null); setDraft(null); setShowDetails(false);
    } catch (e) { console.error("Save failed:", e); alert("Save failed: " + e.message); } finally { setSaving(false); }
  };
  // Delete a junk/wrong input lead (confirmed) — it leaves the input queue.
  const delLead = async () => {
    if (!draft) return;
    setDeleting(true);
    try { await deleteDoc(doc(db, "leads", draft.id)); setSelId(null); setDraft(null); setConfirmDel(false); }
    catch (e) { console.error("Delete failed:", e); alert("Delete failed: " + e.message); }
    finally { setDeleting(false); }
  };
  const inject = async (l) => {
    setInjectingId(l.id); setInjectError('');
    try { await commitInject(l); }
    catch (e) { console.error("Inject failed:", e); setInjectError(`Inject failed: ${e.message || e}`); }
    finally { setInjectingId(null); }
  };
  // 2023 output — writes the evaluated record back into market_2023 ONLY. Never touches
  // leads / businesses / projects / live Market. No inject-to-board (2023 is non-proceedable).
  const insert2023 = async () => {
    if (!draft) return;
    setInserting(true);
    try {
      const strip = (arr) => (arr || []).map(({ suggested, enriched, ...b }) => b);
      await setDoc(doc(db, "market_2023", draft.id), {
        demand: strip(draft.demand), supply: strip(draft.supply),
        enrichmentData: draft.enrichmentData || {}, cityData: draft.cityData || {},
        phase: draft.phase || null,
        evaluated2023: true, evaluatedAt: serverTimestamp(),
      }, { merge: true });
      setSelId(null); setDraft(null);
    } catch (e) { console.error("Insert to 2023 Market failed:", e); alert("Insert failed: " + e.message); }
    finally { setInserting(false); }
  };

  const plug = (label) => <span className="text-[10px] text-amber-500/80 font-bold ml-2">⚙ {label} plugs in here</span>;
  const ic = "w-full bg-gray-900 text-white border border-gray-700 rounded px-2 py-1.5 text-sm outline-none focus:ring-1 focus:ring-orange-500";

  return (
    <div className="absolute inset-0 z-0 bg-gray-900 flex flex-col">
      <div className="flex items-center justify-between p-3 border-b border-gray-800 shrink-0">
        <div className="flex items-center"><Shield className="text-orange-500 mr-2" size={20} /><span className="font-bold tracking-widest uppercase text-white text-sm">Evaluation &amp; Sorting</span></div>
        <div className="flex items-center gap-3 text-xs font-bold"><button onClick={onBack} className="text-gray-300 hover:text-white">← Manager</button><button onClick={goBack} className="text-gray-400 hover:text-white">Exit</button></div>
      </div>
      <div className="flex-1 flex min-h-0">
        {/* INPUT — source-selectable queue */}
        <div className="w-64 shrink-0 border-r border-gray-800 flex flex-col">
          {/* Source selector (with per-source counts) + calendar filter */}
          <div className="p-2 border-b border-gray-800 space-y-2">
            <div className="text-[9px] font-bold text-gray-500 uppercase tracking-wider">Source</div>
            <div className="flex rounded-lg border border-gray-700 overflow-hidden text-[11px] font-bold">
              {[['field', 'Field', fieldNew.length], ['mls', 'MLS', 0], ['2023', '2023', rec2023New.length]].map(([k, lbl, ct]) => (
                <button key={k} onClick={() => { setSource(k); setSelId(null); setDraft(null); }} className={`flex-1 py-1.5 ${source === k ? 'bg-white text-gray-900' : 'bg-gray-800 text-gray-300 hover:bg-gray-700'}`}>{lbl} <span className="opacity-60">{ct}</span></button>
              ))}
            </div>
            {source !== 'mls' && (
              <div className="space-y-1">
                <div className="flex items-center gap-1">
                  <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="flex-1 min-w-0 bg-gray-800 text-gray-200 border border-gray-700 rounded px-1 py-1 text-[10px] outline-none" />
                  <span className="text-gray-600 text-[10px]">→</span>
                  <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className="flex-1 min-w-0 bg-gray-800 text-gray-200 border border-gray-700 rounded px-1 py-1 text-[10px] outline-none" />
                </div>
                <div className="flex gap-1 flex-wrap items-center">
                  <button onClick={() => { setDateFrom(todayStr); setDateTo(todayStr); }} className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-gray-800 border border-gray-700 text-gray-300 hover:bg-gray-700">Today</button>
                  <button onClick={() => { setDateFrom(yestStr); setDateTo(yestStr); }} className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-gray-800 border border-gray-700 text-gray-300 hover:bg-gray-700">Yesterday</button>
                  <button onClick={() => { setDateFrom(monthStartStr); setDateTo(todayStr); }} className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-gray-800 border border-gray-700 text-gray-300 hover:bg-gray-700">This month</button>
                  {(dateFrom || dateTo) && <button onClick={() => { setDateFrom(''); setDateTo(''); }} className="text-[10px] font-bold text-gray-400 underline">Clear</button>}
                </div>
              </div>
            )}
          </div>
          <div className="p-2 text-[10px] font-bold text-gray-500 uppercase tracking-wider border-b border-gray-800">
            {source === 'mls' ? 'Input · MLS' : `Input · ${queue.length} shown${(dateFrom || dateTo) ? ' · in range' : ''} · newest first`}
          </div>
          {/* Bulk AI — FIELD ONLY (writes to live leads) */}
          {source === 'field' && queue.length > 0 && (
            <div className="p-2 border-b border-gray-800">
              {!bulk.running
                ? <button onClick={processAllInput} className="w-full py-1.5 rounded bg-fuchsia-700 hover:bg-fuchsia-600 text-white text-[11px] font-bold">⚡ Process all input leads (AI)</button>
                : <div className="space-y-1">
                    <div className="text-[11px] text-gray-300 font-bold flex items-center"><Loader2 size={12} className="animate-spin mr-1" /> Processing {bulk.done} of {bulk.total}…{bulk.failed ? ` (${bulk.failed} issues)` : ''}</div>
                    <button onClick={cancelBulk} className="w-full py-1 rounded bg-gray-700 hover:bg-gray-600 text-gray-200 text-[10px] font-bold">Cancel</button>
                  </div>}
              {bulk.summary && <p className="text-[10px] text-gray-500 mt-1">{bulk.summary}</p>}
              <p className="text-[9px] text-gray-600 mt-1">Enrich only — never approves. Leads stay here for manual review.</p>
            </div>
          )}
          <div className="flex-1 overflow-y-auto p-2 space-y-1.5">
            {source === 'mls' && <p className="text-center text-gray-500 text-xs py-6 px-2">No MLS source connected yet.<br /><span className="text-gray-600">Reserved for a future feed.</span></p>}
            {source !== 'mls' && queue.length === 0 && <p className="text-center text-gray-500 text-xs py-6">{is2023 ? 'No 2023 records to evaluate.' : 'No new leads.'}</p>}
            {source !== 'mls' && queue.map(l => (
              <button key={l.id} onClick={() => selectLead(l)} className={`w-full text-left p-2 rounded-lg border text-xs ${selId === l.id ? 'bg-orange-700 border-orange-500 text-white' : 'bg-gray-800 border-gray-700 text-gray-300 hover:bg-gray-700'}`}>
                {is2023 ? (
                  <>
                    <div className="font-bold truncate">{l.name || '(unnamed)'}</div>
                    <div className="text-[10px] opacity-80 truncate">{l.listName || '—'}{(l.neighbourhood || l.fsa) ? ` · ${l.neighbourhood || l.fsa}` : ''}</div>
                    <div className="text-[10px] opacity-70">{(l.photos || []).length} photo{(l.photos || []).length === 1 ? '' : 's'}{l.phase ? ` · ${l.phase}` : ''}</div>
                  </>
                ) : (
                  <>
                    <div className="font-bold truncate">{(l.address || 'No address').split(',')[0]}</div>
                    <div className="text-[10px] opacity-80 truncate">{l.collector_name || '—'} · {fmtDate(l.createdAt)}</div>
                    <div className="text-[10px] opacity-70">{(l.photos || []).length} photo{(l.photos || []).length === 1 ? '' : 's'}{l.audioUrl ? ' · 🎙' : ''}</div>
                  </>
                )}
              </button>
            ))}
          </div>
        </div>
        {/* PROCESS — node flow (manual; API markers only) */}
        <div className="flex-1 flex flex-col min-w-0">
          {!draft ? (
            <div className="flex-1 flex items-center justify-center text-gray-500 text-sm px-4 text-center">Select a lead on the left to evaluate.</div>
          ) : (
            <>
              <div className="flex-1 overflow-y-auto p-3 space-y-3">
                <div className="bg-gray-800 border border-gray-700 rounded-lg p-3 text-xs text-gray-300">
                  <div className="text-[10px] font-bold text-gray-500 uppercase mb-1">Raw lead</div>
                  <div className="text-white font-bold">{draft.address || 'No address'}</div>
                  <div>{draft.collector_name || '—'} · {fmtDate(draft.createdAt)} · {draft.zoneName || '—'}</div>

                  {/* #1 — prominent, zoomable photos so the evaluator can judge the phase */}
                  {photoUrls(draft.photos).length > 0 && (
                    <div className="mt-2">
                      <div className="text-[9px] font-bold text-gray-500 uppercase mb-1">Photos ({photoUrls(draft.photos).length}) — tap to zoom</div>
                      <div className="flex gap-1.5 overflow-x-auto">
                        {photoUrls(draft.photos).map((u, i) => <img key={i} src={u} alt="" onClick={() => setLightbox({ i })} className="w-16 h-16 object-cover rounded border border-gray-700 shrink-0 cursor-pointer hover:opacity-80" />)}
                      </div>
                    </div>
                  )}

                  {/* Details + Delete row */}
                  <div className="flex items-center gap-2 mt-2">
                    <button onClick={() => setShowDetails(s => !s)} className="flex-1 py-1.5 rounded-lg font-bold text-[11px] bg-gray-700 hover:bg-gray-600 text-white flex items-center justify-center gap-1"><SearchIcon size={12} /> {showDetails ? 'Hide details' : 'Details'}</button>
                    {!is2023 && <button onClick={() => setConfirmDel(true)} className="py-1.5 px-2 rounded-lg font-bold text-[11px] bg-gray-800 border border-red-500/30 text-red-300 hover:bg-red-900/40 flex items-center gap-1"><Trash2 size={12} /> Delete</button>}
                  </div>
                  {confirmDel && (
                    <div className="mt-2 flex items-center gap-2 bg-red-950/30 border border-red-800/50 rounded-lg p-2">
                      <span className="text-[11px] font-bold text-red-200">Delete this lead? This can't be undone.</span>
                      <button onClick={() => setConfirmDel(false)} disabled={deleting} className="ml-auto text-[11px] font-bold text-gray-300 bg-gray-800 rounded px-2 py-1 disabled:opacity-50">Cancel</button>
                      <button onClick={delLead} disabled={deleting} className="text-[11px] font-bold text-white bg-red-600 hover:bg-red-700 rounded px-2 py-1 disabled:opacity-50">{deleting ? 'Deleting…' : 'Delete'}</button>
                    </div>
                  )}

                  {/* #2 — process inspection: what each AI/API step produced for THIS lead */}
                  {showDetails && (
                    <div className="mt-2 bg-gray-950 border border-gray-700 rounded-lg p-2 space-y-1.5">
                      <div className="text-[10px] font-bold text-gray-400 uppercase">Process inspection — what each step did</div>
                      {PIPE_NODES.map(n => { const r = n.result(draft); return (
                        <div key={n.key} className="text-[11px]">
                          <div className="font-bold text-gray-300">{n.name}</div>
                          {r
                            ? <div className="text-emerald-300 whitespace-pre-wrap break-words bg-emerald-500/10 border border-emerald-500/20 rounded px-1.5 py-1 mt-0.5 max-h-24 overflow-y-auto">{r}</div>
                            : <div className="text-amber-400 font-bold">none found</div>}
                        </div>
                      ); })}
                      <p className="text-[9px] text-gray-600">Visibility only. If enrichment is systematically missing obvious websites, that's a separate enrich-function investigation.</p>
                    </div>
                  )}

                  <button onClick={processThisLead} disabled={procLead.running} className="w-full mt-2 py-2 rounded-lg font-bold text-xs bg-fuchsia-700 hover:bg-fuchsia-600 text-white disabled:opacity-50 flex items-center justify-center">{procLead.running ? <><Loader2 size={14} className="animate-spin mr-1.5" /> {procLead.step || 'Processing…'}</> : '⚡ Process this lead (AI)'}</button>
                  {procLead.note && <p className="text-[10px] text-gray-500 mt-1">{procLead.note}</p>}
                  <p className="text-[9px] text-gray-600 mt-1">Runs OCR → classify → enrich → transcribe → city permit. Enrich only — you still approve manually.</p>
                </div>
                <div className="bg-gray-800 border border-gray-700 rounded-lg p-3">
                  <div className="text-xs font-bold text-orange-300 uppercase flex items-center flex-wrap">Phase node {plug('Claude')}</div>
                  <p className="text-[11px] text-gray-500 mt-1">Collector said: <b className="text-gray-300">{draft.enrichmentData.phase || draft.phase || 'p0'}</b></p>
                  <select value={draft.phase} onChange={e => up({ phase: e.target.value })} className={ic + ' mt-1'}>{PHASES.map(([v, t]) => <option key={v} value={v}>{v} · {t}</option>)}</select>
                </div>
                <div className="bg-gray-800 border border-gray-700 rounded-lg p-3">
                  <div className="text-xs font-bold text-orange-300 uppercase flex items-center flex-wrap gap-y-1">City Data node <span className="text-[10px] font-bold text-green-400 ml-2 inline-flex items-center">⚙ City permit (BuildData): on</span></div>
                  <button onClick={fetchCityPermit} disabled={cityLoading || !draft.address} className="w-full mt-2 py-2 rounded-lg font-bold text-xs bg-orange-700 hover:bg-orange-600 text-white disabled:opacity-40 flex items-center justify-center">{cityLoading ? <><Loader2 size={14} className="animate-spin mr-1.5" /> Fetching permit…</> : '🏛 Fetch city permit'}</button>
                  {!draft.address && <p className="text-[10px] text-gray-600 mt-1">Lead has no address to match.</p>}
                  {cityError && <p className="text-[11px] text-red-400 mt-1">{cityError}</p>}

                  {/* Currently stored on the lead (the chosen permit) — rendered dynamically. */}
                  {draft.cityData && Object.keys(draft.cityData).filter(k => k !== '_source' && draft.cityData[k] !== '' && draft.cityData[k] != null).length > 0 && (
                    <div className="mt-2 border border-gray-700 rounded-lg p-2">
                      <div className="text-[10px] font-bold text-gray-500 uppercase mb-1">Saved on lead</div>
                      <div className="grid grid-cols-2 gap-1.5">
                        {Object.keys(draft.cityData).filter(k => k !== '_source' && draft.cityData[k] !== '' && draft.cityData[k] != null).map(k => (
                          <div key={k} className="bg-gray-900 rounded px-2 py-1"><span className="block text-[9px] text-gray-500 uppercase truncate">{k}</span><span className="text-[11px] text-gray-200 break-words">{String(draft.cityData[k])}</span></div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Fetched results — ALL fields rendered dynamically; user picks one. */}
                  {permits && permits.length === 0 && <p className="text-[11px] text-gray-500 mt-2">No permit found for this address.</p>}
                  {permits && permits.length > 0 && (
                    <div className="mt-2 space-y-2">
                      <div className="text-[10px] font-bold text-gray-500 uppercase">{permits.length} permit{permits.length === 1 ? '' : 's'} found · main building permit auto-selected{draft.cityData && draft.cityData.suggested_phase ? ` · suggests ${String(draft.cityData.suggested_phase).toUpperCase()}` : ''}</div>
                      {draft.cityData && draft.cityData.phase_reason && <div className="text-[10px] text-gray-500">Phase signal: {draft.cityData.phase_reason}{draft.cityData.permit_types_present ? ` · types present: ${draft.cityData.permit_types_present}` : ''} <span className="text-gray-600">(inferred — BuildData has no inspection dates)</span></div>}
                      {permits.map((pm, i) => { const pn = pm.permit_num || pm.permit_number; const isMain = pn && draft.cityData && pn === draft.cityData.primary_permit_num; return (
                        <div key={i} className={`border rounded-lg p-2 ${isMain ? 'border-orange-500/60 bg-orange-500/5' : 'border-gray-700'}`}>
                          {isMain && <div className="text-[9px] font-bold text-orange-300 mb-1">✓ MAIN building permit (auto-selected)</div>}
                          <div className="grid grid-cols-2 gap-1.5">
                            {Object.keys(pm).filter(k => pm[k] !== '' && pm[k] != null).map(k => (
                              <div key={k} className="bg-gray-900 rounded px-2 py-1"><span className="block text-[9px] text-gray-500 uppercase truncate">{k}</span><span className="text-[11px] text-gray-200 break-words">{typeof pm[k] === 'object' ? JSON.stringify(pm[k]) : String(pm[k])}</span></div>
                            ))}
                          </div>
                          <button onClick={() => applyCityPermit(pm)} className={`w-full mt-2 py-1.5 rounded text-white text-[11px] font-bold ${isMain ? 'bg-gray-700 hover:bg-gray-600' : 'bg-indigo-600 hover:bg-indigo-700'}`}>{isMain ? 'Main permit (already applied)' : 'Use this permit → City Data'}</button>
                        </div>
                      ); })}
                    </div>
                  )}
                  <p className="text-[10px] text-gray-600 mt-2">Permit data via BuildData, sourced from City of Toronto Open Data (Open Government Licence – Toronto). Indicative only — verify against the City.</p>
                </div>
                <div className="bg-gray-800 border border-gray-700 rounded-lg p-3">
                  <div className="text-xs font-bold text-blue-300 uppercase flex items-center flex-wrap gap-y-1">Businesses node <span className="text-[10px] font-bold text-green-400 ml-2 inline-flex items-center">⚙ Vision OCR: on</span><span className="text-[10px] font-bold text-green-400 ml-2 inline-flex items-center">⚙ Claude classify: on</span><span className="text-[10px] font-bold text-green-400 ml-2 inline-flex items-center">⚙ Web-enrichment (Google Places): on</span></div>
                  {(draft.photos || []).length > 0 ? (
                    <div className="my-2">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-[10px] font-bold text-gray-500 uppercase">Photos ({draft.photos.length})</span>
                        <button onClick={readAllPhotos} className="text-[11px] font-bold text-blue-400 hover:text-blue-300">Read all photos (OCR)</button>
                      </div>
                      <div className="space-y-2">
                        {draft.photos.map((p, i) => { const o = ocr[i] || {}; return (
                          <div key={i} className="flex gap-2">
                            <img src={p.url} alt="" onClick={() => setLightbox({ i })} className="w-20 h-20 object-cover rounded border border-gray-700 shrink-0 cursor-pointer hover:opacity-80" />
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center justify-between gap-2">
                                <span className="text-[10px] text-gray-400 truncate">{p.name || `Photo ${i + 1}`}</span>
                                <button onClick={() => readPhoto(i, p.url)} disabled={o.loading} className="text-[11px] font-bold text-blue-400 hover:text-blue-300 disabled:opacity-50 shrink-0">{o.loading ? 'Reading…' : 'Read (OCR)'}</button>
                              </div>
                              {o.loading && <div className="text-[11px] text-gray-500 mt-1 flex items-center"><Loader2 size={12} className="animate-spin mr-1" /> Running Vision OCR…</div>}
                              {o.error && <div className="text-[11px] text-red-400 mt-1">OCR error: {o.error}</div>}
                              {typeof o.text === 'string' && <textarea readOnly value={o.text} className="w-full mt-1 bg-gray-950 text-gray-200 border border-gray-700 rounded px-2 py-1 text-[11px] h-24 outline-none font-mono" />}
                            </div>
                          </div>
                        ); })}
                      </div>
                      <p className="text-[10px] text-gray-600 mt-1">Raw text only — read it and fill the business cards below by hand.</p>
                    </div>
                  ) : <p className="text-[11px] text-gray-600 my-2">No photos on this lead.</p>}
                  <div className="mt-2 border-t border-gray-700 pt-2">
                    <button onClick={suggestBusinesses} disabled={classifying || !hasOcrText()} className="w-full py-2 rounded-lg font-bold text-xs bg-orange-700 hover:bg-orange-600 text-white disabled:opacity-40 flex items-center justify-center">{classifying ? <><Loader2 size={14} className="animate-spin mr-1.5" /> Asking Claude…</> : '✨ Suggest businesses from OCR'}</button>
                    {!hasOcrText() && <p className="text-[10px] text-gray-600 mt-1">Run OCR on a photo first to enable suggestions.</p>}
                    {classifyError && <p className="text-[11px] text-red-400 mt-1">{classifyError}</p>}
                    <p className="text-[10px] text-gray-600 mt-1">Suggestions pre-fill editable cards (marked “suggested — review”) — confirm or fix before approving.</p>
                    {((draft.demand || []).length + (draft.supply || []).length) > 0 && <button onClick={enrichAll} disabled={enrichingAll} className="w-full mt-2 py-2 rounded-lg font-bold text-xs bg-cyan-700 hover:bg-cyan-600 text-white disabled:opacity-40 flex items-center justify-center">{enrichingAll ? <><Loader2 size={14} className="animate-spin mr-1.5" /> Enriching all…</> : '🌐 Enrich all businesses (Google Places)'}</button>}
                  </div>
                  <div className="mt-2 border-t border-gray-700 pt-2">
                    <div className="flex items-center justify-between mb-1"><span className="text-[10px] font-bold text-gray-500 uppercase">Demand — builders / owners ({(draft.demand || []).length})</span><button onClick={addDemand} className="text-[11px] font-bold text-blue-400 hover:text-blue-300">+ Add business</button></div>
                    {(draft.demand || []).map((b, i) => (
                      <BusinessCard key={i} biz={b} side="demand" onChange={nb => upDemandBiz(i, nb)} onRemove={() => rmDemandBiz(i)} onEnrich={enrichOne} nationalities={nat.options} />
                    ))}
                    {(draft.demand || []).length === 0 && <p className="text-[10px] text-gray-600 italic">No demand businesses yet — tap “+ Add business”.</p>}
                  </div>
                  <div className="mt-3 border-t border-gray-700 pt-2">
                    <div className="flex items-center justify-between mb-1"><span className="text-[10px] font-bold text-gray-500 uppercase">Supply businesses ({(draft.supply || []).length})</span><button onClick={addSupply} className="text-[11px] font-bold text-blue-400 hover:text-blue-300">+ Add business</button></div>
                    {(draft.supply || []).map((b, i) => (
                      <BusinessCard key={i} biz={b} side="supply" onChange={nb => upSupplyBiz(i, nb)} onRemove={() => rmSupplyBiz(i)} onEnrich={enrichOne} nationalities={nat.options} />
                    ))}
                    <p className="text-[10px] text-gray-600">* Office location is the dedup key for businesses.</p>
                  </div>
                </div>
                <div className="bg-gray-800 border border-gray-700 rounded-lg p-3">
                  <div className="text-xs font-bold text-orange-300 uppercase flex items-center flex-wrap">Other / Deficiency node {plug('Claude')}</div>
                  <textarea rows="2" className={ic + ' mt-1'} placeholder="Notes / flags…" value={draft.enrichmentData.deficiencyNotes} onChange={e => upEnrich('deficiencyNotes', e.target.value)} />
                </div>
                <div className="bg-gray-800 border border-gray-700 rounded-lg p-3">
                  <div className="text-xs font-bold text-teal-300 uppercase flex items-center flex-wrap gap-y-1">Voice node <span className="text-[10px] font-bold text-green-400 ml-2 inline-flex items-center">⚙ Whisper: on</span></div>
                  {draft.audioUrl ? <audio controls src={draft.audioUrl} className="w-full mt-2 h-9" /> : <p className="text-[11px] text-gray-500 mt-1">No voice note.</p>}
                  {draft.audioUrl && <button onClick={transcribe} disabled={transcribing} className="w-full mt-2 py-2 rounded-lg font-bold text-xs bg-teal-700 hover:bg-teal-600 text-white disabled:opacity-40 flex items-center justify-center">{transcribing ? <><Loader2 size={14} className="animate-spin mr-1.5" /> Transcribing…</> : '🎙 Transcribe'}</button>}
                  {transcribeError && <p className="text-[11px] text-red-400 mt-1">{transcribeError}</p>}
                  <textarea rows="2" className={ic + ' mt-2'} placeholder="Transcript (Whisper, editable)…" value={draft.enrichmentData.transcript} onChange={e => upEnrich('transcript', e.target.value)} />
                  {draft.initialComment ? <p className="text-[10px] text-gray-500 mt-1">Field note: {draft.initialComment}</p> : null}
                </div>
              </div>
              {/* Output action bar — differs by source */}
              {is2023 ? (
                <div className="shrink-0 border-t border-gray-800 p-3">
                  <button disabled={inserting} onClick={insert2023} className="w-full py-2.5 rounded-lg font-bold bg-amber-600 hover:bg-amber-700 text-white text-sm disabled:opacity-50 flex items-center justify-center">{inserting ? <><Loader2 size={16} className="animate-spin mr-1.5" /> Inserting…</> : 'Insert to 2023 Market →'}</button>
                  <p className="text-[10px] text-gray-600 mt-1.5 text-center">Saves the evaluated record to the 2023 archive only — no board/live Market (prospecting reference).</p>
                </div>
              ) : (
                <div className="shrink-0 border-t border-gray-800 p-3 space-y-2">
                  {/* Site Stopped — separate from phase. When stopped, the lead routes to Others. */}
                  <button type="button" onClick={() => up({ siteStatus: draft.siteStatus === 'stopped' ? 'active' : 'stopped' })}
                    className={`w-full py-2 rounded-lg text-xs font-bold border flex items-center justify-center gap-2 ${draft.siteStatus === 'stopped' ? 'bg-red-900/40 border-red-500/50 text-red-300' : 'bg-gray-800 border-gray-700 text-gray-300 hover:bg-gray-700'}`}>
                    <Ban size={13} /> {draft.siteStatus === 'stopped' ? 'STOPPED · keeps phase · routes to Others' : 'Mark site Stopped'}
                  </button>
                  <div className="grid grid-cols-3 gap-2">
                    <button disabled={saving || draft.siteStatus === 'stopped'} title={draft.siteStatus === 'stopped' ? 'Stopped sites route to Others' : ''} onClick={() => writeStatus('enriched')} className="py-2.5 rounded-lg font-bold bg-green-600 hover:bg-green-700 text-white text-sm disabled:opacity-40">Approve →</button>
                    <button disabled={saving || draft.siteStatus === 'stopped'} title={draft.siteStatus === 'stopped' ? 'Stopped sites route to Others' : ''} onClick={() => writeStatus('needs_eval')} className="py-2.5 rounded-lg font-bold bg-amber-600 hover:bg-amber-700 text-white text-sm disabled:opacity-40">Needs Eval</button>
                    <button disabled={saving} onClick={() => writeStatus('other')} className={`py-2.5 rounded-lg font-bold text-white text-sm disabled:opacity-50 ${draft.siteStatus === 'stopped' ? 'bg-red-700 hover:bg-red-600 ring-2 ring-red-400' : 'bg-gray-700 hover:bg-gray-600'}`}>Others</button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
        {/* OUTPUT — all processed leads, grouped by outcome; per-lead Inject across outcomes */}
        <div className="w-64 shrink-0 border-l border-gray-800 flex flex-col">
          <div className="p-2 border-b border-gray-800 space-y-2">
            <div className="text-[10px] font-bold text-gray-500 uppercase tracking-wider">
              {is2023 ? `Output · inserted to 2023 (${output.length})` : source === 'mls' ? 'Output' : 'Output · processed'}
            </div>
            {source === 'field' && (
              <>
                {/* Outcome filter + counts */}
                <div className="flex gap-1 text-[10px] font-bold">
                  {[['all', 'All', outputFiltered.length], ['approved', 'Approved', outCounts.approved], ['other', 'Others', outCounts.other], ['needs_eval', 'Eval', outCounts.needs_eval]].map(([k, lbl, ct]) => (
                    <button key={k} onClick={() => setOutcomeF(k)} className={`flex-1 py-1 rounded border ${outcomeF === k ? 'bg-white text-gray-900 border-white' : 'bg-gray-800 border-gray-700 text-gray-300 hover:bg-gray-700'}`}>{lbl}<br /><span className="opacity-70">{ct}</span></button>
                  ))}
                </div>
                {/* Calendar filter */}
                <div className="flex items-center gap-1">
                  <input type="date" value={outFrom} onChange={e => setOutFrom(e.target.value)} className="flex-1 min-w-0 bg-gray-800 text-gray-200 border border-gray-700 rounded px-1 py-1 text-[10px] outline-none" />
                  <span className="text-gray-600 text-[10px]">→</span>
                  <input type="date" value={outTo} onChange={e => setOutTo(e.target.value)} className="flex-1 min-w-0 bg-gray-800 text-gray-200 border border-gray-700 rounded px-1 py-1 text-[10px] outline-none" />
                  {(outFrom || outTo) && <button onClick={() => { setOutFrom(''); setOutTo(''); }} className="text-[10px] font-bold text-gray-400 shrink-0">✕</button>}
                </div>
              </>
            )}
          </div>
          <div className="flex-1 overflow-y-auto p-2 space-y-1.5">
            {is2023 ? (
              <>
                {output.length === 0 && <p className="text-center text-gray-500 text-xs py-6">Nothing inserted yet.</p>}
                {output.map(r => (
                  <div key={r.id} className="bg-gray-800 border border-gray-700 rounded-lg p-2 text-xs">
                    <div className="text-white font-bold truncate">{r.name || '(unnamed)'}</div>
                    <div className="text-[10px] text-gray-400">✓ inserted to 2023 Market · {((r.demand || []).length + (r.supply || []).length)} biz</div>
                  </div>
                ))}
              </>
            ) : source === 'mls' ? (
              <p className="text-center text-gray-500 text-xs py-6 px-2">—</p>
            ) : (
              <>
                {output.length === 0 && <p className="text-center text-gray-500 text-xs py-6">No processed leads{(outFrom || outTo) ? ' in range' : ''}.</p>}
                {injectError && <div className="text-[11px] text-red-400 px-1">{injectError}</div>}
                {output.map(l => { const nBiz = (l.demand || []).length + (l.supply || []).length; const oc = outcomeOf(l); const injected = l.status === 'injected'; const ocCls = oc === 'approved' ? 'bg-green-500/20 text-green-300' : oc === 'other' ? 'bg-gray-600/40 text-gray-300' : 'bg-amber-500/20 text-amber-300'; return (
                  <div key={l.id} className="bg-gray-800 border border-gray-700 rounded-lg p-2 text-xs">
                    <div className="flex items-center justify-between gap-1">
                      <div className="text-white font-bold truncate">{(l.address || 'No address').split(',')[0]}</div>
                      <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded shrink-0 ${ocCls}`}>{oc === 'approved' ? 'Approved' : oc === 'other' ? 'Other' : 'Eval'}</span>
                    </div>
                    <div className="text-[10px] text-gray-400">{injected ? '✓ injected → Market' : 'processed'} · {nBiz} biz · {fmtDate(l.updatedAt || l.createdAt)}</div>
                    <button onClick={() => inject(l)} disabled={injectingId === l.id} className={`mt-1 w-full py-1.5 rounded text-[11px] font-bold disabled:opacity-50 ${injected ? 'bg-gray-700 hover:bg-gray-600 text-gray-200' : 'bg-indigo-600 hover:bg-indigo-700 text-white'}`}>{injectingId === l.id ? (injected ? 'Re-syncing…' : 'Injecting…') : (injected ? 'Re-sync to Market' : 'Inject → Market')}</button>
                  </div>
                ); })}
              </>
            )}
          </div>
        </div>
      </div>
      {lightbox && draft && <PhotoLightbox urls={photoUrls(draft.photos)} i={lightbox.i} onClose={() => setLightbox(null)} onIndex={(n) => setLightbox({ i: n })} />}
    </div>
  );
};

// ── REUSABLE Market filter bar (sides · phases · zones · search) ────────────────────
// Controlled + props-driven so a future "2023 Market" can reuse it against another data
// source. `zones` = [{value,label}] (built from whatever zone field the data carries).
// `children` = optional extra controls (e.g. the live Market's tier select).
const MarketFilterBar = ({ value, onChange, zones, resultLabel, children }) => {
  const sel = "bg-gray-800 text-white border border-gray-700 rounded px-2 py-1.5 text-xs outline-none";
  const set = (patch) => onChange({ ...value, ...patch });
  const active = (value.side !== 'all' ? 1 : 0) + (value.phase !== 'all' ? 1 : 0) + (value.zone !== 'all' ? 1 : 0) + (value.q && value.q.trim() ? 1 : 0);
  return (
    <div className="flex flex-wrap items-center gap-2">
      <select value={value.side} onChange={e => set({ side: e.target.value })} className={sel}><option value="all">All sides</option><option value="demand">Demand</option><option value="supply">Supply</option></select>
      <select value={value.phase} onChange={e => set({ phase: e.target.value })} className={sel}><option value="all">All phases</option>{PHASES.map(([val, t]) => <option key={val} value={val}>{val} · {t}</option>)}</select>
      <select value={value.zone} onChange={e => set({ zone: e.target.value })} className={sel}>{zones.map(z => <option key={z.value} value={z.value}>{z.label}</option>)}</select>
      {children}
      <input value={value.q} onChange={e => set({ q: e.target.value })} placeholder="Search name / phone / web / address…" className={sel + ' flex-1 min-w-[140px]'} />
      {active > 0 && <button onClick={() => onChange({ ...value, side: 'all', phase: 'all', zone: 'all', q: '' })} className="text-[11px] font-bold text-gray-300 hover:text-white bg-gray-800 border border-gray-700 rounded px-2 py-1.5">Clear ({active})</button>}
      {resultLabel && <span className="text-[11px] text-gray-500 font-bold ml-auto">{resultLabel}</span>}
    </div>
  );
};

// Photos may be stored as bare URL strings (2023 archive) or { url, ... } objects
// (field leads/projects). Normalize to a flat list of URL strings.
const photoUrls = (arr) => (Array.isArray(arr) ? arr : []).map(p => typeof p === 'string' ? p : (p && p.url) || '').filter(Boolean);

// Shared zoom lightbox — one implementation reused by RecordDetail and Evaluation.
const PhotoLightbox = ({ urls, i, onClose, onIndex }) => {
  if (!urls || !urls.length) return null;
  const idx = ((i % urls.length) + urls.length) % urls.length;
  return (
    <div className="absolute inset-0 z-50 bg-black/90 flex flex-col items-center justify-center" onClick={onClose}>
      <img src={urls[idx]} alt="" className="max-h-[80%] max-w-[90%] object-contain rounded" onClick={e => e.stopPropagation()} />
      <div className="flex items-center gap-4 mt-3 text-white" onClick={e => e.stopPropagation()}>
        <button onClick={() => onIndex((idx - 1 + urls.length) % urls.length)} className="px-3 py-1.5 bg-gray-800 rounded font-bold">‹ Prev</button>
        <span className="text-sm font-bold">{idx + 1} / {urls.length}</span>
        <button onClick={() => onIndex((idx + 1) % urls.length)} className="px-3 py-1.5 bg-gray-800 rounded font-bold">Next ›</button>
        <button onClick={onClose} className="px-3 py-1.5 bg-gray-700 rounded font-bold">Close ✕</button>
      </div>
    </div>
  );
};

// ── SHARED RECORD DETAIL VIEW ──────────────────────────────────────────────────────
// One detail/edit view used BOTH by the Market (click a card) and the 3D Network (click
// a node). Shows photos prominently (the user judges the construction phase from them),
// shows all the record's data, and makes every meaningful field editable incl. the
// canonical phase + status. Saving stamps phaseSystem:'v2' (clears the "needs review"
// flag) and merges into projects/businesses (never duplicates → dedup-safe).
const RecordDetail = ({ kind, record, photos = [], relationships = [], projById = {}, bizById = {}, onClose, onSaved, onOpenRecord, actorName }) => {
  const isLead = kind === 'lead';
  const isProject = kind === 'project';
  const collName = isLead ? 'leads' : isProject ? 'projects' : 'businesses';
  const [d, setD] = useState(() => ({ ...record }));
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [lightbox, setLightbox] = useState(null); // { i }
  const set = (k, v) => setD(p => ({ ...p, [k]: v }));
  const setEn = (k, v) => setD(p => ({ ...p, enrichmentData: { ...(p.enrichmentData || {}), [k]: v } }));
  const setCity = (k, v) => setD(p => ({ ...p, cityData: { ...(p.cityData || {}), [k]: v } }));
  const ic = "w-full bg-gray-950 text-white border border-gray-700 rounded px-2 py-1.5 text-sm outline-none focus:ring-1 focus:ring-orange-500";
  const lbl = "text-[10px] font-bold text-gray-500 uppercase mb-1";
  const pics = photoUrls(photos);
  const needsReview = !isCanonicalPhased(d);
  const phaseUnknown = !Object.prototype.hasOwnProperty.call(PHASE_LABEL, d.phase);

  // Leads carry active/stopped in `siteStatus`; projects/businesses in `status`.
  const statusVal = isLead ? (d.siteStatus || 'active') : (d.status || 'active');
  const setStatusVal = (v) => set(isLead ? 'siteStatus' : 'status', v);
  const showAddress = isProject || isLead;

  // Social links + nationality (business only).
  const nat = useNationalities();
  const [newNat, setNewNat] = useState('');
  const setSocial = (k, v) => setD(p => ({ ...p, socials: { ...(p.socials || {}), [k]: v } }));
  const natOptions = ['Unknown', ...nat.options.filter(o => o.toLowerCase() !== 'unknown')];
  const addNationality = async () => { const v = newNat.trim(); if (!v) return; await nat.add(v); set('nationality', v); setNewNat(''); };

  // Gatekeepers / contacts (business only) — editable list.
  const gks = d.gatekeepers || [];
  const setGk = (i, k, v) => setD(p => ({ ...p, gatekeepers: (p.gatekeepers || []).map((g, idx) => idx === i ? { ...g, [k]: v } : g) }));
  const addGk = () => setD(p => ({ ...p, gatekeepers: [...(p.gatekeepers || []), { name: '', role: '', phone: '', email: '' }] }));
  const rmGk = (i) => setD(p => ({ ...p, gatekeepers: (p.gatekeepers || []).filter((_, idx) => idx !== i) }));

  // Lead demand (single builder/owner) + supply (subcontractors) — editable for leads.
  const setDemand = (k, v) => setD(p => ({ ...p, demand: { ...(p.demand || {}), [k]: v } }));
  const setDemandContact = (i, k, v) => setD(p => ({ ...p, demand: { ...(p.demand || {}), contacts: ((p.demand || {}).contacts || []).map((c, idx) => idx === i ? { ...c, [k]: v } : c) } }));
  const addDemandContact = () => setD(p => ({ ...p, demand: { ...(p.demand || {}), contacts: [...(((p.demand || {}).contacts) || []), { name: '', role: '', phone: '', email: '' }] } }));
  const setSupply = (i, k, v) => setD(p => ({ ...p, supply: (p.supply || []).map((b, idx) => idx === i ? { ...b, [k]: v } : b) }));
  const addSupply = () => setD(p => ({ ...p, supply: [...(p.supply || []), { businessName: '', trade: '', phone: '' }] }));
  const rmSupply = (i) => setD(p => ({ ...p, supply: (p.supply || []).filter((_, idx) => idx !== i) }));
  const demand = d.demand || {};

  // Relationship context — keep the partner business id so the row is clickable.
  const partners = !isProject ? relationships.filter(r => r.a === record.id || r.b === record.id).map(r => {
    const otherId = r.a === record.id ? r.b : r.a;
    return { id: otherId, biz: bizById[otherId], name: (bizById[otherId] && bizById[otherId].businessName) || r.aName || r.bName || otherId, weight: r.weight || 1 };
  }) : [];
  // Standard city/permit fields shown as inputs; any extra permit keys rendered read-only.
  const cityExtra = Object.keys(d.cityData || {}).filter(k => !['permit', 'value', 'status', '_source'].includes(k) && d.cityData[k] !== '' && d.cityData[k] != null);
  const openBiz = (b) => { if (b && onOpenRecord) onOpenRecord('business', b); };
  const openProj = (p) => { if (p && onOpenRecord) onOpenRecord('project', p); };
  const jobsites = !isProject ? (d.jobsites || []) : [];
  const projBiz = isProject ? [
    ...((record.demandBusinessIds || []).map(id => ['demand', bizById[id]])),
    ...((record.supplyBusinessIds || []).map(id => ['supply', bizById[id]])),
  ].filter(([, b]) => b) : [];
  const en = d.enrichmentData || {};
  const city = d.cityData || {};

  const save = async () => {
    setSaving(true);
    try {
      // Notes + city/permit are editable for both kinds.
      const base = {
        phase: d.phase || 'p0', status: d.status === 'stopped' ? 'stopped' : 'active',
        phaseSystem: CANONICAL_PHASE_VERSION, isProspect: !!d.isProspect,
        enrichmentData: d.enrichmentData || {}, cityData: d.cityData || {},
        updatedAt: serverTimestamp(),
      };
      if (isLead) {
        // Collector fixing their own pre-approval submission. Keep workflow `status` as-is
        // (don't override a manager). Record attribution + an update event (economics).
        const who = actorName || d.collector_name || d.submittedBy || null;
        await setDoc(doc(db, 'leads', record.id), {
          phase: d.phase || 'p0', siteStatus: d.siteStatus === 'stopped' ? 'stopped' : 'active',
          phaseSystem: CANONICAL_PHASE_VERSION, isProspect: !!d.isProspect,
          address: d.address || '', enrichmentData: d.enrichmentData || {}, cityData: d.cityData || {},
          demand: d.demand || {}, supply: d.supply || [],
          updatedBy: who, updatedByUid: (auth.currentUser && auth.currentUser.uid) || null,
          lastUpdatedAt: serverTimestamp(), updatedAt: serverTimestamp(),
        }, { merge: true });
        await addDoc(collection(db, 'lead_update_events'), { leadId: record.id, collectorName: who, collectorUid: (auth.currentUser && auth.currentUser.uid) || null, at: serverTimestamp() });
      } else if (isProject) {
        await setDoc(doc(db, 'projects', record.id), { ...base, address: d.address || '' }, { merge: true });
      } else {
        // Recompute the dedup keys from the edited name/website so matching stays correct.
        await setDoc(doc(db, 'businesses', record.id), {
          ...base,
          businessName: d.businessName || '', normalizedName: normName(d.businessName),
          side: d.side || 'supply', trade: d.trade || '',
          phone: d.phone || '', website: d.website || '', websiteDomain: normDomain(d.website),
          officeLocation: d.officeLocation || '',
          gatekeepers: d.gatekeepers || [],
          socials: { ...blankSocials(), ...(d.socials || {}) }, nationality: d.nationality || 'Unknown',
        }, { merge: true });
      }
      onSaved && onSaved();
      onClose();
    } catch (e) { console.error('Detail save failed:', e); alert('Save failed: ' + e.message); }
    finally { setSaving(false); }
  };

  // Delete the record doc. Relationship edges are intentionally left in place — the
  // Market card list and the 3D graph both filter to existing nodes, so a dangling edge
  // is harmless (it simply isn't drawn). No crash, no orphan-cleanup pass needed.
  const del = async () => {
    setDeleting(true);
    try {
      await deleteDoc(doc(db, collName, record.id));
      onClose();
    } catch (e) { console.error('Delete failed:', e); alert('Delete failed: ' + e.message); }
    finally { setDeleting(false); }
  };

  return (
    <div className="absolute inset-0 z-30 bg-black/70 flex items-stretch justify-center p-2 md:p-6" onClick={() => !saving && onClose()}>
      <div className="bg-gray-900 border border-gray-700 rounded-xl w-full max-w-5xl flex flex-col overflow-hidden" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="shrink-0 flex items-center justify-between gap-2 p-3 border-b border-gray-800">
          <div className="min-w-0 flex items-center gap-2">
            <span className={`inline-block w-2.5 h-2.5 rounded-full ${showAddress ? 'bg-orange-400' : (d.side === 'demand' ? 'bg-blue-400' : 'bg-amber-400')}`} />
            <span className="text-white font-bold text-sm truncate">{showAddress ? (d.address || 'No address') : (d.businessName || '(unnamed)')}</span>
            <span className="text-[9px] font-bold text-gray-400 border border-gray-700 rounded px-1.5 py-0.5 uppercase">{isLead ? 'Lead' : isProject ? 'Project' : 'Business'}</span>
            {needsReview && <span className="text-[9px] font-bold text-amber-300 border border-amber-500/40 rounded px-1.5 py-0.5">⚠ phase needs review</span>}
          </div>
          <button onClick={() => !saving && onClose()} className="text-gray-400 hover:text-white shrink-0"><X size={18} /></button>
        </div>

        {/* Body */}
        <div className="flex-1 min-h-0 overflow-y-auto md:overflow-hidden md:flex">
          {/* Photos */}
          <div className="md:w-1/2 md:overflow-y-auto p-3 border-b md:border-b-0 md:border-r border-gray-800">
            <div className={lbl}>Photos — inspect to judge the phase ({pics.length})</div>
            {pics.length === 0 ? (
              <div className="text-gray-500 text-sm bg-gray-800 border border-gray-700 rounded-lg p-6 text-center">No photos on this record — phase can't be judged visually.</div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {pics.map((u, i) => <img key={i} src={u} alt="" onClick={() => setLightbox({ i })} className="w-full h-28 object-cover rounded-lg border border-gray-700 cursor-pointer hover:opacity-80" />)}
              </div>
            )}
          </div>

          {/* Editable fields + read-only context */}
          <div className="md:w-1/2 md:overflow-y-auto p-3 space-y-3">
            {/* PHASE + STATUS (always first — the priority) */}
            <div className="bg-gray-800 border border-gray-700 rounded-lg p-3 space-y-2">
              <div>
                <div className={lbl}>Phase (canonical)</div>
                <select value={phaseUnknown ? '' : d.phase} onChange={e => set('phase', e.target.value)} className={ic}>
                  {phaseUnknown && <option value="">⚠ {d.phase || 'unset'} — pick a phase</option>}
                  {PHASES.map(([v, t]) => <option key={v} value={v}>{v.toUpperCase()} · {t}</option>)}
                </select>
              </div>
              <div>
                <div className={lbl}>Site status</div>
                <div className="grid grid-cols-2 gap-2">
                  <button onClick={() => setStatusVal('active')} className={`py-2 rounded-lg text-xs font-bold border ${statusVal !== 'stopped' ? 'bg-blue-500/20 border-blue-500/50 text-blue-200' : 'bg-gray-900 border-gray-700 text-gray-400'}`}>Active</button>
                  <button onClick={() => setStatusVal('stopped')} className={`py-2 rounded-lg text-xs font-bold border flex items-center justify-center gap-1 ${statusVal === 'stopped' ? 'bg-red-900/50 border-red-500/50 text-red-200' : 'bg-gray-900 border-gray-700 text-gray-400'}`}><Ban size={12} /> Stopped</button>
                </div>
              </div>
              <button onClick={() => set('isProspect', !d.isProspect)} className={`w-full py-2 rounded-lg text-xs font-bold border flex items-center justify-center gap-1 ${d.isProspect ? 'bg-yellow-500/20 border-yellow-500/50 text-yellow-200' : 'bg-gray-900 border-gray-700 text-gray-400'}`}><Star size={12} /> {d.isProspect ? 'Prospect ✓' : 'Mark as prospect'}</button>
            </div>

            {/* Identity / contact fields */}
            {showAddress ? (
              <div>
                <div className={lbl}>Address</div>
                <input className={ic} value={d.address || ''} onChange={e => set('address', e.target.value)} />
                <div className="text-[11px] text-gray-500 mt-1">Zone: {d.zoneName || '—'}</div>
              </div>
            ) : (
              <div className="space-y-2">
                <div><div className={lbl}>Business name</div><input className={ic} value={d.businessName || ''} onChange={e => set('businessName', e.target.value)} /></div>
                <div className="grid grid-cols-2 gap-2">
                  <div><div className={lbl}>Side</div><select className={ic} value={d.side || 'supply'} onChange={e => set('side', e.target.value)}><option value="demand">Demand</option><option value="supply">Supply</option></select></div>
                  <div><div className={lbl}>Trade / role</div><input className={ic} value={d.trade || ''} onChange={e => set('trade', e.target.value)} /></div>
                </div>
                <div><div className={lbl}>Phone</div><input className={ic} value={d.phone || ''} onChange={e => set('phone', e.target.value)} /></div>
                <div><div className={lbl}>Website</div><input className={ic} value={d.website || ''} onChange={e => set('website', e.target.value)} /></div>
                <div><div className={lbl}>Office location</div><input className={ic} value={d.officeLocation || ''} onChange={e => set('officeLocation', e.target.value)} /></div>
                {splitRoleCodes(d.roleCodes).length > 0 && <div className="flex flex-wrap gap-1">{splitRoleCodes(d.roleCodes).map((c, i) => <span key={i} className="font-mono text-[10px] text-gray-300 bg-gray-700 rounded px-1.5 py-0.5">{c}</span>)}</div>}
                {/* Nationality — manual, extensible (default Unknown). NOT AI-inferred. */}
                <div>
                  <div className={lbl}>Nationality <span className="text-gray-600 normal-case">(manual)</span></div>
                  <div className="flex gap-2">
                    <select className={ic} value={natOptions.includes(d.nationality) ? d.nationality : 'Unknown'} onChange={e => set('nationality', e.target.value)}>{natOptions.map(o => <option key={o} value={o}>{o}</option>)}</select>
                  </div>
                  <div className="flex gap-1.5 mt-1">
                    <input className={ic} placeholder="+ Add a nationality…" value={newNat} onChange={e => setNewNat(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addNationality(); } }} />
                    <button onClick={addNationality} disabled={!newNat.trim()} className="shrink-0 px-2.5 rounded bg-gray-700 hover:bg-gray-600 text-white text-xs font-bold disabled:opacity-40">Add</button>
                  </div>
                </div>
                {/* Social links — editable; tappable chips shown when set. */}
                <div className="bg-gray-800 border border-gray-700 rounded-lg p-2">
                  <div className="flex items-center justify-between mb-1"><span className={lbl + ' mb-0'}>Social links</span><SocialChips socials={d.socials} /></div>
                  <div className="grid grid-cols-2 gap-1.5">
                    {SOCIAL_FIELDS.map(([k, lblS]) => <input key={k} className={ic} placeholder={lblS} value={(d.socials || {})[k] || ''} onChange={e => setSocial(k, e.target.value)} />)}
                  </div>
                </div>
              </div>
            )}

            {/* Lead: demand (builder/owner) + supply (subs) — collector's own submission */}
            {isLead && (
              <div className="bg-gray-800 border border-gray-700 rounded-lg p-2 space-y-2">
                <div className={lbl + ' mb-0'}>Demand (builder / owner)</div>
                <input className={ic} placeholder="Business / builder name" value={demand.businessName || ''} onChange={e => setDemand('businessName', e.target.value)} />
                <div className="grid grid-cols-2 gap-1.5">
                  <input className={ic} placeholder="Website" value={demand.website || ''} onChange={e => setDemand('website', e.target.value)} />
                  <input className={ic} placeholder="Office location" value={demand.officeLocation || ''} onChange={e => setDemand('officeLocation', e.target.value)} />
                </div>
                <div className="flex items-center justify-between"><span className="text-[10px] font-bold text-gray-500 uppercase">Contacts ({(demand.contacts || []).length})</span><button onClick={addDemandContact} className="text-[11px] font-bold text-blue-400 hover:text-blue-300">+ Add</button></div>
                {(demand.contacts || []).map((c, i) => (
                  <div key={i} className="grid grid-cols-2 gap-1.5">
                    <input className={ic} placeholder="Name" value={c.name || ''} onChange={e => setDemandContact(i, 'name', e.target.value)} />
                    <input className={ic} placeholder="Role" value={c.role || ''} onChange={e => setDemandContact(i, 'role', e.target.value)} />
                    <input className={ic} placeholder="Phone" value={c.phone || ''} onChange={e => setDemandContact(i, 'phone', e.target.value)} />
                    <input className={ic} placeholder="Email" value={c.email || ''} onChange={e => setDemandContact(i, 'email', e.target.value)} />
                  </div>
                ))}
                <div className="flex items-center justify-between pt-1"><span className={lbl + ' mb-0'}>Supply (subcontractors, {(d.supply || []).length})</span><button onClick={addSupply} className="text-[11px] font-bold text-blue-400 hover:text-blue-300">+ Add</button></div>
                {(d.supply || []).map((b, i) => (
                  <div key={i} className="grid grid-cols-[1fr_1fr_1fr_auto] gap-1.5 items-center">
                    <input className={ic} placeholder="Name" value={b.businessName || b.company || ''} onChange={e => setSupply(i, 'businessName', e.target.value)} />
                    <input className={ic} placeholder="Trade" value={b.trade || ''} onChange={e => setSupply(i, 'trade', e.target.value)} />
                    <input className={ic} placeholder="Phone" value={b.phone || ''} onChange={e => setSupply(i, 'phone', e.target.value)} />
                    <button onClick={() => rmSupply(i)} className="text-gray-500 hover:text-red-400"><X size={14} /></button>
                  </div>
                ))}
              </div>
            )}

            {/* Contacts / gatekeepers (business) */}
            {!isProject && !isLead && (
              <div className="bg-gray-800 border border-gray-700 rounded-lg p-2">
                <div className="flex items-center justify-between mb-1"><span className={lbl + ' mb-0'}>Contacts / gatekeepers ({gks.length})</span><button onClick={addGk} className="text-[11px] font-bold text-blue-400 hover:text-blue-300">+ Add</button></div>
                {gks.length === 0 && <p className="text-[11px] text-gray-600">No contacts.</p>}
                {gks.map((g, i) => (
                  <div key={i} className="grid grid-cols-2 gap-1.5 mb-1.5 border-b border-gray-800 pb-1.5">
                    <input className={ic} placeholder="Name" value={g.name || ''} onChange={e => setGk(i, 'name', e.target.value)} />
                    <input className={ic} placeholder="Role" value={g.role || ''} onChange={e => setGk(i, 'role', e.target.value)} />
                    <input className={ic} placeholder="Phone" value={g.phone || ''} onChange={e => setGk(i, 'phone', e.target.value)} />
                    <input className={ic} placeholder="Email" value={g.email || ''} onChange={e => setGk(i, 'email', e.target.value)} />
                    <button onClick={() => rmGk(i)} className="col-span-2 text-[10px] font-bold text-red-400 hover:text-red-300 text-right">Remove</button>
                  </div>
                ))}
              </div>
            )}

            {/* Notes — editable free text (field note / transcript / deficiency) */}
            <div className="bg-gray-800 border border-gray-700 rounded-lg p-3 space-y-2">
              <div><div className={lbl}>Field note / comment</div><textarea rows={3} className={ic} placeholder="Free-text note from collection / enrichment…" value={en.initialComment || ''} onChange={e => setEn('initialComment', e.target.value)} /></div>
              <div><div className={lbl}>Voice transcript</div><textarea rows={2} className={ic} placeholder="Transcript (editable)…" value={en.transcript || ''} onChange={e => setEn('transcript', e.target.value)} /></div>
              <div><div className={lbl}>Deficiency / flags</div><textarea rows={2} className={ic} placeholder="Deficiency notes / flags…" value={en.deficiencyNotes || ''} onChange={e => setEn('deficiencyNotes', e.target.value)} /></div>
            </div>

            {/* City / permit data — editable standard fields + any extra permit fields read-only.
                (Toronto redacts contractor/builder names, so those won't appear.) */}
            <div className="bg-gray-800 border border-gray-700 rounded-lg p-3 space-y-2">
              <div className={lbl}>City / permit data</div>
              <div className="grid grid-cols-3 gap-2">
                <div><div className="text-[9px] text-gray-500 mb-0.5">Permit #</div><input className={ic} value={city.permit || ''} onChange={e => setCity('permit', e.target.value)} /></div>
                <div><div className="text-[9px] text-gray-500 mb-0.5">Value</div><input className={ic} value={city.value || ''} onChange={e => setCity('value', e.target.value)} /></div>
                <div><div className="text-[9px] text-gray-500 mb-0.5">Type / status</div><input className={ic} value={city.status || ''} onChange={e => setCity('status', e.target.value)} /></div>
              </div>
              {cityExtra.length > 0 && (
                <div className="grid grid-cols-2 gap-1.5 pt-1">
                  {cityExtra.map(k => <div key={k} className="bg-gray-900 rounded px-2 py-1"><div className="text-[9px] text-gray-500 uppercase truncate">{k}</div><div className="text-[11px] text-gray-200 break-words">{typeof city[k] === 'object' ? JSON.stringify(city[k]) : String(city[k])}</div></div>)}
                </div>
              )}
              {city._source && <div className="text-[9px] text-gray-600">Source: {city._source}</div>}
            </div>

            {/* Read-only context — ratings, links (clickable to hop records), dates */}
            <div className="bg-gray-800 border border-gray-700 rounded-lg p-3 text-[11px] text-gray-400 space-y-2">
              {/* External Google rating — kept strictly separate from any internal rating */}
              {(d.googleRating || d.googleReviewCount || (d.rating !== undefined && d.rating !== '')) && (
                <div className="flex flex-wrap gap-3">
                  {(d.googleRating || d.googleReviewCount) && <span>🌐 Google (external): <b className="text-gray-200">{d.googleRating || '—'}</b>{d.googleReviewCount ? ` (${d.googleReviewCount})` : ''}</span>}
                  {d.rating !== undefined && d.rating !== '' && <span>⭐ Internal: <b className="text-gray-200">{d.rating}</b></span>}
                </div>
              )}
              {isProject && projBiz.length > 0 && (
                <div><div className={lbl}>Businesses on this project</div>{projBiz.map(([side, b], i) => <button key={i} onClick={() => openBiz(b)} className="w-full text-left truncate hover:text-white"><span className={`inline-block w-2 h-2 rounded-full mr-1.5 ${side === 'demand' ? 'bg-blue-400' : 'bg-amber-400'}`} />{b.businessName || '(unnamed)'} <span className="text-gray-600">· {side} ›</span></button>)}</div>
              )}
              {!isProject && jobsites.length > 0 && (
                <div><div className={lbl}>Jobsites ({jobsites.length})</div>{jobsites.map((j, i) => { const pr = projById[j.projectId]; return <button key={i} onClick={() => openProj(pr)} disabled={!pr} className="w-full text-left truncate hover:text-white disabled:hover:text-gray-400">📍 {(pr && pr.address) || j.address || j.projectId} <span className="text-gray-600">· {j.phase || '—'}{pr ? ' ›' : ''}</span></button>; })}</div>
              )}
              {!isProject && partners.length > 0 && (
                <div><div className={lbl}>Connected businesses ({partners.length})</div>{partners.map((p, i) => <button key={i} onClick={() => openBiz(p.biz)} disabled={!p.biz} className="w-full text-left truncate hover:text-white disabled:hover:text-gray-400">🔗 {p.name} <span className="text-gray-600">· {p.weight} shared{p.biz ? ' ›' : ''}</span></button>)}</div>
              )}
              {d.possibleDuplicate && <div className="text-amber-400">⚠ flagged possible duplicate</div>}
              <div className="text-gray-600">{d.createdAt ? `Created ${fmtDate(d.createdAt)}` : ''}{d.updatedAt ? ` · Updated ${fmtDate(d.updatedAt)}` : ''}</div>
            </div>
          </div>
        </div>

        {/* Footer */}
        {confirmDelete ? (
          <div className="shrink-0 border-t border-red-800/50 bg-red-950/30 p-3 flex items-center gap-2">
            <span className="text-xs font-bold text-red-200">Delete this {isProject ? 'project' : 'business'}? This can't be undone.</span>
            <button onClick={() => setConfirmDelete(false)} disabled={deleting} className="ml-auto px-3 py-2 rounded-lg text-xs font-bold text-gray-300 bg-gray-800 hover:bg-gray-700 disabled:opacity-50">Cancel</button>
            <button onClick={del} disabled={deleting} className="px-4 py-2 rounded-lg font-bold bg-red-600 hover:bg-red-700 text-white text-sm disabled:opacity-50 flex items-center justify-center">
              {deleting ? <><Loader2 size={15} className="animate-spin mr-1.5" /> Deleting…</> : <><Trash2 size={14} className="mr-1.5" /> Delete</>}
            </button>
          </div>
        ) : (
          <div className="shrink-0 border-t border-gray-800 p-3 flex items-center gap-2">
            <button onClick={() => setConfirmDelete(true)} disabled={saving} className="px-3 py-2 rounded-lg text-xs font-bold text-red-300 bg-gray-800 hover:bg-red-900/40 border border-red-500/30 flex items-center gap-1"><Trash2 size={13} /> Delete</button>
            <button onClick={() => !saving && onClose()} className="px-3 py-2 rounded-lg text-xs font-bold text-gray-300 bg-gray-800 hover:bg-gray-700">Cancel</button>
            <button onClick={save} disabled={saving || phaseUnknown} title={phaseUnknown ? 'Pick a canonical phase first' : ''} className="ml-auto px-4 py-2 rounded-lg font-bold bg-orange-600 hover:bg-orange-700 text-white text-sm disabled:opacity-50 flex items-center justify-center">
              {saving ? <><Loader2 size={15} className="animate-spin mr-1.5" /> Saving…</> : <><Save size={14} className="mr-1.5" /> Save → Market</>}
            </button>
          </div>
        )}
      </div>

      {/* Shared zoom lightbox — sits above the detail panel. */}
      {lightbox && pics.length > 0 && (
        <PhotoLightbox urls={pics} i={lightbox.i} onClose={() => setLightbox(null)} onIndex={(n) => setLightbox({ i: n })} />
      )}
    </div>
  );
};

// Re-frames the map: to the focused zone (block polygon/center) if set, else to fit all pins.
const MarketMapCamera = ({ focusBlock, pts, sig }) => {
  const map = useMap();
  useEffect(() => {
    if (!map || !window.google) return;
    if (focusBlock) {
      const poly = (focusBlock.block && focusBlock.block.length) ? focusBlock.block : null;
      if (poly) { const b = new window.google.maps.LatLngBounds(); poly.forEach(p => b.extend(p)); map.fitBounds(b, 60); return; }
      if (focusBlock.center) { map.panTo(focusBlock.center); map.setZoom(15); return; }
    }
    if (pts && pts.length) { const b = new window.google.maps.LatLngBounds(); pts.forEach(p => b.extend(p)); map.fitBounds(b, 60); if (pts.length === 1) map.setZoom(16); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, sig]);
  return null;
};
// Draw a block's outline on the map (purple, non-clickable).
const BlockOutline = ({ pts }) => {
  const map = useMap();
  useEffect(() => {
    if (!map || !window.google || !pts || !pts.length) return;
    const poly = new window.google.maps.Polygon({ paths: pts, clickable: false, strokeColor: '#a855f7', strokeOpacity: 0.9, strokeWeight: 2, fillColor: '#a855f7', fillOpacity: 0.08 });
    poly.setMap(map);
    return () => poly.setMap(null);
  }, [map, pts]);
  return null;
};

// ── REUSABLE Market map view ───────────────────────────────────────────────────────
// Plots the SAME filtered set the list shows. `bizCoordOf(b)` resolves a business to a
// coordinate (e.g. via its jobsites' project coords); businesses without one are skipped.
// ── Site-command pins: color = status, number = phase (map view only; no data changes) ──
// Three status buckets → three accessible traffic-light colors. Maps the EXISTING status
// fields (no recompute): Stopped site OR "other" outcome → Stopped/Others; needs-eval
// outcome → Evaluation; everything else → Active.
const SITE_STATUS = {
  active:  { label: 'Active',           color: '#16a34a' }, // green
  eval:    { label: 'Evaluation',       color: '#f59e0b' }, // amber
  stopped: { label: 'Stopped / Others', color: '#dc2626' }, // red
};
const siteBucket = (p) => (p && (p.status === 'stopped' || p.outcome === 'other')) ? 'stopped'
  : (p && p.outcome === 'needs_eval') ? 'eval' : 'active';
// Phase number for the pin: p0..p6 → "0".."6", px → "X"; anything else (unset/unknown)
// → "" so a site with no real phase shows NO number rather than a wrong default.
const phasePinLabel = (phase) => {
  const s = String(phase || '').toLowerCase();
  if (s === 'px') return 'X';
  const m = s.match(/^p([0-6])$/);
  return m ? m[1] : '';
};

const MarketMapView = ({ projects, businesses, bizCoordOf, plot, onPlotChange, focusBlock, onPick, onOpenDetail, hideToggle, projLabel }) => {
  const [sel, setSel] = useState(null);
  const showProj = plot === 'projects' || plot === 'both';
  const showBiz = plot === 'businesses' || plot === 'both';
  const projMarkers = showProj ? projects.filter(p => p.coords).map(p => ({ type: 'project', id: p.id, pos: p.coords, item: p })) : [];
  const projSkipped = showProj ? projects.filter(p => !p.coords).length : 0;
  const bizPairs = showBiz ? businesses.map(b => ({ b, pos: bizCoordOf(b) })) : [];
  const bizMarkers = bizPairs.filter(x => x.pos).map(x => ({ type: 'business', id: x.b.id, pos: x.pos, item: x.b }));
  const bizSkipped = bizPairs.length - bizMarkers.length;
  const markers = [...projMarkers, ...bizMarkers];
  const pts = markers.map(m => m.pos);
  const sig = `${plot}|${focusBlock ? focusBlock.id : ''}|${markers.length}|${pts[0] ? pts[0].lat.toFixed(3) + ',' + pts[0].lng.toFixed(3) : ''}`;
  const tBtn = (k, label) => <button onClick={() => onPlotChange(k)} className={`px-2.5 py-1 rounded text-[11px] font-bold border ${plot === k ? 'bg-white text-gray-900 border-white' : 'bg-gray-800 text-gray-300 border-gray-700'}`}>{label}</button>;
  return (
    <div className="absolute inset-0 flex flex-col">
      <div className="shrink-0 flex items-center gap-2 px-3 py-2 border-b border-gray-800 flex-wrap">
        {!hideToggle && <><span className="text-[10px] font-bold text-gray-500 uppercase">Plot:</span>{tBtn('projects', 'Sites / Leads')}{tBtn('businesses', 'Businesses')}{tBtn('both', 'Both')}</>}
        {showProj && (
          <div className="flex items-center gap-2.5 flex-wrap text-[10px] font-bold">
            {Object.values(SITE_STATUS).map(s => <span key={s.label} className="inline-flex items-center gap-1 text-gray-400"><span className="w-2.5 h-2.5 rounded-full border border-white/40" style={{ backgroundColor: s.color }} />{s.label}</span>)}
            <span className="text-gray-600">· number = phase (0–6 / X)</span>
          </div>
        )}
        <span className="text-[11px] text-gray-500 ml-auto">{markers.length} pin{markers.length === 1 ? '' : 's'}{(projSkipped + bizSkipped) > 0 ? ` · ${projSkipped + bizSkipped} skipped (no coords)` : ''}</span>
      </div>
      <div className="flex-1 relative min-h-0">
        <APIProvider apiKey={GOOGLE_MAPS_API_KEY}>
          <Map defaultCenter={{ lat: 43.766, lng: -79.41 }} defaultZoom={12} mapId="blue_jays_admin_map" gestureHandling="greedy" disableDefaultUI={false}>
            <MarketMapCamera focusBlock={focusBlock} pts={pts} sig={sig} />
            {focusBlock && focusBlock.block && focusBlock.block.length > 0 && <BlockOutline pts={focusBlock.block} />}
            {markers.map(m => (
              <AdvancedMarker key={m.type + m.id} position={m.pos} onClick={() => setSel(m)}>
                {m.type === 'project'
                  ? (() => { const bk = siteBucket(m.item); const ph = phasePinLabel(m.item.phase); return (
                      <div style={{ backgroundColor: SITE_STATUS[bk].color }} title={`${SITE_STATUS[bk].label}${ph ? ' · Phase ' + ph : ' · no phase'}`} className="w-7 h-7 rounded-full shadow-lg border-2 border-white flex items-center justify-center text-white text-[12px] font-black leading-none">
                        {ph ? ph : <MapPin size={14} />}
                      </div>
                    ); })()
                  : <div className={`w-4 h-4 rounded-full border-2 border-white shadow-lg ${m.item.side === 'demand' ? 'bg-blue-500' : 'bg-amber-500'}`} />}
              </AdvancedMarker>
            ))}
          </Map>
        </APIProvider>
        {markers.length === 0 && <div className="absolute inset-0 flex items-center justify-center text-gray-500 text-sm pointer-events-none">No mappable items for these filters.</div>}
        {sel && (
          <div className="absolute left-3 right-3 bottom-3 bg-gray-900 border border-gray-700 rounded-xl p-3 shadow-2xl">
            <button onClick={() => setSel(null)} className="absolute top-2 right-2 text-gray-500 hover:text-white"><X size={16} /></button>
            {sel.type === 'project' ? (
              <>
                <div className="text-white font-bold text-sm truncate pr-6">{sel.item.address || 'No address'}</div>
                <div className="text-[11px] text-gray-400 mt-0.5">{projLabel || 'Site'} · {PHASE_LABEL[sel.item.phase] || sel.item.phase || '—'} · {((sel.item.demandBusinessIds || []).length + (sel.item.supplyBusinessIds || []).length)} business(es) · {(sel.item.photos || []).length} photo(s)</div>
              </>
            ) : (
              <>
                <div className="text-white font-bold text-sm truncate pr-6">{sel.item.businessName || '(unnamed)'}</div>
                <div className="text-[11px] text-gray-400 mt-0.5 capitalize">{sel.item.side}{sel.item.trade ? ` · ${sel.item.trade}` : ''}{splitRoleCodes(sel.item.roleCodes)[0] ? ` · ${splitRoleCodes(sel.item.roleCodes).join(' · ')}` : ''}</div>
                {(sel.item.phone || sel.item.website) && <div className="text-[11px] text-gray-500 mt-0.5 truncate">{[sel.item.phone, sel.item.website].filter(Boolean).join(' · ')}</div>}
              </>
            )}
            <div className="mt-2 flex gap-2">
              {onOpenDetail && <button onClick={() => { onOpenDetail(sel); setSel(null); }} className="flex-1 py-1.5 rounded bg-orange-600 hover:bg-orange-700 text-white text-[11px] font-bold">Open full card →</button>}
              {onPick && <button onClick={() => { onPick(sel); setSel(null); }} className={`py-1.5 rounded bg-gray-700 hover:bg-gray-600 text-white text-[11px] font-bold ${onOpenDetail ? 'px-3' : 'flex-1 w-full'}`}>{onOpenDetail ? 'List' : 'Open in list →'}</button>}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const ManagerMarket = ({ onBack, goBack, activeBlocks }) => {
  const [businesses, setBusinesses] = useState([]);
  const [projects, setProjects] = useState([]);
  const [tab, setTab] = useState('projects');       // 'projects' | 'businesses'
  const [mode, setMode] = useState('list');         // 'list' | 'map'
  const [plot, setPlot] = useState('projects');     // map: 'projects' | 'businesses' | 'both'
  const [filters, setFilters] = useState({ side: 'all', phase: 'all', zone: 'all', q: '' });
  const [tierF, setTierF] = useState('all');        // all | Active | Very Active | Super Active
  const [statusF, setStatusF] = useState('all');    // all | active | others | stopped | eval (projects)
  const [tradeF, setTradeF] = useState('all');      // all | <trade> (businesses)
  const [prospectF, setProspectF] = useState('all');// all | prospect
  const [natF, setNatF] = useState('all');          // all | Unknown | <nationality> (businesses)
  const [sortBiz, setSortBiz] = useState('none');   // none | rating_desc | rating_asc (Google score)
  const nat = useNationalities();
  const [tiers, setTiers] = useState(SEED_TIERS);   // live thresholds from ai_config
  const [relationships, setRelationships] = useState([]); // edges, for the detail view's "connected" list
  const [detail, setDetail] = useState(null);       // { kind, record } — open detail view, null = closed

  useEffect(() => {
    const u1 = onSnapshot(collection(db, "businesses"), s => setBusinesses(s.docs.map(d => ({ id: d.id, ...d.data() }))), e => console.error("businesses load:", e));
    const u2 = onSnapshot(collection(db, "projects"), s => setProjects(s.docs.map(d => ({ id: d.id, ...d.data() }))), e => console.error("projects load:", e));
    const u3 = onSnapshot(doc(db, "ai_config", "activity_tiers"), s => { if (s.exists()) setTiers({ ...SEED_TIERS, ...s.data() }); }, e => console.error("tiers load:", e));
    const u4 = onSnapshot(collection(db, "relationships"), s => setRelationships(s.docs.map(d => ({ id: d.id, ...d.data() }))), e => console.error("relationships load:", e));
    return () => { u1(); u2(); u3(); u4(); };
  }, []);

  const bizById = Object.fromEntries(businesses.map(b => [b.id, b]));
  const projById = Object.fromEntries(projects.map(p => [p.id, p]));
  const blockById = Object.fromEntries((activeBlocks || []).map(b => [b.id, b]));
  const empty = businesses.length === 0 && projects.length === 0;
  const ql = filters.q.trim().toLowerCase();
  const tierOf = (b) => computeTier((b.jobsites || []).length, tiers);

  // Zone options come from the block_id/zoneName each item was collected under (label
  // resolved against the drawn blocks, e.g. block 7 → "Willowdale West").
  const zoneMap = {};
  projects.forEach(p => { if (p.block_id) zoneMap[p.block_id] = (blockById[p.block_id] && blockById[p.block_id].name) || p.zoneName || p.block_id; });
  businesses.forEach(b => (b.jobsites || []).forEach(j => { if (j.block_id) zoneMap[j.block_id] = (blockById[j.block_id] && blockById[j.block_id].name) || j.zoneName || j.block_id; }));
  const zones = [{ value: 'all', label: 'All zones' }, ...Object.entries(zoneMap).map(([value, label]) => ({ value, label }))];
  // Trade/title options, dynamic from the businesses present (like the zone filter).
  const tradeOf = (b) => ((b.trade || '').trim()) || (Array.isArray(b.roleCodes) && b.roleCodes[0]) || '';
  const trades = [...new Set(businesses.map(tradeOf).filter(Boolean))].sort((a, b) => a.localeCompare(b));
  const focusBlock = filters.zone !== 'all' ? (blockById[filters.zone] || null) : null;
  // Resolve a business to a coordinate via its jobsites' project coords (else its own coords).
  const bizCoordOf = (b) => { for (const j of (b.jobsites || [])) { const pr = projById[j.projectId]; if (pr && pr.coords) return pr.coords; } return b.coords || null; };

  // Combined market status for a project: stopped / eval (needs-eval outcome) / active.
  // Stopped + Eval together form the "others" (non-active) group.
  const marketStatusOf = (p) => (p.status || 'active') === 'stopped' ? 'stopped' : (p.outcome === 'needs_eval' ? 'eval' : 'active');
  const statusMatch = (p) => statusF === 'all' || (statusF === 'others' ? marketStatusOf(p) !== 'active' : marketStatusOf(p) === statusF);

  const visBiz = businesses.filter(b =>
    (filters.side === 'all' || b.side === filters.side) &&
    (filters.phase === 'all' || (b.jobsites || []).some(j => j.phase === filters.phase)) &&
    (filters.zone === 'all' || (b.jobsites || []).some(j => j.block_id === filters.zone)) &&
    (tierF === 'all' || tierOf(b) === tierF) &&
    (tradeF === 'all' || tradeOf(b) === tradeF) &&
    (prospectF === 'all' || !!b.isProspect) &&
    (natF === 'all' || (b.nationality || 'Unknown') === natF) &&
    (!ql || (b.businessName || '').toLowerCase().includes(ql) || (b.phone || '').toLowerCase().includes(ql) || (b.website || '').toLowerCase().includes(ql) || (b.trade || '').toLowerCase().includes(ql) || (b.roleCodes || []).join(' ').includes(ql))
  ).sort((a, b) => {
    if (sortBiz === 'none') return 0;
    // Google score sort; unrated ('' / null) always sort last.
    const ra = (a.googleRating === '' || a.googleRating == null) ? null : Number(a.googleRating);
    const rb = (b.googleRating === '' || b.googleRating == null) ? null : Number(b.googleRating);
    if (ra == null && rb == null) return 0;
    if (ra == null) return 1;
    if (rb == null) return -1;
    return sortBiz === 'rating_asc' ? ra - rb : rb - ra;
  });
  const visProj = projects.filter(p =>
    (filters.phase === 'all' || p.phase === filters.phase) &&
    statusMatch(p) &&
    (prospectF === 'all' || !!p.isProspect) &&
    (filters.side === 'all' || (filters.side === 'demand' ? (p.demandBusinessIds || []).length : (p.supplyBusinessIds || []).length)) &&
    (filters.zone === 'all' || p.block_id === filters.zone) &&
    (!ql || (p.address || '').toLowerCase().includes(ql))
  );

  const dot = (side) => <span className={`inline-block w-2 h-2 rounded-full mr-1.5 ${side === 'demand' ? 'bg-blue-400' : 'bg-amber-400'}`} />;
  const sel = "bg-gray-800 text-white border border-gray-700 rounded px-2 py-1.5 text-xs outline-none";

  return (
    <div className="absolute inset-0 z-0 bg-gray-900 flex flex-col">
      <div className="flex items-center justify-between p-3 border-b border-gray-800 shrink-0">
        <div className="flex items-center"><Shield className="text-orange-500 mr-2" size={20} /><span className="font-bold tracking-widest uppercase text-white text-sm">Market · system of record</span></div>
        <div className="flex items-center gap-3 text-xs font-bold"><button onClick={onBack} className="text-gray-300 hover:text-white">← Manager</button><button onClick={goBack} className="text-gray-400 hover:text-white">Exit</button></div>
      </div>

      {empty ? (
        <div className="flex-1 flex flex-col items-center justify-center text-center px-6">
          <Globe className="text-gray-600 mb-3" size={36} />
          <div className="text-gray-300 font-bold mb-1">No injected data yet</div>
          <p className="text-gray-500 text-sm max-w-xs">Approve and <b className="text-gray-300">Inject</b> a lead in Evaluation &amp; Sorting — businesses, jobsites and relationships build up here.</p>
        </div>
      ) : (
        <>
          {/* Tabs + List/Map toggle + reusable filter bar */}
          <div className="shrink-0 px-3 pt-3">
            <div className="flex gap-2 mb-2 items-center">
              <button onClick={() => setTab('projects')} className={`px-3 py-1.5 rounded-lg text-xs font-bold border ${tab === 'projects' ? 'bg-white text-gray-900 border-white' : 'bg-gray-800 text-gray-300 border-gray-700'}`}>📁 Projects <span className="opacity-60">{projects.length}</span></button>
              <button onClick={() => setTab('businesses')} className={`px-3 py-1.5 rounded-lg text-xs font-bold border ${tab === 'businesses' ? 'bg-white text-gray-900 border-white' : 'bg-gray-800 text-gray-300 border-gray-700'}`}>🏢 Businesses <span className="opacity-60">{businesses.length}</span></button>
              <div className="ml-auto flex rounded-lg border border-gray-700 overflow-hidden">
                <button onClick={() => setMode('list')} className={`px-3 py-1.5 text-xs font-bold ${mode === 'list' ? 'bg-white text-gray-900' : 'bg-gray-800 text-gray-300'}`}>List</button>
                <button onClick={() => setMode('map')} className={`px-3 py-1.5 text-xs font-bold ${mode === 'map' ? 'bg-white text-gray-900' : 'bg-gray-800 text-gray-300'}`}>Map</button>
              </div>
            </div>
            <div className="mb-3">
              <MarketFilterBar value={filters} onChange={setFilters} zones={zones} resultLabel={`${visBiz.length} businesses · ${visProj.length} projects`}>
                {tab === 'projects' && <select value={statusF} onChange={e => setStatusF(e.target.value)} className={sel}><option value="all">All statuses</option><option value="active">Active</option><option value="others">Others (non-active)</option><option value="stopped">⛔ Stopped</option><option value="eval">🔎 Eval (needs-eval)</option></select>}
                {tab === 'businesses' && <select value={tradeF} onChange={e => setTradeF(e.target.value)} className={sel}><option value="all">All trades</option>{trades.map(t => <option key={t} value={t}>{t}</option>)}</select>}
                {tab === 'businesses' && <select value={tierF} onChange={e => setTierF(e.target.value)} className={sel}><option value="all">All tiers</option><option value="Active">Active</option><option value="Very Active">Very Active</option><option value="Super Active">Super Active (ON AIR Marketing)</option></select>}
                {tab === 'businesses' && <select value={sortBiz} onChange={e => setSortBiz(e.target.value)} className={sel}><option value="none">Sort: default</option><option value="rating_desc">★ Google high→low</option><option value="rating_asc">★ Google low→high</option></select>}
                {tab === 'businesses' && <select value={natF} onChange={e => setNatF(e.target.value)} className={sel}><option value="all">All nationalities</option>{['Unknown', ...nat.options.filter(o => o.toLowerCase() !== 'unknown')].map(o => <option key={o} value={o}>{o}</option>)}</select>}
                <select value={prospectF} onChange={e => setProspectF(e.target.value)} className={sel}><option value="all">All records</option><option value="prospect">⭐ Prospects only</option></select>
              </MarketFilterBar>
            </div>
          </div>

          {mode === 'map' ? (
            <div className="flex-1 relative min-h-0">
              <MarketMapView projects={visProj} businesses={visBiz} bizCoordOf={bizCoordOf} plot={plot} onPlotChange={setPlot} focusBlock={focusBlock} onOpenDetail={(m) => setDetail({ kind: m.type === 'business' ? 'business' : 'project', record: m.item })} onPick={(m) => { setMode('list'); setTab(m.type === 'business' ? 'businesses' : 'projects'); }} />
            </div>
          ) : (
          <div className="flex-1 overflow-y-auto px-3 pb-4">
            {tab === 'projects' && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {visProj.length === 0 && <p className="text-gray-500 text-sm py-6">No projects match the filters.</p>}
                {visProj.map(p => {
                  const dem = (p.demandBusinessIds || []).map(id => bizById[id]).filter(Boolean);
                  const sup = (p.supplyBusinessIds || []).map(id => bizById[id]).filter(Boolean);
                  const gk = (dem[0] && (dem[0].gatekeepers || [])[0]) || (sup[0] && (sup[0].gatekeepers || [])[0]);
                  const needsReview = !isCanonicalPhased(p);
                  const stopped = (p.status || 'active') === 'stopped';
                  return (
                    <div key={p.id} onClick={() => setDetail({ kind: 'project', record: p })} className="bg-gray-800 border border-gray-700 rounded-xl p-3 cursor-pointer hover:border-orange-500/60 transition-colors">
                      <div className="flex items-start justify-between gap-2 mb-2">
                        <div className="min-w-0"><div className="text-white font-bold text-sm truncate">{p.address || 'No address'}</div><div className="text-[11px] text-gray-500">loc 00 · {needsReview ? <span className="text-amber-400">⚠ Phase needs review{p.phase ? ` (${p.phase})` : ''}</span> : (PHASE_LABEL[p.phase] || p.phase || '—')}</div></div>
                        <div className="flex items-center gap-1.5 shrink-0">
                          {p.isProspect && <span className="text-[10px] font-bold text-yellow-300 bg-yellow-500/15 border border-yellow-500/40 rounded px-1.5 py-0.5">⭐</span>}
                          {p.outcome === 'needs_eval' && !stopped && <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-amber-500/20 text-amber-300 whitespace-nowrap">🔎 eval</span>}
                          <span className={`text-[10px] font-bold px-2 py-0.5 rounded whitespace-nowrap capitalize ${stopped ? 'bg-red-500/20 text-red-300' : 'bg-blue-500/20 text-blue-300'}`}>{stopped ? '⛔ stopped' : (p.status || 'active')}</span>
                          <span className="text-[10px] font-bold text-orange-300 border border-orange-500/40 rounded px-1.5 py-0.5 flex items-center gap-1"><Edit2 size={10} /> Open</span>
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-1.5 mb-2 text-[10px] font-bold">
                        <span className={`px-2 py-0.5 rounded ${needsReview ? 'bg-amber-500/20 text-amber-300' : 'bg-gray-700 text-gray-300'}`}>{needsReview ? '⚠ review' : `${(p.phase || '').toUpperCase()} · ${PHASE_LABEL[p.phase] || '—'}`}</span>
                        <span className="px-2 py-0.5 rounded bg-gray-700 text-gray-300">{(p.photos || []).length} photo{(p.photos || []).length === 1 ? '' : 's'}</span>
                      </div>
                      {gk && <div className="bg-gray-900 border border-gray-700 rounded-lg p-2 mb-2 text-[11px]"><div className="text-gray-400 font-bold flex items-center gap-1.5">🔑 Gatekeeper {gk.priorityCode && <span className="font-mono text-[10px] text-orange-300 bg-orange-500/10 rounded px-1">{gk.priorityCode}</span>}</div><div className="text-gray-200 font-semibold">{gk.name || '—'}{gk.role ? ` · ${gk.role}` : ''}</div>{(gk.email || gk.phone) && <div className="text-gray-500">{[gk.email, gk.phone].filter(Boolean).join(' · ')}</div>}</div>}
                      <div className="border border-gray-700 rounded-lg p-2">
                        <div className="text-[10px] font-bold text-gray-500 uppercase mb-1 flex justify-between"><span>Businesses on this project</span><span>{dem.length + sup.length}</span></div>
                        {[...dem.map(b => ['demand', b]), ...sup.map(b => ['supply', b])].map(([side, b], i) => (
                          <div key={i} className="flex items-center text-[12px] py-0.5">{dot(side)}<span className="text-gray-200 font-semibold truncate">{b.businessName || '(unnamed)'}</span><span className="text-gray-500 text-[10px] ml-1.5 truncate">{b.trade ? b.trade + ' · ' : ''}{side}</span>{splitRoleCodes(b.roleCodes)[0] && <span className="ml-auto font-mono text-[10px] text-gray-500">{splitRoleCodes(b.roleCodes).join(' · ')}</span>}{b.possibleDuplicate && <span className="ml-2 text-[9px] text-amber-400 border border-amber-500/40 rounded px-1">dup?</span>}</div>
                        ))}
                        {dem.length + sup.length === 0 && <p className="text-[11px] text-gray-600">No businesses linked.</p>}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {tab === 'businesses' && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {visBiz.length === 0 && <p className="text-gray-500 text-sm py-6">No businesses match the filters.</p>}
                {visBiz.map(b => { const tier = tierOf(b); const nSites = (b.jobsites || []).length; const tierCls = tier === 'Super Active' ? 'bg-fuchsia-500/20 text-fuchsia-300 border-fuchsia-500/40' : tier === 'Very Active' ? 'bg-indigo-500/20 text-indigo-300 border-indigo-500/40' : 'bg-blue-500/20 text-blue-300 border-blue-500/40'; return (
                  <div key={b.id} onClick={() => setDetail({ kind: 'business', record: b })} className="bg-gray-800 border border-gray-700 rounded-xl p-3 cursor-pointer hover:border-orange-500/60 transition-colors">
                    <div className="flex items-start justify-between gap-2 mb-1">
                      <div className="min-w-0 flex items-center">{dot(b.side)}<span className="text-white font-bold text-sm truncate">{b.businessName || '(unnamed)'}</span></div>
                      <div className="flex items-center gap-1 shrink-0">
                      {b.isProspect && <span className="text-[9px] font-bold text-yellow-300 bg-yellow-500/15 border border-yellow-500/40 rounded px-1">⭐ prospect</span>}
                      {b.possibleDuplicate && <span className="text-[9px] text-amber-400 border border-amber-500/40 rounded px-1 whitespace-nowrap">possible duplicate</span>}
                      </div>
                    </div>
                    {tier && <div className="flex flex-wrap items-center gap-1.5 mb-1.5"><span className={`text-[10px] font-bold border rounded px-1.5 py-0.5 ${tierCls}`}>{tier} · {nSites} jobsite{nSites === 1 ? '' : 's'}</span>{tier === 'Super Active' && <span className="text-[10px] font-bold border rounded px-1.5 py-0.5 bg-rose-500/20 text-rose-300 border-rose-500/40">📡 ON AIR Marketing candidate</span>}</div>}
                    <div className="text-[11px] text-gray-500 mb-2 capitalize flex items-center gap-1.5 flex-wrap">{b.side}{b.trade ? ` · ${b.trade}` : ''}
                      {b.nationality && b.nationality !== 'Unknown' && <span className="normal-case text-[9px] font-bold text-violet-300 bg-violet-500/15 border border-violet-500/30 rounded px-1.5 py-0.5">🏳 {b.nationality}</span>}
                      {(b.googleRating !== '' && b.googleRating != null) && <span className="normal-case text-[9px] font-bold text-amber-300 bg-amber-500/15 border border-amber-500/30 rounded px-1.5 py-0.5">★ {b.googleRating}{(b.googleReviewCount !== '' && b.googleReviewCount != null) ? ` (${b.googleReviewCount})` : ''}</span>}
                    </div>
                    {splitRoleCodes(b.roleCodes).length > 0 && <div className="flex flex-wrap gap-1 mb-2">{splitRoleCodes(b.roleCodes).map((c, i) => <span key={i} className="font-mono text-[10px] text-gray-300 bg-gray-700 rounded px-1.5 py-0.5">{c}</span>)}</div>}
                    {(() => { const chips = <SocialChips socials={b.socials} />; return chips ? <div className="mb-2">{chips}</div> : null; })()}
                    <div className="text-[11px] text-gray-400 space-y-0.5">
                      {b.phone && <div>📞 {b.phone}</div>}
                      {b.website && <div className="truncate">🌐 {b.website}</div>}
                      <div className="flex gap-3 text-gray-500 pt-1">
                        <span>📍 {(b.jobsites || []).length} jobsite{(b.jobsites || []).length === 1 ? '' : 's'}</span>
                        <span>🔑 {(b.gatekeepers || []).length} gatekeeper{(b.gatekeepers || []).length === 1 ? '' : 's'}</span>
                      </div>
                    </div>
                  </div>
                ); })}
              </div>
            )}
          </div>
          )}
        </>
      )}

      {/* Full detail view — photos + all fields editable incl. canonical phase + status.
          Saving merges into projects/businesses and clears the "needs review" flag.
          `key` forces a fresh edit-draft when hopping to a linked record. */}
      {detail && (
        <RecordDetail
          key={detail.kind + ':' + detail.record.id}
          kind={detail.kind}
          record={detail.record}
          photos={detail.kind === 'project' ? (detail.record.photos || []) : (detail.record.jobsites || []).flatMap(j => (projById[j.projectId] || {}).photos || [])}
          relationships={relationships}
          projById={projById}
          bizById={bizById}
          onClose={() => setDetail(null)}
          onOpenRecord={(k, r) => setDetail({ kind: k, record: r })}
        />
      )}
    </div>
  );
};

const ManagerWarRoom = ({ goBack, jumpToCollector, activeBlocks }) => {
  const [block, setBlock] = useState(null);     // {lat,lng}[] of the block polygon
  const [blockId, setBlockId] = useState(null); // TerraDraw id of the block (for edit)
  const [routes, setRoutes] = useState([]);     // [{ id, points: {lat,lng}[] }]
  const [routeNames, setRouteNames] = useState([]); // per-route given names (by order)
  const [drawMode, setDrawMode] = useState('block');
  const [managerView, setManagerView] = useState('blocks'); // default = Blocks list
  const [editingBlock, setEditingBlock] = useState(null);   // territory being edited (null = new)
  const [inCharge, setInCharge] = useState('');             // assigned collector uid
  const [collectors, setCollectors] = useState([]);
  const [editSel, setEditSel] = useState(null); // { type:'block' } | { type:'route', id }
  const [zoneName, setZoneName] = useState('');
  const [saving, setSaving] = useState(false);
  const drawApiRef = useRef(null);

  useEffect(() => {
    const unsub = onSnapshot(collection(db, "users"),
      (snap) => setCollectors(snap.docs.map(d => ({ uid: d.id, ...d.data() })).filter(u => u.role === 'collector')),
      (err) => console.error("Failed to load collectors:", err));
    return () => unsub();
  }, []);

  // A territory is one BLOCK polygon holding zero or more ROUTE lines. Block-first:
  // routes/edit unlock once a block exists.
  const hasBlock = !!(block && block.length >= 3);
  const handleShapesChange = ({ block: b, routes: r, mode }) => {
    setBlock(b ? b.points : null); setBlockId(b ? b.id : null); setRoutes(r || []);
    // Mirror the engine's real mode so the toggle/panel reflect an auto-stop after Finish.
    if (mode) setDrawMode(prev => prev === mode ? prev : mode);
  };
  const chooseMode = (m) => {
    if (m !== 'block' && !hasBlock) return; // block-first gate
    setDrawMode(m); setEditSel(null);
    if (drawApiRef.current) drawApiRef.current.setMode(m);
  };
  const undoPoint = () => { if (drawApiRef.current) drawApiRef.current.undo(); };
  const finishShape = () => { if (drawApiRef.current) drawApiRef.current.finish(); setDrawMode('edit'); setEditSel(null); };
  const clearCurrent = () => { if (drawApiRef.current) drawApiRef.current.clear(drawMode); };

  // Edit-by-name
  const selectEdit = (sel) => {
    setEditSel(sel);
    const id = sel.type === 'block' ? blockId : sel.id;
    if (id && drawApiRef.current) drawApiRef.current.selectFeature(id);
  };
  const editUndo = () => {
    if (!editSel || !drawApiRef.current) return;
    drawApiRef.current.trimFeature(editSel.type === 'block' ? blockId : editSel.id);
  };
  const editDelete = () => {
    if (!editSel || editSel.type !== 'route' || !drawApiRef.current) return;
    drawApiRef.current.deleteFeature(editSel.id); setEditSel(null);
  };
  // Click an item in the session list → enter Edit mode and select it.
  const pickItem = (sel) => { chooseMode('edit'); selectEdit(sel); };

  // Open the canvas to CREATE a new block (empty) or EDIT an existing one (loaded).
  const startNewBlock = () => {
    setEditingBlock(null); setBlock(null); setBlockId(null); setRoutes([]); setRouteNames([]);
    setZoneName(''); setInCharge(''); setEditSel(null); setDrawMode('block');
    if (drawApiRef.current) drawApiRef.current.clear('all');
    setManagerView('edit');
  };
  const openEditBlock = (b) => {
    setEditingBlock(b); setZoneName(b.name || ''); setInCharge(b.assignedCollectorUid || '');
    setRouteNames((Array.isArray(b.routes) ? b.routes : []).map((r, i) => (r && r.name) || `Route ${i + 1}`));
    setEditSel(null); setDrawMode('edit'); setManagerView('edit');
  };
  const deleteBlock = async (b) => {
    if (!window.confirm(`Delete block "${b.name}"? This removes the block and its routes for everyone.`)) return;
    try { await deleteDoc(doc(db, "territories", b.id)); } catch (e) { console.error("Delete failed:", e); alert("Could not delete: " + e.message); }
  };

  const resetCanvas = () => {
    if (drawApiRef.current) drawApiRef.current.clear('all');
    setBlock(null); setBlockId(null); setRoutes([]); setRouteNames([]); setEditSel(null);
    setDrawMode('block'); setZoneName(''); setInCharge(''); setEditingBlock(null);
  };

  const handleSaveZone = async () => {
    // Block + name required; routes optional. Routes saved as named OBJECTS.
    const blockPts = hasBlock ? block : null;
    const savedRoutes = routes.filter(r => r.points.length >= 2).map((r, i) => ({ name: (routeNames[i] && routeNames[i].trim()) || `Route ${i + 1}`, points: r.points }));
    if (!blockPts) { alert("Draw a block area first (a territory is a named block)."); return; }
    if (!zoneName) { alert("Please give this territory a name."); return; }
    setSaving(true);
    try {
      const c = collectors.find(x => x.uid === inCharge);
      const payload = {
        name: zoneName, color: "#8b5cf6", block: blockPts, routes: savedRoutes,
        center: blockPts[0], startPoint: (savedRoutes[0] && savedRoutes[0].points[0]) || blockPts[0],
        assignedCollectorUid: inCharge || null, assignedCollectorName: c ? (c.name || c.email) : null,
        updatedAt: serverTimestamp()
      };
      if (editingBlock) await setDoc(doc(db, "territories", editingBlock.id), payload, { merge: true });
      else await addDoc(collection(db, "territories"), { ...payload, createdAt: serverTimestamp() });
      resetCanvas();
      setManagerView('blocks');
    } catch (e) {
      console.error("Failed to save territory:", e);
      alert("Could not save: " + e.message);
    } finally { setSaving(false); }
  };

  const modeBtn = (m, label) => {
    const disabled = m !== 'block' && !hasBlock;
    return <button key={m} disabled={disabled} onClick={() => chooseMode(m)} className={`py-2 rounded-lg text-sm font-bold border transition-all disabled:opacity-40 disabled:cursor-not-allowed ${drawMode === m ? 'bg-orange-600 border-orange-600 text-white' : 'bg-gray-800 border-gray-700 text-gray-300 hover:bg-gray-700'}`}>{label}</button>;
  };

  // Full static class strings (Tailwind can't see dynamically-built class names).
  const itemCls = (active, activeCls) => `text-left px-3 py-2 rounded-lg text-sm font-bold border transition-all ${active ? activeCls : 'bg-gray-800 border-gray-700 text-gray-300 hover:bg-gray-700'}`;

  // ONE layout: map stays the big left area; the right panel swaps content by view.
  // (Evaluation & Sorting and Market are now top-level workspace tabs, not sub-views here.)
  return (
    <APIProvider apiKey={GOOGLE_MAPS_API_KEY}>
      <div className="absolute inset-0 z-0 bg-gray-900 flex flex-col md:flex-row">
        <div className="flex-1 relative min-h-0">
          <Map defaultCenter={{ lat: 43.766, lng: -79.423 }} defaultZoom={13} mapId="blue_jays_admin_map" gestureHandling="greedy" disableDefaultUI={false}>
            {managerView === 'edit' && <AdminDrawingEngine onShapesChange={handleShapesChange} onReady={(api) => { drawApiRef.current = api; if (editingBlock) api.loadShapes(editingBlock.block, editingBlock.routes); }} />}
            {managerView !== 'edit' && (activeBlocks || []).map(b => b.center ? (
              <AdvancedMarker key={b.id} position={b.center} onClick={() => openEditBlock(b)}>
                <div className="px-3 py-1.5 bg-gray-900 text-white font-bold text-xs rounded-full border-2 border-white shadow-xl hover:scale-105 transition-transform whitespace-nowrap max-w-[160px] truncate">{b.name}</div>
              </AdvancedMarker>
            ) : null)}
          </Map>
          <div className="absolute top-2 left-2 flex items-center bg-gray-900/80 backdrop-blur px-3 py-1.5 rounded-lg"><Shield className="text-orange-500 mr-2" size={16} /><span className="font-bold tracking-widest uppercase text-white text-xs">War Room</span></div>
        </div>
        <div className="w-full md:w-96 md:max-w-[40%] shrink-0 bg-gray-900 border-t md:border-t-0 md:border-l border-gray-800 flex flex-col overflow-hidden min-h-0 max-h-full">
          {managerView === 'blocks' && <ManagerBlocks activeBlocks={activeBlocks || []} onCreate={startNewBlock} onEdit={openEditBlock} onDelete={deleteBlock} onAccounts={() => setManagerView('accounts')} onActivity={() => setManagerView('activity')} goBack={goBack} />}
          {managerView === 'accounts' && <ManagerAccounts onDraw={() => setManagerView('blocks')} goBack={goBack} />}
          {managerView === 'activity' && <ManagerActivity activeBlocks={activeBlocks || []} onDraw={() => setManagerView('blocks')} goBack={goBack} />}
          {managerView === 'edit' && (
            <div className="p-3 overflow-y-auto flex flex-col gap-3 h-full">
          <div className="flex items-center justify-between">
            <span className="text-white font-bold text-sm truncate">{editingBlock ? `Editing ${zoneName || editingBlock.name}` : 'New block'}</span>
            <div className="flex items-center gap-3 text-xs font-bold shrink-0"><button onClick={() => { resetCanvas(); setManagerView('blocks'); }} className="text-gray-300 hover:text-white">← Blocks</button><button onClick={goBack} className="text-gray-400 hover:text-white">Exit</button></div>
          </div>
          <div className="grid grid-cols-3 gap-2">
            {modeBtn('block', 'Block')}{modeBtn('route', 'Route')}{modeBtn('edit', 'Edit')}
          </div>
          <div className="text-center text-xs text-gray-400">
            {drawMode === 'block' && !hasBlock && <p>Draw the <span className="text-orange-300 font-bold">block area</span> (tap corners), then Finish. Routes unlock after the block.</p>}
            {drawMode === 'block' && hasBlock && <p>Block ready. Switch to <span className="text-green-300 font-bold">Route</span> to draw paths inside it.</p>}
            {drawMode === 'route' && <p>Tap to draw a <span className="text-green-300 font-bold">route</span>, then Finish.</p>}
            {drawMode === 'edit' && <p>Pick a shape below: drag its points, undo its last point, or delete a route.</p>}
          </div>

          {/* Drawn this session — appears the moment you Finish; also the Edit selector. */}
          <div className="flex flex-col gap-1">
            <span className="text-[10px] font-bold text-gray-500 uppercase tracking-wider">Drawn this session</span>
            <div className="flex flex-col gap-1 max-h-40 overflow-y-auto">
              {!hasBlock && routes.length === 0 && <p className="text-xs text-gray-600 text-center py-2">Nothing drawn yet</p>}
              {hasBlock && <button onClick={() => pickItem({ type: 'block' })} className={itemCls(drawMode === 'edit' && editSel && editSel.type === 'block', 'bg-orange-700 border-orange-500 text-white')}>✓ Block · {block.length} pts</button>}
              {routes.map((r, i) => (
                <div key={r.id} className="flex gap-1 items-center">
                  <button onClick={() => pickItem({ type: 'route', id: r.id })} className={`${itemCls(drawMode === 'edit' && editSel && editSel.id === r.id, 'bg-green-700 border-green-500 text-white')} flex-1 truncate`}>{routeLabel(i, r.points)}</button>
                  <input value={routeNames[i] || ''} onChange={e => setRouteNames(prev => { const a = [...prev]; a[i] = e.target.value; return a; })} placeholder={`Route ${i + 1}`} className="w-24 shrink-0 bg-gray-900 text-white border border-gray-700 rounded-lg px-2 py-1.5 text-xs outline-none" />
                </div>
              ))}
            </div>
          </div>

          {drawMode !== 'edit' ? (
            <div className="grid grid-cols-3 gap-2">
              <button onClick={undoPoint} className="py-2.5 rounded-lg font-bold bg-gray-800 hover:bg-gray-700 text-white text-sm flex items-center justify-center"><RotateCcw size={16} className="mr-1.5" /> Undo</button>
              <button onClick={finishShape} className="py-2.5 rounded-lg font-bold bg-gray-700 hover:bg-gray-600 text-white text-sm flex items-center justify-center"><CheckCircle size={16} className="mr-1.5" /> Finish</button>
              <button onClick={clearCurrent} className="py-2.5 rounded-lg font-bold bg-gray-800 hover:bg-gray-700 text-red-300 text-sm flex items-center justify-center"><X size={16} className="mr-1.5" /> Clear</button>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-2">
              <button onClick={editUndo} disabled={!editSel} className="py-2.5 rounded-lg font-bold bg-gray-800 hover:bg-gray-700 text-white text-sm flex items-center justify-center disabled:opacity-40"><RotateCcw size={16} className="mr-1.5" /> Undo point</button>
              <button onClick={editDelete} disabled={!editSel || editSel.type !== 'route'} className="py-2.5 rounded-lg font-bold bg-gray-800 hover:bg-gray-700 text-red-300 text-sm flex items-center justify-center disabled:opacity-40"><X size={16} className="mr-1.5" /> Delete route</button>
            </div>
          )}

          <div className="mt-auto">
            <label className="text-[10px] font-bold text-gray-500 uppercase tracking-wider block mb-1">In charge</label>
            <select value={inCharge} onChange={e => setInCharge(e.target.value)} className="w-full bg-gray-800 text-white border border-gray-700 rounded-lg p-2.5 mb-2 text-sm outline-none">
              <option value="">Unassigned</option>
              {collectors.map(c => <option key={c.uid} value={c.uid}>{c.name || c.email}</option>)}
            </select>
            <input type="text" placeholder="Territory name (e.g., 'West End Block A')" className="w-full bg-gray-800 text-white border border-gray-700 rounded-lg p-2.5 mb-2 focus:ring-2 focus:ring-orange-500 outline-none" value={zoneName} onChange={e => setZoneName(e.target.value)} />
            <button onClick={handleSaveZone} disabled={saving} className="w-full py-3 rounded-xl font-bold bg-orange-600 hover:bg-orange-700 text-white shadow-[0_0_15px_rgba(139,92,246,0.5)] transition-all flex items-center justify-center disabled:opacity-50">{saving ? <Loader2 size={18} className="mr-2 animate-spin" /> : <Save size={18} className="mr-2" />} {saving ? 'Saving...' : (editingBlock ? 'Save changes' : 'Save block')}</button>
          </div>
            </div>
          )}
        </div>
      </div>
    </APIProvider>
  );
};

// ── MANAGER WORKSPACE — one console with a persistent 4-tab top bar ─────────────────
// Tabs (always visible, one active at a time): Setting Routes · Data Collection ·
// Evaluation & Sorting · Market. Each tab fills the area below the bar.
const ManagerDataCollection = () => (
  <div className="absolute inset-0 bg-gray-900 flex flex-col items-center justify-center text-center px-6">
    <MapPin className="text-blue-500 mb-3" size={32} />
    <div className="font-bold tracking-widest uppercase text-white text-sm mb-2">Data Collection</div>
    <p className="text-gray-500 text-sm max-w-xs">Live mirror of the collector field view — routes in progress and leads as they land. Coming soon.</p>
  </div>
);

// ── AI CONTROL TOWER (5th tab) — engine dashboard + editable classification map ─────
const AI_ENGINES = [
  { name: 'OCR (Vision)', desc: 'Reads text from lead photos', info: 'Confidence: top full-text annotation (set in function for now)' },
  { name: 'Classification (Claude)', desc: 'OCR text → businesses + role codes', info: 'Model claude-sonnet-4-6 · role-code map editable below' },
  { name: 'Voice (Whisper)', desc: 'Voice note → transcript', info: 'Language: auto-detect (set in function for now)' },
  { name: 'City permit (BuildData)', desc: 'Address → live Toronto permit', info: 'Match: full-text q on street + municipality=toronto (set in function for now)' },
  { name: 'Web-enrichment (Google Places)', desc: 'Business → office location + Google rating', info: 'Fields pulled: address, phone, website, rating, reviews (set in function for now)' },
];

// Gather a lead's businesses (demand may be a raw object pre-processing, or an array after).
const leadBusinesses = (l) => {
  if (!l) return [];
  const dem = Array.isArray(l.demand) ? l.demand : (l.demand && (l.demand.businessName || l.demand.trade) ? [l.demand] : []);
  const sup = Array.isArray(l.supply) ? l.supply : [];
  return [...dem, ...sup].filter(b => b && (b.businessName || b.trade || b.company));
};

// ── AI PIPELINE VISUALIZER (n8n-style showcase) ─────────────────────────────────────
// Read-only diagram of the EXISTING processLeadAI chain (OCR → Classify → Enrich → Voice
// → City). Nodes + connecting arrows; click a node for detail; optional "trace a lead"
// shows that lead's real result at each step. NOT an editor — visual showcase only.
const PIPE_NODES = [
  { key: 'ocr', name: 'OCR (Vision)', icon: ScanLine, color: '#22d3ee', result: (l) => (l.enrichmentData && l.enrichmentData.ocr) ? String(l.enrichmentData.ocr) : null },
  { key: 'classify', name: 'Classification (Claude)', icon: Network, color: '#e879f9', result: (l) => { const bs = leadBusinesses(l); return bs.length ? bs.map(b => `${b.businessName || b.company || b.trade || '?'}${b.roleCode ? ` (${b.roleCode})` : (b.trade ? ` · ${b.trade}` : '')}`).join('\n') : null; } },
  { key: 'enrich', name: 'Web-enrichment (Google Places)', icon: Globe, color: '#60a5fa', result: (l) => { const bs = leadBusinesses(l).filter(b => b.website || b.googleRating || b.officeLocation); return bs.length ? bs.map(b => `${b.businessName || '?'}${b.website ? ` · ${b.website}` : ''}${(b.googleRating || b.googleRating === 0) && b.googleRating !== '' ? ` · ★${b.googleRating}${b.googleReviewCount ? ` (${b.googleReviewCount})` : ''}` : ''}`).join('\n') : null; } },
  { key: 'voice', name: 'Voice (Whisper)', icon: Mic, color: '#2dd4bf', result: (l) => (l.enrichmentData && l.enrichmentData.transcript) ? String(l.enrichmentData.transcript) : null },
  { key: 'city', name: 'City permit (BuildData)', icon: Building, color: '#f59e0b', result: (l) => { const c = l.cityData || {}; const keys = Object.keys(c).filter(k => k !== '_source' && c[k] !== '' && c[k] != null); return keys.length ? keys.slice(0, 8).map(k => `${k}: ${typeof c[k] === 'object' ? JSON.stringify(c[k]) : c[k]}`).join('\n') : null; } },
];
const Connector = () => (
  <div className="flex items-center shrink-0 self-center px-0.5" aria-hidden>
    <div className="w-6 md:w-9 h-[2px] bg-gradient-to-r from-gray-600 to-gray-400" />
    <ChevronRight size={16} className="text-gray-400 -ml-1.5" />
  </div>
);
const EndPill = ({ icon: Icon, label, color }) => (
  <div className="shrink-0 self-center flex flex-col items-center justify-center w-24 h-24 rounded-xl border-2 border-dashed" style={{ borderColor: color }}>
    <Icon size={22} style={{ color }} />
    <span className="text-[10px] font-bold text-gray-300 mt-1 text-center leading-tight px-1">{label}</span>
  </div>
);
const AIPipeline = ({ leads = [] }) => {
  const [openKey, setOpenKey] = useState(null);
  const [traceId, setTraceId] = useState('');
  const meta = Object.fromEntries(AI_ENGINES.map(e => [e.name, e]));
  const trace = traceId ? leads.find(l => l.id === traceId) : null;
  const openNode = PIPE_NODES.find(n => n.key === openKey) || null;
  const openMeta = openNode ? meta[openNode.name] : null;
  const openResult = (openNode && trace) ? openNode.result(trace) : undefined;
  const gridBg = { backgroundColor: '#0b1220', backgroundImage: 'radial-gradient(rgba(148,163,184,0.18) 1px, transparent 1px)', backgroundSize: '18px 18px' };
  return (
    <div className="rounded-xl border border-gray-700 overflow-hidden">
      {/* Header + trace picker */}
      <div className="flex items-center gap-2 flex-wrap px-3 py-2 bg-gray-950 border-b border-gray-800">
        <span className="text-[11px] font-bold text-gray-300 uppercase tracking-wider flex items-center"><Network size={14} className="mr-1.5 text-orange-400" /> AI pipeline</span>
        <span className="text-[10px] font-bold text-green-400 inline-flex items-center"><span className="w-2 h-2 rounded-full bg-green-400 mr-1" />5 engines active</span>
        <div className="ml-auto flex items-center gap-2">
          <span className="text-[10px] font-bold text-gray-500 uppercase">Trace a lead</span>
          <select value={traceId} onChange={e => setTraceId(e.target.value)} className="bg-gray-800 text-white border border-gray-700 rounded px-2 py-1 text-xs outline-none max-w-[200px]">
            <option value="">— static view —</option>
            {leads.map(l => <option key={l.id} value={l.id}>{(l.address || '(no address)').slice(0, 40)}</option>)}
          </select>
          {trace && <button onClick={() => setTraceId('')} className="text-[10px] font-bold text-gray-300 bg-gray-800 border border-gray-700 rounded px-2 py-1">Clear</button>}
        </div>
      </div>

      {/* Diagram */}
      <div className="overflow-x-auto p-4" style={gridBg}>
        <div className="flex items-stretch gap-0 min-w-max">
          <EndPill icon={Camera} label="Photo / Lead" color="#94a3b8" />
          <Connector />
          {PIPE_NODES.map((n, i) => {
            const m = meta[n.name] || {};
            const Icon = n.icon;
            const res = trace ? n.result(trace) : undefined;
            const isOpen = openKey === n.key;
            return (
              <React.Fragment key={n.key}>
                <button onClick={() => setOpenKey(isOpen ? null : n.key)} className={`shrink-0 self-center text-left w-44 bg-gray-900 rounded-xl border transition-all ${isOpen ? 'border-orange-400 ring-2 ring-orange-500/40' : 'border-gray-700 hover:border-gray-500'}`} style={{ borderTopColor: n.color, borderTopWidth: 3 }}>
                  <div className="p-2.5">
                    <div className="flex items-center gap-2">
                      <span className="w-7 h-7 rounded-lg flex items-center justify-center shrink-0" style={{ backgroundColor: n.color + '22' }}><Icon size={15} style={{ color: n.color }} /></span>
                      <span className="text-white font-bold text-xs leading-tight">{n.name}</span>
                    </div>
                    <div className="flex items-center gap-1 mt-1.5"><span className="w-1.5 h-1.5 rounded-full bg-green-400" /><span className="text-[9px] font-bold text-green-400 uppercase">active</span><span className="text-[9px] text-gray-600 ml-auto">step {i + 1}</span></div>
                    <p className="text-[10px] text-gray-400 mt-1 leading-snug">{m.desc}</p>
                    {trace && (
                      res
                        ? <div className="mt-1.5 text-[9px] text-emerald-300 bg-emerald-500/10 border border-emerald-500/30 rounded px-1.5 py-1 whitespace-pre-wrap break-words max-h-16 overflow-hidden">{res}</div>
                        : <div className="mt-1.5 text-[9px] font-bold text-amber-400 bg-amber-500/10 border border-amber-500/30 rounded px-1.5 py-1">no result for this lead</div>
                    )}
                  </div>
                </button>
                {i < PIPE_NODES.length - 1 && <Connector />}
              </React.Fragment>
            );
          })}
          <Connector />
          <EndPill icon={CheckCircle} label="Enriched lead" color="#34d399" />
        </div>
      </div>

      {/* Detail panel for the selected node */}
      {openNode && (
        <div className="px-3 py-3 bg-gray-950 border-t border-gray-800">
          <div className="flex items-center gap-2 mb-1">
            <span className="w-6 h-6 rounded-lg flex items-center justify-center" style={{ backgroundColor: openNode.color + '22' }}><openNode.icon size={13} style={{ color: openNode.color }} /></span>
            <span className="text-white font-bold text-sm">{openNode.name}</span>
            <span className="text-[10px] font-bold text-green-400 inline-flex items-center ml-1"><span className="w-2 h-2 rounded-full bg-green-400 mr-1" />active</span>
            <button onClick={() => setOpenKey(null)} className="ml-auto text-gray-500 hover:text-white"><X size={16} /></button>
          </div>
          <p className="text-xs text-gray-300">{openMeta && openMeta.desc}</p>
          <p className="text-[11px] text-gray-500 mt-1">⚙ {openMeta && openMeta.info}</p>
          {trace && (
            <div className="mt-2">
              <div className="text-[10px] font-bold text-gray-500 uppercase mb-1">Result for “{(trace.address || '(no address)').slice(0, 48)}”</div>
              {openResult
                ? <pre className="text-[11px] text-emerald-200 bg-emerald-500/10 border border-emerald-500/30 rounded p-2 whitespace-pre-wrap break-words max-h-40 overflow-y-auto">{openResult}</pre>
                : <div className="text-[11px] font-bold text-amber-400 bg-amber-500/10 border border-amber-500/30 rounded p-2">No result at this step for this lead — the engine ran but found nothing (or hasn't run yet).</div>}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
const ManagerControlTower = ({ onBack, goBack }) => {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState('');
  const [tiers, setTiers] = useState(null);          // activity-tier thresholds
  const [savingTiers, setSavingTiers] = useState(false);
  const [savedTiers, setSavedTiers] = useState('');
  const [rates, setRates] = useState(null);          // collector pay rates (settings)
  const [savingRates, setSavingRates] = useState(false);
  const [savedRates, setSavedRates] = useState('');
  const [leads, setLeads] = useState([]);            // for the pipeline "trace a lead" mode
  useEffect(() => {
    const u = onSnapshot(collection(db, 'leads'), s => setLeads(s.docs.map(d => ({ id: d.id, ...d.data() }))), e => console.error('pipeline leads load:', e));
    return () => u();
  }, []);
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const ref = doc(db, 'ai_config', 'classification');
        const snap = await getDoc(ref);
        if (snap.exists()) { if (alive) setCfg({ roleCodeMap: snap.data().roleCodeMap || [], demandFixed: snap.data().demandFixed || [] }); }
        else { // seed from the hardcoded map so nothing breaks
          const seed = { roleCodeMap: SEED_ROLE_MAP, demandFixed: SEED_DEMAND_FIXED };
          await setDoc(ref, { ...seed, updatedAt: serverTimestamp() });
          if (alive) setCfg(seed);
        }
      } catch (e) { console.error('ai_config load failed:', e); if (alive) setCfg({ roleCodeMap: SEED_ROLE_MAP, demandFixed: SEED_DEMAND_FIXED }); }
      try {
        const tref = doc(db, 'ai_config', 'activity_tiers');
        const tsnap = await getDoc(tref);
        if (tsnap.exists()) { if (alive) setTiers({ ...SEED_TIERS, ...tsnap.data() }); }
        else { await setDoc(tref, { ...SEED_TIERS, updatedAt: serverTimestamp() }); if (alive) setTiers({ ...SEED_TIERS }); }
      } catch (e) { console.error('activity_tiers load failed:', e); if (alive) setTiers({ ...SEED_TIERS }); }
      try {
        const rref = doc(db, 'ai_config', 'collector_rates');
        const rsnap = await getDoc(rref);
        if (rsnap.exists()) { if (alive) setRates({ ...SEED_RATES, ...rsnap.data() }); }
        else { await setDoc(rref, { ...SEED_RATES, updatedAt: serverTimestamp() }); if (alive) setRates({ ...SEED_RATES }); }
      } catch (e) { console.error('collector_rates load failed:', e); if (alive) setRates({ ...SEED_RATES }); }
    })();
    return () => { alive = false; };
  }, []);
  const upTier = (k, v) => setTiers(t => ({ ...t, [k]: v === '' ? '' : Number(v) }));
  const saveTiers = async () => {
    setSavingTiers(true); setSavedTiers('');
    try { await setDoc(doc(db, 'ai_config', 'activity_tiers'), { active: Number(tiers.active) || 0, veryActive: Number(tiers.veryActive) || 0, superActive: Number(tiers.superActive) || 0, updatedAt: serverTimestamp() }, { merge: true }); setSavedTiers('Saved — applies when tiers are computed/displayed.'); }
    catch (e) { setSavedTiers('Save failed: ' + e.message); }
    finally { setSavingTiers(false); }
  };
  const upRate = (k, v) => setRates(r => ({ ...r, [k]: v === '' ? '' : Number(v) }));
  const saveRates = async () => {
    setSavingRates(true); setSavedRates('');
    try { await setDoc(doc(db, 'ai_config', 'collector_rates'), { perKm: Number(rates.perKm) || 0, perLeadGenerated: Number(rates.perLeadGenerated) || 0, perLeadUpdated: Number(rates.perLeadUpdated) || 0, updatedAt: serverTimestamp() }, { merge: true }); setSavedRates('Saved — earnings recompute live in the Cost view + collector profiles.'); }
    catch (e) { setSavedRates('Save failed: ' + e.message); }
    finally { setSavingRates(false); }
  };
  const ic = "bg-gray-950 text-white border border-gray-700 rounded px-2 py-1 text-xs outline-none focus:ring-1 focus:ring-orange-500";
  const upRow = (i, k, v) => setCfg(c => ({ ...c, roleCodeMap: c.roleCodeMap.map((r, idx) => idx === i ? { ...r, [k]: (k === 'title' || k === 'phase') ? (v === '' ? '' : Number(v)) : v } : r) }));
  const addRow = () => setCfg(c => ({ ...c, roleCodeMap: [...(c.roleCodeMap || []), { trade: '', title: '', phase: '', side: 'supply', code: '' }] }));
  const rmRow = (i) => setCfg(c => ({ ...c, roleCodeMap: c.roleCodeMap.filter((_, idx) => idx !== i) }));
  const upDem = (i, k, v) => setCfg(c => ({ ...c, demandFixed: c.demandFixed.map((r, idx) => idx === i ? { ...r, [k]: v } : r) }));
  const addDem = () => setCfg(c => ({ ...c, demandFixed: [...(c.demandFixed || []), { role: '', code: '' }] }));
  const rmDem = (i) => setCfg(c => ({ ...c, demandFixed: c.demandFixed.filter((_, idx) => idx !== i) }));
  const save = async () => {
    setSaving(true); setSaved('');
    try { await setDoc(doc(db, 'ai_config', 'classification'), { roleCodeMap: cfg.roleCodeMap || [], demandFixed: cfg.demandFixed || [], updatedAt: serverTimestamp() }, { merge: true }); setSaved('Saved — applies to the next classification immediately, no redeploy needed.'); }
    catch (e) { setSaved('Save failed: ' + e.message); }
    finally { setSaving(false); }
  };
  return (
    <div className="absolute inset-0 z-0 bg-gray-900 flex flex-col">
      <div className="flex items-center justify-between p-3 border-b border-gray-800 shrink-0">
        <div className="flex items-center"><Shield className="text-orange-500 mr-2" size={20} /><span className="font-bold tracking-widest uppercase text-white text-sm">AI Control Tower</span></div>
        <div className="flex items-center gap-3 text-xs font-bold"><button onClick={onBack} className="text-gray-300 hover:text-white">← Manager</button><button onClick={goBack} className="text-gray-400 hover:text-white">Exit</button></div>
      </div>
      <div className="flex-1 overflow-y-auto p-4 space-y-5">
        {/* Part 1 — AI pipeline visualizer (n8n-style showcase, display-only) */}
        <div>
          <div className="text-[11px] font-bold text-gray-500 uppercase mb-2">AI pipeline — how a lead is enriched</div>
          <AIPipeline leads={leads} />
          <p className="text-[10px] text-gray-600 mt-1.5">Showcase of the live enrichment chain (read-only). Pick a lead in “Trace a lead” to see its real result at each step. The classification role-code map below is the one editable piece.</p>
        </div>

        {/* Part 2 — editable classification map */}
        {!cfg ? <p className="text-gray-500 text-sm">Loading classification map…</p> : (
          <>
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-[11px] font-bold text-gray-500 uppercase">Supply role-code map ({(cfg.roleCodeMap || []).length})</span>
                <button onClick={addRow} className="text-[11px] font-bold text-blue-400 hover:text-blue-300">+ Add row</button>
              </div>
              <div className="space-y-1">
                <div className="grid grid-cols-[1fr_56px_56px_84px_84px_24px] gap-1 text-[9px] font-bold text-gray-500 uppercase px-1"><span>Trade</span><span>Title</span><span>Phase</span><span>Side</span><span>Code</span><span /></div>
                {(cfg.roleCodeMap || []).map((r, i) => (
                  <div key={i} className="grid grid-cols-[1fr_56px_56px_84px_84px_24px] gap-1 items-center">
                    <input className={ic} value={r.trade || ''} onChange={e => upRow(i, 'trade', e.target.value)} />
                    <input className={ic} value={r.title ?? ''} onChange={e => upRow(i, 'title', e.target.value)} />
                    <input className={ic} value={r.phase ?? ''} onChange={e => upRow(i, 'phase', e.target.value)} />
                    <select className={ic} value={r.side || 'supply'} onChange={e => upRow(i, 'side', e.target.value)}><option value="supply">supply</option><option value="demand">demand</option></select>
                    <input className={ic + ' font-mono'} value={r.code || ''} onChange={e => upRow(i, 'code', e.target.value)} />
                    <button onClick={() => rmRow(i)} className="text-gray-500 hover:text-red-400"><X size={14} /></button>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-[11px] font-bold text-gray-500 uppercase">Demand fixed list ({(cfg.demandFixed || []).length})</span>
                <button onClick={addDem} className="text-[11px] font-bold text-blue-400 hover:text-blue-300">+ Add row</button>
              </div>
              <div className="space-y-1">
                <div className="grid grid-cols-[1fr_84px_24px] gap-1 text-[9px] font-bold text-gray-500 uppercase px-1"><span>Role</span><span>Code</span><span /></div>
                {(cfg.demandFixed || []).map((r, i) => (
                  <div key={i} className="grid grid-cols-[1fr_84px_24px] gap-1 items-center">
                    <input className={ic} value={r.role || ''} onChange={e => upDem(i, 'role', e.target.value)} />
                    <input className={ic + ' font-mono'} value={r.code || ''} onChange={e => upDem(i, 'code', e.target.value)} />
                    <button onClick={() => rmDem(i)} className="text-gray-500 hover:text-red-400"><X size={14} /></button>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex items-center gap-3">
              <button onClick={save} disabled={saving} className="px-4 py-2 rounded-lg font-bold text-sm bg-orange-600 hover:bg-orange-700 text-white disabled:opacity-50 flex items-center">{saving ? <><Loader2 size={16} className="animate-spin mr-1.5" /> Saving…</> : <><Save size={16} className="mr-1.5" /> Save map</>}</button>
              {saved && <span className="text-[11px] text-gray-400">{saved}</span>}
            </div>
            <p className="text-[10px] text-gray-600">Changes apply to the next classification immediately — no redeploy needed. The function falls back to its built-in map if this config is ever missing.</p>
          </>
        )}

        {/* Part 4 — Activity-tier thresholds */}
        {tiers && (
          <div className="border-t border-gray-800 pt-4">
            <div className="text-[11px] font-bold text-gray-500 uppercase mb-2">Activity Tiers</div>
            <div className="grid grid-cols-3 gap-2 max-w-md">
              <label className="text-[11px] text-gray-400">Active ≥<input type="number" className={ic + ' w-full mt-1'} value={tiers.active ?? ''} onChange={e => upTier('active', e.target.value)} /></label>
              <label className="text-[11px] text-gray-400">Very Active ≥<input type="number" className={ic + ' w-full mt-1'} value={tiers.veryActive ?? ''} onChange={e => upTier('veryActive', e.target.value)} /></label>
              <label className="text-[11px] text-gray-400">Super Active ≥<input type="number" className={ic + ' w-full mt-1'} value={tiers.superActive ?? ''} onChange={e => upTier('superActive', e.target.value)} /></label>
            </div>
            <div className="flex items-center gap-3 mt-2">
              <button onClick={saveTiers} disabled={savingTiers} className="px-4 py-2 rounded-lg font-bold text-sm bg-orange-600 hover:bg-orange-700 text-white disabled:opacity-50 flex items-center">{savingTiers ? <><Loader2 size={16} className="animate-spin mr-1.5" /> Saving…</> : <><Save size={16} className="mr-1.5" /> Save tiers</>}</button>
              {savedTiers && <span className="text-[11px] text-gray-400">{savedTiers}</span>}
            </div>
            <p className="text-[10px] text-gray-600 mt-2">Tier = a business's accumulated jobsite count vs these thresholds. <b className="text-rose-300">Super Active businesses become ON AIR Marketing candidates</b> (acquisition: bring into our network or convert to our customer — distinct from selling leads for existing customers). Thresholds apply when tiers are computed/displayed.</p>
          </div>
        )}

        {/* Part 5 — Collector pay rates (drives the Cost view + collector earnings) */}
        {rates && (
          <div className="border-t border-gray-800 pt-4">
            <div className="text-[11px] font-bold text-gray-500 uppercase mb-2">Collector Pay Rates</div>
            <div className="grid grid-cols-3 gap-2 max-w-md">
              <label className="text-[11px] text-gray-400">Per km ($)<input type="number" step="0.01" className={ic + ' w-full mt-1'} value={rates.perKm ?? ''} onChange={e => upRate('perKm', e.target.value)} /></label>
              <label className="text-[11px] text-gray-400">Per lead generated ($)<input type="number" step="0.01" className={ic + ' w-full mt-1'} value={rates.perLeadGenerated ?? ''} onChange={e => upRate('perLeadGenerated', e.target.value)} /></label>
              <label className="text-[11px] text-gray-400">Per lead updated ($)<input type="number" step="0.01" className={ic + ' w-full mt-1'} value={rates.perLeadUpdated ?? ''} onChange={e => upRate('perLeadUpdated', e.target.value)} /></label>
            </div>
            <div className="flex items-center gap-3 mt-2">
              <button onClick={saveRates} disabled={savingRates} className="px-4 py-2 rounded-lg font-bold text-sm bg-orange-600 hover:bg-orange-700 text-white disabled:opacity-50 flex items-center">{savingRates ? <><Loader2 size={16} className="animate-spin mr-1.5" /> Saving…</> : <><Save size={16} className="mr-1.5" /> Save rates</>}</button>
              {savedRates && <span className="text-[11px] text-gray-400">{savedRates}</span>}
            </div>
            <p className="text-[10px] text-gray-600 mt-2">Earnings = (km × per-km) + (leads generated × rate) + (leads updated × rate). The <b className="text-gray-300">Cost</b> tab shows each collector's total; rates are settings only and live separately from that cost view.</p>
          </div>
        )}
      </div>
    </div>
  );
};

// ── 2023 MARKET — read-only archive (market_2023), firewalled from live data ────────
// Reuses MarketFilterBar + MarketMapView. Zones = standard geography (neighbourhood/FSA),
// not operational block codes. Reference/prospecting only: no deal files, no inject-to-board.
const SUPPLY_KW = ['drywall', 'framing', 'concrete', 'rebar', 'roof', 'plumb', 'electric', 'hvac', 'mechanical', 'insulat', 'paint', 'mason', 'landscap', 'lumber', 'supply', 'floor', 'tile', 'stucco', 'window', 'door', 'excavat', 'demolition', 'steel', 'glass', 'cabinet', 'stone', 'fence', 'fencing', 'siding', 'aluminum', 'hardware', 'granite', 'countertop'];
const DEMAND_KW = ['builder', 'homes', 'developer', 'development', 'construction', 'contracting', 'realty', 'realtor', 'custom home', 'build group', 'design', 'architect', 'properties'];
const derive2023Side = (c) => {
  const t = `${c.name || ''} ${c.desc || ''} ${c.listName || ''}`.toLowerCase();
  if (SUPPLY_KW.some(k => t.includes(k))) return 'supply';
  if (DEMAND_KW.some(k => t.includes(k))) return 'demand';
  return '';
};

const Market2023 = ({ onBack, goBack }) => {
  const [records, setRecords] = useState([]);
  const [mode, setMode] = useState('list');     // list | map
  const [filters, setFilters] = useState({ side: 'all', phase: 'all', zone: 'all', q: '' });
  const [natF, setNatF] = useState('all');
  const nat = useNationalities();
  const [lightbox, setLightbox] = useState(null); // { urls, i }
  useEffect(() => {
    const u = onSnapshot(collection(db, "market_2023"), s => setRecords(s.docs.map(d => ({ id: d.id, ...d.data() }))), e => console.error("market_2023 load:", e));
    return () => u();
  }, []);

  const empty = records.length === 0;
  const ql = filters.q.trim().toLowerCase();
  const zoneKey = (c) => c.neighbourhood || c.fsa || '';
  const zoneCount = {};
  records.forEach(c => { const z = zoneKey(c); if (z) zoneCount[z] = (zoneCount[z] || 0) + 1; });
  const zones = [{ value: 'all', label: 'All areas' }, ...Object.keys(zoneCount).sort().map(z => ({ value: z, label: `${z} (${zoneCount[z]})` }))];

  const vis = records.filter(c => {
    const side = derive2023Side(c);
    return (filters.side === 'all' || side === filters.side)
      && (filters.phase === 'all' || c.phase === filters.phase)
      && (filters.zone === 'all' || zoneKey(c) === filters.zone)
      && (natF === 'all' || (c.nationality || 'Unknown') === natF)
      && (!ql || (c.name || '').toLowerCase().includes(ql) || (c.phone || '').toLowerCase().includes(ql) || (c.website || '').toLowerCase().includes(ql) || (c.email || '').toLowerCase().includes(ql) || (c.postalCode || '').toLowerCase().includes(ql));
  });
  // Shape geocoded records as "projects" so the reusable map can plot them directly.
  const mapProjects = vis.map(c => ({ id: c.id, address: c.formattedAddress || c.name || '(unnamed)', coords: c.coords || null, phase: c.phase, photos: c.photos || [], demandBusinessIds: [], supplyBusinessIds: [] }));
  const geocodedCount = records.filter(c => c.coords).length;

  return (
    <div className="absolute inset-0 z-0 bg-gray-900 flex flex-col">
      <div className="flex items-center justify-between p-3 border-b border-gray-800 shrink-0">
        <div className="flex items-center gap-2"><Layers className="text-amber-500" size={18} /><span className="font-bold tracking-widest uppercase text-white text-sm">2023 Market</span><span className="text-[9px] font-bold text-amber-300 border border-amber-500/40 rounded px-1.5 py-0.5">ARCHIVE · READ-ONLY</span></div>
        <div className="flex items-center gap-3 text-xs font-bold"><button onClick={onBack} className="text-gray-300 hover:text-white">← Manager</button><button onClick={goBack} className="text-gray-400 hover:text-white">Exit</button></div>
      </div>

      {empty ? (
        <div className="flex-1 flex flex-col items-center justify-center text-center px-6">
          <Layers className="text-gray-600 mb-3" size={36} />
          <div className="text-gray-300 font-bold mb-1">No 2023 archive loaded</div>
          <p className="text-gray-500 text-sm max-w-xs">The <span className="font-mono text-gray-300">market_2023</span> collection is empty. Run the one-time Trello import to populate it.</p>
        </div>
      ) : (
        <>
          <div className="shrink-0 px-3 pt-3">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-[11px] text-gray-500 font-bold">{records.length} archived cards · {geocodedCount} mappable</span>
              <div className="ml-auto flex rounded-lg border border-gray-700 overflow-hidden">
                <button onClick={() => setMode('list')} className={`px-3 py-1.5 text-xs font-bold ${mode === 'list' ? 'bg-white text-gray-900' : 'bg-gray-800 text-gray-300'}`}>List</button>
                <button onClick={() => setMode('map')} className={`px-3 py-1.5 text-xs font-bold ${mode === 'map' ? 'bg-white text-gray-900' : 'bg-gray-800 text-gray-300'}`}>Map</button>
              </div>
            </div>
            <div className="mb-3">
              <MarketFilterBar value={filters} onChange={setFilters} zones={zones} resultLabel={`${vis.length} records`}>
                <select value={natF} onChange={e => setNatF(e.target.value)} className="bg-gray-800 text-white border border-gray-700 rounded px-2 py-1.5 text-xs outline-none"><option value="all">All nationalities</option>{['Unknown', ...nat.options.filter(o => o.toLowerCase() !== 'unknown')].map(o => <option key={o} value={o}>{o}</option>)}</select>
              </MarketFilterBar>
            </div>
          </div>

          {mode === 'map' ? (
            <div className="flex-1 relative min-h-0">
              <MarketMapView projects={mapProjects} businesses={[]} bizCoordOf={() => null} plot="projects" onPlotChange={() => {}} focusBlock={null} hideToggle projLabel="2023 record" onPick={() => setMode('list')} />
            </div>
          ) : (
            <div className="flex-1 overflow-y-auto px-3 pb-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {vis.length === 0 && <p className="text-gray-500 text-sm py-6">No records match the filters.</p>}
                {vis.map(c => { const side = derive2023Side(c); const photos = c.photos || []; return (
                  <div key={c.id} className="bg-gray-800 border border-gray-700 rounded-xl p-3">
                    <div className="flex items-start justify-between gap-2 mb-1">
                      <div className="min-w-0 flex items-center">{side && <span className={`inline-block w-2 h-2 rounded-full mr-1.5 ${side === 'demand' ? 'bg-blue-400' : 'bg-amber-400'}`} />}<span className="text-white font-bold text-sm truncate">{c.name || '(unnamed)'}</span></div>
                      {c.phase && <span className="text-[9px] font-bold text-orange-300 border border-orange-500/40 rounded px-1 whitespace-nowrap">Phase {String(c.phase).replace('p', '')}</span>}
                    </div>
                    <div className="text-[11px] text-gray-500 mb-1.5">{c.listName || '—'}{side ? ` · ${side}` : ''}{(c.neighbourhood || c.fsa) ? ` · ${c.neighbourhood || c.fsa}` : ''}</div>
                    <div className="text-[11px] text-gray-400 space-y-0.5">
                      {c.phone && <div>📞 {c.phone}</div>}
                      {c.website && <div className="truncate">🌐 {c.website}</div>}
                      {c.email && <div className="truncate">✉️ {c.email}</div>}
                      {c.postalCode && <div>📮 {c.postalCode}{c.coords ? '' : ' · (no map pin)'}</div>}
                    </div>
                    {photos.length > 0 && (
                      <div className="mt-2">
                        <div className="flex gap-1.5 overflow-x-auto">
                          {photos.slice(0, 6).map((u, i) => <img key={i} src={u} alt="" onClick={() => setLightbox({ urls: photos, i })} className="w-14 h-14 object-cover rounded border border-gray-700 shrink-0 cursor-pointer hover:opacity-80" />)}
                        </div>
                        <div className="text-[10px] text-gray-600 mt-1">{photos.length} photo{photos.length === 1 ? '' : 's'}{photos.length > 6 ? ' · tap to view all' : ''}</div>
                      </div>
                    )}
                  </div>
                ); })}
              </div>
            </div>
          )}
        </>
      )}

      {/* Photo lightbox */}
      {lightbox && (
        <div className="absolute inset-0 z-50 bg-black/90 flex flex-col items-center justify-center" onClick={() => setLightbox(null)}>
          <img src={lightbox.urls[lightbox.i]} alt="" className="max-h-[80%] max-w-[90%] object-contain rounded" onClick={e => e.stopPropagation()} />
          <div className="flex items-center gap-4 mt-3 text-white" onClick={e => e.stopPropagation()}>
            <button onClick={() => setLightbox(l => ({ ...l, i: (l.i - 1 + l.urls.length) % l.urls.length }))} className="px-3 py-1.5 bg-gray-800 rounded font-bold">‹ Prev</button>
            <span className="text-sm font-bold">{lightbox.i + 1} / {lightbox.urls.length}</span>
            <button onClick={() => setLightbox(l => ({ ...l, i: (l.i + 1) % l.urls.length }))} className="px-3 py-1.5 bg-gray-800 rounded font-bold">Next ›</button>
            <button onClick={() => setLightbox(null)} className="px-3 py-1.5 bg-gray-700 rounded font-bold">Close ✕</button>
          </div>
        </div>
      )}
    </div>
  );
};

// ── 3D NETWORK VIEW (Part 2) ───────────────────────────────────────────────────────
// Live force-directed graph of the REAL Market: business + project nodes, relationship
// edges (business↔business sharing a jobsite) + business→project edges from jobsites[].
// Reuses MarketFilterBar to filter and the shared RecordDetail to open a node.
const NET_MAX_NODES = 600; // guard so the graph never loads unboundedly as data grows
const ManagerNetwork = ({ onBack, goBack, activeBlocks }) => {
  const [businesses, setBusinesses] = useState([]);
  const [projects, setProjects] = useState([]);
  const [relationships, setRelationships] = useState([]);
  const [tiers, setTiers] = useState(SEED_TIERS);
  const [filters, setFilters] = useState({ side: 'all', phase: 'all', zone: 'all', q: '' });
  const [natF, setNatF] = useState('all');      // nationality lens for the graph
  const nat = useNationalities();
  const [detail, setDetail] = useState(null);   // { kind, record }
  const [truncated, setTruncated] = useState(0);
  const wrapRef = useRef(null);
  const [dim, setDim] = useState({ w: 0, h: 0 });

  useEffect(() => {
    const u1 = onSnapshot(collection(db, "businesses"), s => setBusinesses(s.docs.map(d => ({ id: d.id, ...d.data() }))), e => console.error("net businesses load:", e));
    const u2 = onSnapshot(collection(db, "projects"), s => setProjects(s.docs.map(d => ({ id: d.id, ...d.data() }))), e => console.error("net projects load:", e));
    const u3 = onSnapshot(collection(db, "relationships"), s => setRelationships(s.docs.map(d => ({ id: d.id, ...d.data() }))), e => console.error("net relationships load:", e));
    const u4 = onSnapshot(doc(db, "ai_config", "activity_tiers"), s => { if (s.exists()) setTiers({ ...SEED_TIERS, ...s.data() }); }, e => console.error("net tiers load:", e));
    return () => { u1(); u2(); u3(); u4(); };
  }, []);

  // Measure the container so the canvas fills it exactly (don't default to window size).
  useEffect(() => {
    const el = wrapRef.current; if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(entries => { for (const e of entries) setDim({ w: Math.floor(e.contentRect.width), h: Math.floor(e.contentRect.height) }); });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const bizById = useMemo(() => Object.fromEntries(businesses.map(b => [b.id, b])), [businesses]);
  const projById = useMemo(() => Object.fromEntries(projects.map(p => [p.id, p])), [projects]);
  const blockById = useMemo(() => Object.fromEntries((activeBlocks || []).map(b => [b.id, b])), [activeBlocks]);

  // Zone options (same derivation as the Market): block_id → block name.
  const zones = useMemo(() => {
    const zoneMap = {};
    projects.forEach(p => { if (p.block_id) zoneMap[p.block_id] = (blockById[p.block_id] && blockById[p.block_id].name) || p.zoneName || p.block_id; });
    businesses.forEach(b => (b.jobsites || []).forEach(j => { if (j.block_id) zoneMap[j.block_id] = (blockById[j.block_id] && blockById[j.block_id].name) || j.zoneName || j.block_id; }));
    return [{ value: 'all', label: 'All zones' }, ...Object.entries(zoneMap).map(([value, label]) => ({ value, label }))];
  }, [projects, businesses, blockById]);

  // Build the filtered graph. Memoized so the simulation only re-seeds on real changes.
  const graphData = useMemo(() => {
    const ql = filters.q.trim().toLowerCase();
    const bizOk = (b) =>
      (filters.side === 'all' || b.side === filters.side) &&
      (filters.phase === 'all' || (b.jobsites || []).some(j => j.phase === filters.phase)) &&
      (filters.zone === 'all' || (b.jobsites || []).some(j => j.block_id === filters.zone)) &&
      (natF === 'all' || (b.nationality || 'Unknown') === natF) &&
      (!ql || (b.businessName || '').toLowerCase().includes(ql) || (b.trade || '').toLowerCase().includes(ql) || (b.phone || '').toLowerCase().includes(ql) || (b.website || '').toLowerCase().includes(ql));
    const projOk = (p) =>
      (filters.phase === 'all' || p.phase === filters.phase) &&
      (filters.side === 'all' || (filters.side === 'demand' ? (p.demandBusinessIds || []).length : (p.supplyBusinessIds || []).length)) &&
      (filters.zone === 'all' || p.block_id === filters.zone) &&
      (!ql || (p.address || '').toLowerCase().includes(ql));

    let bz = businesses.filter(bizOk);
    // Nationality lens: scope the graph to the tagged businesses + ONLY the projects they
    // connect to (their jobsites) — highlights that community + its sites/edges.
    const connectedProjIds = natF === 'all' ? null : new Set(bz.flatMap(b => (b.jobsites || []).map(j => j.projectId).filter(Boolean)));
    let pr = projects.filter(p => projOk(p) && (!connectedProjIds || connectedProjIds.has(p.id)));
    let cut = 0;
    if (bz.length + pr.length > NET_MAX_NODES) { cut = bz.length + pr.length - NET_MAX_NODES; bz = bz.slice(0, NET_MAX_NODES); pr = pr.slice(0, Math.max(0, NET_MAX_NODES - bz.length)); }

    const inB = new Set(bz.map(b => b.id));
    const inP = new Set(pr.map(p => p.id));
    const nodes = [
      ...bz.map(b => ({ id: 'b:' + b.id, name: b.businessName || '(unnamed)', kind: 'business', side: b.side, val: Math.max(1, (b.jobsites || []).length), record: b })),
      ...pr.map(p => ({ id: 'p:' + p.id, name: p.address || 'Site', kind: 'project', val: 2, record: p })),
    ];
    const links = [];
    // business ↔ business (shared jobsite); weight = shared count.
    relationships.forEach(r => { if (inB.has(r.a) && inB.has(r.b)) links.push({ source: 'b:' + r.a, target: 'b:' + r.b, weight: r.weight || 1 }); });
    // business → project (from jobsites[]).
    bz.forEach(b => (b.jobsites || []).forEach(j => { if (j.projectId && inP.has(j.projectId)) links.push({ source: 'b:' + b.id, target: 'p:' + j.projectId, weight: 1 }); }));
    return { nodes, links, _cut: cut };
  }, [businesses, projects, relationships, filters, natF]);

  useEffect(() => { setTruncated(graphData._cut || 0); }, [graphData]);

  const openNode = (n) => { if (n && n.record) setDetail({ kind: n.kind, record: n.record }); };
  const nodeColor = (n) => n.kind === 'project' ? '#c084fc' : (n.side === 'demand' ? '#60a5fa' : '#fbbf24');
  const empty = graphData.nodes.length === 0;
  const detailPhotos = detail ? (detail.kind === 'project' ? (detail.record.photos || []) : (detail.record.jobsites || []).flatMap(j => (projById[j.projectId] || {}).photos || [])) : [];

  return (
    <div className="absolute inset-0 z-0 bg-gray-900 flex flex-col">
      <div className="flex items-center justify-between p-3 border-b border-gray-800 shrink-0">
        <div className="flex items-center"><Network className="text-orange-500 mr-2" size={20} /><span className="font-bold tracking-widest uppercase text-white text-sm">Network · 3D</span></div>
        <div className="flex items-center gap-3 text-xs font-bold"><button onClick={onBack} className="text-gray-300 hover:text-white">← Manager</button><button onClick={goBack} className="text-gray-400 hover:text-white">Exit</button></div>
      </div>

      <div className="shrink-0 px-3 pt-3 pb-2">
        <MarketFilterBar value={filters} onChange={setFilters} zones={zones} resultLabel={`${graphData.nodes.length} nodes · ${graphData.links.length} links`}>
          <select value={natF} onChange={e => setNatF(e.target.value)} className="bg-gray-800 text-white border border-gray-700 rounded px-2 py-1.5 text-xs outline-none"><option value="all">All nationalities</option>{['Unknown', ...nat.options.filter(o => o.toLowerCase() !== 'unknown')].map(o => <option key={o} value={o}>{o}</option>)}</select>
        </MarketFilterBar>
        <div className="flex flex-wrap items-center gap-3 mt-2 text-[10px] font-bold text-gray-500">
          <span className="flex items-center gap-1"><span className="inline-block w-2.5 h-2.5 rounded-full" style={{ background: '#60a5fa' }} /> Demand biz</span>
          <span className="flex items-center gap-1"><span className="inline-block w-2.5 h-2.5 rounded-full" style={{ background: '#fbbf24' }} /> Supply biz</span>
          <span className="flex items-center gap-1"><span className="inline-block w-2.5 h-2.5 rounded-full" style={{ background: '#c084fc' }} /> Project</span>
          <span className="text-gray-600">size = jobsites · click a node to open</span>
          {truncated > 0 && <span className="text-amber-400">⚠ showing first {NET_MAX_NODES} of {NET_MAX_NODES + truncated} nodes</span>}
        </div>
      </div>

      <div ref={wrapRef} className="flex-1 relative min-h-0">
        {empty ? (
          <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6">
            <Network className="text-gray-600 mb-3" size={36} />
            <div className="text-gray-300 font-bold mb-1">No network to show</div>
            <p className="text-gray-500 text-sm max-w-xs">Inject leads in Evaluation &amp; Sorting (or relax the filters) — businesses, projects and their relationships appear here as a graph.</p>
          </div>
        ) : (dim.w > 0 && dim.h > 0) && (
          <ForceGraph3D
            graphData={{ nodes: graphData.nodes, links: graphData.links }}
            width={dim.w}
            height={dim.h}
            backgroundColor="#0b1220"
            nodeLabel={n => `${n.name}${n.kind === 'business' ? ` · ${n.side || ''}` : ' · project'}`}
            nodeColor={nodeColor}
            nodeVal={n => n.val}
            nodeOpacity={0.9}
            linkColor={l => (l.weight || 1) >= 2 ? '#a78bfa' : '#475569'}
            linkWidth={l => Math.min(6, (l.weight || 1))}
            linkOpacity={0.55}
            warmupTicks={20}
            cooldownTicks={120}
            onNodeClick={openNode}
          />
        )}
      </div>

      {detail && (
        <RecordDetail
          key={detail.kind + ':' + detail.record.id}
          kind={detail.kind}
          record={detail.record}
          photos={detailPhotos}
          relationships={relationships}
          projById={projById}
          bizById={bizById}
          onClose={() => setDetail(null)}
          onOpenRecord={(k, r) => setDetail({ kind: k, record: r })}
        />
      )}
    </div>
  );
};

// ── COST (staff) — manager-facing unit economics. Data collectors only for now; built
// as generic per-entity rows + a roll-up total so it can grow into the full Figma
// "Users & Files" financial dashboard (other staff, income, clients, API providers).
// Rates live in the AI Control Tower (settings) — this view is read-only cost output.
const ManagerCost = ({ onBack, goBack }) => {
  const [sessions, setSessions] = useState([]);
  const [leads, setLeads] = useState([]);
  const [updateEvents, setUpdateEvents] = useState([]);
  const [rates, setRates] = useState(SEED_RATES);
  useEffect(() => {
    const us = onSnapshot(collection(db, "route_session"), s => setSessions(s.docs.map(d => ({ id: d.id, ...d.data() }))), e => console.error("cost sessions:", e));
    const ul = onSnapshot(collection(db, "leads"), s => setLeads(s.docs.map(d => ({ id: d.id, ...d.data() }))), e => console.error("cost leads:", e));
    const ue = onSnapshot(collection(db, "lead_update_events"), s => setUpdateEvents(s.docs.map(d => ({ id: d.id, ...d.data() }))), e => console.error("cost events:", e));
    const ur = onSnapshot(doc(db, "ai_config", "collector_rates"), s => { if (s.exists()) setRates({ ...SEED_RATES, ...s.data() }); }, e => console.error("cost rates:", e));
    return () => { us(); ul(); ue(); ur(); };
  }, []);
  const rows = collectorStats(sessions, leads, updateEvents).map(s => ({ ...s, e: computeEarnings(s, rates) }));
  const grand = rows.reduce((a, r) => a + r.e.total, 0);

  return (
    <div className="absolute inset-0 z-0 bg-gray-900 flex flex-col">
      <div className="flex items-center justify-between p-3 border-b border-gray-800 shrink-0">
        <div className="flex items-center"><DollarSign className="text-emerald-500 mr-2" size={20} /><span className="font-bold tracking-widest uppercase text-white text-sm">Cost · Staff</span></div>
        <div className="flex items-center gap-3 text-xs font-bold"><button onClick={onBack} className="text-gray-300 hover:text-white">← Manager</button><button onClick={goBack} className="text-gray-400 hover:text-white">Exit</button></div>
      </div>
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="text-[11px] font-bold text-gray-500 uppercase">Data collectors ({rows.length})</div>
          <div className="text-[10px] text-gray-500">Rates: {money(rates.perKm)}/km · {money(rates.perLeadGenerated)}/generated · {money(rates.perLeadUpdated)}/updated <span className="text-gray-600">(edit in AI Control Tower)</span></div>
        </div>

        {rows.length === 0 ? (
          <div className="text-center text-gray-500 text-sm py-10">No collector activity yet. Earnings appear once collectors pave km, submit, or update leads.</div>
        ) : (
          <div className="border border-gray-800 rounded-xl overflow-hidden">
            <div className="grid grid-cols-[1.4fr_1fr_1fr_1fr_0.9fr] gap-2 px-3 py-2 bg-gray-950 text-[9px] font-bold text-gray-500 uppercase">
              <span>Collector</span><span className="text-right">Distance</span><span className="text-right">Generated</span><span className="text-right">Updated</span><span className="text-right">Cost</span>
            </div>
            {rows.map(r => (
              <div key={r.name} className="grid grid-cols-[1.4fr_1fr_1fr_1fr_0.9fr] gap-2 px-3 py-2 border-t border-gray-800 text-[12px] items-center">
                <span className="text-white font-bold truncate">{r.name}</span>
                <span className="text-right text-gray-300">{r.km.toFixed(1)} km<br /><span className="text-[10px] text-gray-500">{money(r.e.kmPay)}</span></span>
                <span className="text-right text-gray-300">{r.generated}<br /><span className="text-[10px] text-gray-500">{money(r.e.genPay)}</span></span>
                <span className="text-right text-gray-300">{r.updated}<br /><span className="text-[10px] text-gray-500">{money(r.e.updPay)}</span></span>
                <span className="text-right text-emerald-400 font-bold">{money(r.e.total)}</span>
              </div>
            ))}
            <div className="grid grid-cols-[1.4fr_1fr_1fr_1fr_0.9fr] gap-2 px-3 py-2 border-t-2 border-gray-700 bg-gray-950 text-[12px] items-center">
              <span className="text-gray-400 font-bold uppercase text-[10px]">Total cost</span><span /><span /><span />
              <span className="text-right text-emerald-300 font-black">{money(grand)}</span>
            </div>
          </div>
        )}

        <p className="text-[10px] text-gray-600">Cost = each collector's earned pay (a cost to the org). Distance comes from GPS tracking — a known ~800 m jump bug means per-km cost is only as accurate as the distance until that's fixed. This is the seed of the broader financial dashboard; other staff, income, clients and API providers plug in as more entity rows later.</p>
      </div>
    </div>
  );
};

// ── INJECTION / FEEDER (field app → Supabase Service boards) ────────────────────────
// The Lead Feeder. Distributes field-collected records into the Supabase boards via the
// `inject` Edge Function (POST only — no Supabase keys in this frontend). Two streams:
// Phase Leads → customer phase board (New Input); Prospects → Our Service board (New Prospect).
const INJECT_URL = process.env.REACT_APP_SUPABASE_INJECT_URL || '';
const INJECT_ANON = process.env.REACT_APP_SUPABASE_ANON_KEY || '';
let INJECT_TARGETS = [];
try { INJECT_TARGETS = JSON.parse(process.env.REACT_APP_INJECT_TARGETS || '[]'); } catch (e) { INJECT_TARGETS = []; }

const phaseNum = (ph) => { if (ph === null || ph === undefined || ph === '') return null; const n = Number(String(ph).toLowerCase().replace(/^p/, '')); return Number.isInteger(n) && n >= 0 && n <= 6 ? n : null; };

const ManagerInjection = ({ onBack, goBack, activeBlocks }) => {
  const [projects, setProjects] = useState([]);
  const [businesses, setBusinesses] = useState([]);
  const [tab, setTab] = useState('leads');            // 'leads' | 'prospects'
  const [q, setQ] = useState('');
  const [phaseF, setPhaseF] = useState('all');
  const [zoneF, setZoneF] = useState('all');
  const [statusF, setStatusF] = useState('all');      // all | ready | blocked
  const [targetIdx, setTargetIdx] = useState(0);
  const [injectingKey, setInjectingKey] = useState(null);
  const [results, setResults] = useState({});         // _key -> { ok?, error?, deduped?, ... }
  const [lightbox, setLightbox] = useState(null);     // { urls, i }

  useEffect(() => {
    const u1 = onSnapshot(collection(db, 'projects'), s => setProjects(s.docs.map(d => ({ id: d.id, ...d.data() }))), e => console.error('inj projects load:', e));
    const u2 = onSnapshot(collection(db, 'businesses'), s => setBusinesses(s.docs.map(d => ({ id: d.id, ...d.data() }))), e => console.error('inj businesses load:', e));
    return () => { u1(); u2(); };
  }, []);

  const bizById = useMemo(() => Object.fromEntries(businesses.map(b => [b.id, b])), [businesses]);
  const projById = useMemo(() => Object.fromEntries(projects.map(p => [p.id, p])), [projects]);
  const blockById = useMemo(() => Object.fromEntries((activeBlocks || []).map(b => [b.id, b])), [activeBlocks]);
  const actor = (auth.currentUser && (auth.currentUser.displayName || auth.currentUser.email)) || 'field-manager';
  const target = INJECT_TARGETS[targetIdx] || null;

  // Derive Phase-Lead records from injected projects (demand side).
  const leadRecs = useMemo(() => projects.filter(p => !p.supabaseInjected && !p.sentToField).map(p => {
    const demandBiz = (p.demandBusinessIds || []).map(id => bizById[id]).filter(Boolean);
    const b = demandBiz[0];
    const gk = b && (b.gatekeepers || [])[0];
    const name = (gk && gk.name) || (b && b.businessName) || '';
    const phone = (gk && gk.phone) || (b && b.phone) || '';
    const gatekeeper_type = (b && b.businessName) ? 'business' : (name ? 'individual' : '');
    const cur = phaseNum(p.phase);
    const zone = (blockById[p.block_id] && blockById[p.block_id].name) || p.zoneName || '';
    const project_number = p.projectNumber || `FD-${String(p.id || '').slice(-6).toUpperCase()}`;
    const missing = [];
    if (!name) missing.push('name'); if (!phone) missing.push('number');
    if (cur === null) missing.push('phase'); if (!gatekeeper_type) missing.push('gatekeeper type');
    return { _key: 'p:' + p.id, id: p.id, kind: 'lead', name, phone, gatekeeper_type, current_phase: cur, zone, address: p.address || '', project_number, photos: photoUrls(p.photos), notes: (p.enrichmentData && p.enrichmentData.initialComment) || '', email: (gk && gk.email) || '', website: (b && b.website) || '', ready: missing.length === 0, missing };
  }), [projects, bizById, blockById]);

  // Derive Prospect records from prospect-flagged businesses (supply side).
  const prospectRecs = useMemo(() => businesses.filter(b => b.isProspect && !b.supabaseInjected && !b.sentToField).map(b => {
    const gk = (b.gatekeepers || [])[0];
    const name = (gk && gk.name) || b.businessName || '';
    const phone = (gk && gk.phone) || b.phone || '';
    const js = (b.jobsites || [])[0];
    const zone = (js && ((blockById[js.block_id] && blockById[js.block_id].name) || js.zoneName)) || '';
    const photos = (b.jobsites || []).flatMap(j => photoUrls((projById[j.projectId] || {}).photos)).slice(0, 8);
    const missing = [];
    if (!name) missing.push('name'); if (!phone) missing.push('number');
    return { _key: 'b:' + b.id, id: b.id, kind: 'prospect', name, phone, company: b.businessName || '', trade: b.trade || '', active_sites: (b.jobsites || []).length, zone, profile_id: `SUP-${String(b.id || '').slice(-6).toUpperCase()}`, photos, email: (gk && gk.email) || '', ready: missing.length === 0, missing };
  }), [businesses, blockById, projById]);

  const recs = tab === 'leads' ? leadRecs : prospectRecs;
  const ql = q.trim().toLowerCase();
  const zones = ['all', ...Array.from(new Set(recs.map(r => r.zone).filter(Boolean))).sort()];
  const vis = recs.filter(r =>
    (statusF === 'all' || (statusF === 'ready' ? r.ready : !r.ready)) &&
    (zoneF === 'all' || r.zone === zoneF) &&
    (tab !== 'leads' || phaseF === 'all' || r.current_phase === Number(phaseF)) &&
    (!ql || r.name.toLowerCase().includes(ql) || r.phone.toLowerCase().includes(ql) || r.address.toLowerCase().includes(ql) || (r.project_number || r.profile_id || '').toLowerCase().includes(ql))
  );
  const readyCount = recs.filter(r => r.ready).length;

  const setResult = (key, v) => setResults(m => ({ ...m, [key]: v }));

  const inject = async (r) => {
    if (!INJECT_URL || !INJECT_ANON) { setResult(r._key, { error: 'Inject endpoint not configured (set REACT_APP_SUPABASE_INJECT_URL + REACT_APP_SUPABASE_ANON_KEY).' }); return; }
    if (r.kind === 'lead' && !target) { setResult(r._key, { error: 'Pick a target customer account first.' }); return; }
    if (r.missing.length) { setResult(r._key, { error: 'Missing ' + r.missing.join(', ') }); return; }
    const payload = r.kind === 'lead'
      ? { stream: 'lead', project_number: r.project_number, target: { kind: 'phase', phase: r.current_phase, customer_account_id: target.id }, name: r.name, phone: r.phone, address: r.address, zone: r.zone, gatekeeper_type: r.gatekeeper_type, current_phase: r.current_phase, contact_base: { email: r.email || null, website: r.website || null }, photos_ref: r.photos, notes: r.notes, source: 'field', actor }
      : { stream: 'prospect', profile_id: r.profile_id, name: r.name, phone: r.phone, company: r.company, trade: r.trade, address: r.address, zone: r.zone, active_sites: r.active_sites, contact_base: { email: r.email || null }, photos_ref: r.photos, notes: r.notes || '', source: 'field_prospect', actor };
    setInjectingKey(r._key);
    try {
      const res = await fetch(INJECT_URL, { method: 'POST', headers: { 'Authorization': `Bearer ${INJECT_ANON}`, 'apikey': INJECT_ANON, 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.ok) { setResult(r._key, { error: data.error || `HTTP ${res.status}` }); }
      else {
        // Record the injection on the Firebase record so it leaves the ready list.
        await setDoc(doc(db, r.kind === 'lead' ? 'projects' : 'businesses', r.id), { supabaseInjected: { at: new Date().toISOString(), stream: r.kind, cardId: data.card_id || null, prospectId: data.prospect_id || null, injectionId: data.injection_id || null, boardId: data.board_id || null, by: actor } }, { merge: true });
        setResult(r._key, { ok: true, deduped: data.deduped });
      }
    } catch (e) { setResult(r._key, { error: e.message || String(e) }); }
    finally { setInjectingKey(null); }
  };

  // Send a blocked record back to the collector bucket (evaluation Type B).
  const sendToField = async (r) => {
    try {
      await setDoc(doc(db, r.kind === 'lead' ? 'projects' : 'businesses', r.id), { sentToField: { at: new Date().toISOString(), reason: 'Blocked at Injection — returned to collector (eval Type B)', missing: r.missing, by: actor } }, { merge: true });
    } catch (e) { setResult(r._key, { error: 'Send to field failed: ' + e.message }); }
  };

  const sel = "bg-gray-800 text-white border border-gray-700 rounded px-2 py-1.5 text-xs outline-none";
  return (
    <div className="absolute inset-0 z-0 bg-gray-900 flex flex-col">
      <div className="flex items-center justify-between p-3 border-b border-gray-800 shrink-0">
        <div className="flex items-center"><Send className="text-orange-500 mr-2" size={18} /><span className="font-bold tracking-widest uppercase text-white text-sm">Injection · the feeder</span></div>
        <div className="flex items-center gap-3 text-xs font-bold"><button onClick={onBack} className="text-gray-300 hover:text-white">← Manager</button><button onClick={goBack} className="text-gray-400 hover:text-white">Exit</button></div>
      </div>

      {!INJECT_URL && <div className="shrink-0 bg-amber-900/30 border-b border-amber-700/40 text-amber-200 text-[11px] px-3 py-2">⚠ Inject endpoint not configured — set <b>REACT_APP_SUPABASE_INJECT_URL</b>, <b>REACT_APP_SUPABASE_ANON_KEY</b>, and <b>REACT_APP_INJECT_TARGETS</b> (customer accounts) in the field app env, then rebuild. Records list below; Inject will error until configured.</div>}

      <div className="shrink-0 px-3 pt-3 space-y-2">
        <div className="flex gap-2 items-center">
          <button onClick={() => setTab('leads')} className={`px-3 py-1.5 rounded-lg text-xs font-bold border ${tab === 'leads' ? 'bg-white text-gray-900 border-white' : 'bg-gray-800 text-gray-300 border-gray-700'}`}>🎯 Phase Leads <span className="opacity-60">{leadRecs.length}</span></button>
          <button onClick={() => setTab('prospects')} className={`px-3 py-1.5 rounded-lg text-xs font-bold border ${tab === 'prospects' ? 'bg-white text-gray-900 border-white' : 'bg-gray-800 text-gray-300 border-gray-700'}`}>🤝 Prospects <span className="opacity-60">{prospectRecs.length}</span></button>
          <span className="ml-auto text-[11px] text-gray-500 font-bold">{vis.length} shown · {readyCount} ready</span>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search name / phone / address / #…" className={sel + ' flex-1 min-w-[160px]'} />
          {tab === 'leads' && <select value={phaseF} onChange={e => setPhaseF(e.target.value)} className={sel}><option value="all">All phases</option>{[0,1,2,3,4,5,6].map(n => <option key={n} value={n}>P{n} · {PHASE_LABEL['p' + n]}</option>)}</select>}
          <select value={zoneF} onChange={e => setZoneF(e.target.value)} className={sel}>{zones.map(z => <option key={z} value={z}>{z === 'all' ? 'All zones' : z}</option>)}</select>
          <select value={statusF} onChange={e => setStatusF(e.target.value)} className={sel}><option value="all">All</option><option value="ready">Ready</option><option value="blocked">Blocked</option></select>
        </div>
        {tab === 'leads' && (
          <div className="flex items-center gap-2 bg-gray-800 border border-gray-700 rounded-lg px-2.5 py-1.5">
            <span className="text-[10px] font-bold text-gray-500 uppercase">Injecting into →</span>
            {INJECT_TARGETS.length > 0 ? (
              <select value={targetIdx} onChange={e => setTargetIdx(Number(e.target.value))} className={sel}>{INJECT_TARGETS.map((t, i) => <option key={t.id || i} value={i}>{t.label || t.id}</option>)}</select>
            ) : <span className="text-[11px] text-amber-400 font-bold">No customer targets configured (REACT_APP_INJECT_TARGETS)</span>}
            {target && <span className="text-[11px] text-gray-300 font-bold">· each lead → its own phase board on <b className="text-white">{target.label || target.id}</b></span>}
          </div>
        )}
      </div>

      <div className="flex-1 overflow-y-auto p-3">
        {vis.length === 0 ? (
          <div className="text-center text-gray-500 text-sm py-10">{tab === 'leads' ? 'No demand leads in the Market to inject. Approve + inject leads in Evaluation → they appear here.' : 'No prospect-flagged businesses. Mark supply businesses as ⭐ prospect in the Market to feed them here.'}</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {vis.map(r => { const res = results[r._key]; const busy = injectingKey === r._key; return (
              <div key={r._key} className={`bg-gray-800 border rounded-xl p-3 ${r.ready ? 'border-gray-700' : 'border-amber-600/40'}`}>
                <div className="flex items-start justify-between gap-2 mb-1">
                  <div className="min-w-0">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <span className="font-mono text-[10px] text-gray-400 bg-gray-900 rounded px-1.5 py-0.5">{r.kind === 'lead' ? r.project_number : r.profile_id}</span>
                      {r.kind === 'lead' && r.current_phase !== null && <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-orange-500/20 text-orange-300">P{r.current_phase} · {PHASE_LABEL['p' + r.current_phase]}</span>}
                      {r.kind === 'lead' && r.gatekeeper_type && <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-gray-700 text-gray-300 capitalize">{r.gatekeeper_type}</span>}
                      {r.zone && <span className="text-[10px] text-gray-500">📍 {r.zone}</span>}
                    </div>
                    <div className="text-white font-bold text-sm mt-1 truncate">{r.name || <span className="text-amber-400">⚠ no name</span>}</div>
                    <div className="text-[12px] text-gray-300">{r.phone ? `📞 ${r.phone}` : <span className="text-amber-400">⚠ no number</span>}</div>
                    {r.address && <div className="text-[11px] text-gray-500 truncate">{r.address}</div>}
                    {r.kind === 'prospect' && <div className="text-[11px] text-gray-400 mt-0.5">{r.trade ? r.trade + ' · ' : ''}{r.active_sites} active site{r.active_sites === 1 ? '' : 's'}</div>}
                  </div>
                  <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded shrink-0 ${r.ready ? 'bg-green-500/20 text-green-300' : 'bg-amber-500/20 text-amber-300'}`}>{r.ready ? 'Ready' : 'Blocked'}</span>
                </div>
                {r.photos.length > 0 && (
                  <div className="flex gap-1.5 overflow-x-auto my-2">{r.photos.slice(0, 6).map((u, i) => <img key={i} src={u} alt="" onClick={() => setLightbox({ urls: r.photos, i })} className="w-12 h-12 object-cover rounded border border-gray-700 shrink-0 cursor-pointer hover:opacity-80" />)}</div>
                )}
                {r.notes && <div className="text-[11px] text-gray-400 mb-2">📝 {r.notes}</div>}
                {!r.ready && <div className="text-[11px] font-bold text-amber-400 mb-2">Missing: {r.missing.join(', ')}</div>}
                {res && (res.ok
                  ? <div className="text-[11px] font-bold text-green-400 mb-2">✓ Injected{res.deduped ? ' (updated existing)' : ''} → {r.kind === 'lead' ? 'New Input' : 'New Prospect'}</div>
                  : <div className="text-[11px] font-bold text-red-400 mb-2">✕ {res.error}</div>)}
                <div className="flex gap-2">
                  <button onClick={() => inject(r)} disabled={!r.ready || busy || (r.kind === 'lead' && !target)} className="flex-1 py-2 rounded-lg font-bold text-xs bg-orange-600 hover:bg-orange-700 text-white disabled:opacity-40 flex items-center justify-center gap-1">{busy ? <><Loader2 size={13} className="animate-spin" /> Injecting…</> : <><Send size={13} /> Inject{r.kind === 'lead' ? ' → phase board' : ' → prospect board'}</>}</button>
                  {!r.ready && <button onClick={() => sendToField(r)} className="py-2 px-3 rounded-lg font-bold text-xs bg-gray-700 hover:bg-gray-600 text-gray-200">Send to field</button>}
                </div>
              </div>
            ); })}
          </div>
        )}
      </div>
      {lightbox && <PhotoLightbox urls={lightbox.urls} i={lightbox.i} onClose={() => setLightbox(null)} onIndex={(n) => setLightbox(l => ({ ...l, i: n }))} />}
    </div>
  );
};

const WORKSPACE_TABS = [
  ['routes', 'Setting Routes'],
  ['collection', 'Data Collection'],
  ['evaluation', 'Evaluation & Sorting'],
  ['market', 'Market'],
  ['injection', 'Injection'],
  ['network', 'Network'],
  ['cost', 'Cost'],
  ['m2023', '2023 Market'],
  ['tower', 'AI Control Tower'],
];
const ManagerWorkspace = ({ goBack, jumpToCollector, activeBlocks }) => {
  const [tab, setTab] = useState('routes');
  return (
    <div className="absolute inset-0 z-0 bg-gray-900 flex flex-col">
      <div className="shrink-0 flex items-stretch bg-gray-950 border-b border-gray-800 overflow-x-auto">
        <div className="flex items-center px-3 shrink-0"><BrandLogo height={18} dark showWords={false} /></div>
        {WORKSPACE_TABS.map(([key, label]) => (
          <button key={key} onClick={() => setTab(key)} className={`px-4 py-3 text-xs font-bold uppercase tracking-wider whitespace-nowrap border-b-2 transition-colors ${tab === key ? 'border-orange-500 text-white bg-gray-900' : 'border-transparent text-gray-400 hover:text-white'}`}>{label}</button>
        ))}
        <button onClick={goBack} className="ml-auto px-4 py-3 text-xs font-bold text-gray-400 hover:text-white whitespace-nowrap">Exit</button>
      </div>
      <div className="flex-1 relative min-h-0">
        {tab === 'routes' && <ManagerWarRoom goBack={goBack} jumpToCollector={jumpToCollector} activeBlocks={activeBlocks} />}
        {tab === 'collection' && <ManagerDataCollection />}
        {tab === 'evaluation' && <ManagerEvaluation onBack={() => setTab('routes')} goBack={goBack} />}
        {tab === 'market' && <ManagerMarket onBack={() => setTab('routes')} goBack={goBack} activeBlocks={activeBlocks} />}
        {tab === 'injection' && <ManagerInjection onBack={() => setTab('routes')} goBack={goBack} activeBlocks={activeBlocks} />}
        {tab === 'network' && <ManagerNetwork onBack={() => setTab('routes')} goBack={goBack} activeBlocks={activeBlocks} />}
        {tab === 'cost' && <ManagerCost onBack={() => setTab('routes')} goBack={goBack} />}
        {tab === 'm2023' && <Market2023 onBack={() => setTab('routes')} goBack={goBack} />}
        {tab === 'tower' && <ManagerControlTower onBack={() => setTab('routes')} goBack={goBack} />}
      </div>
    </div>
  );
};

// --- 3. DATA COLLECTION MODULE ---
const MapCameraUpdater = ({ coords, pinDropped, activeBlock, isRouting, follow, fitPoints, fitKey, panReq }) => {
  const map = useMap();
  const fittedRef = useRef(null);
  const panHandledRef = useRef(null);
  // Route/pin framing. Defined BEFORE the pan effect so that on mount the one-time route
  // fit runs first and an explicit pan (recenter-after-submit) runs AFTER it → pan wins.
  useEffect(() => {
    if (!map) return;
    if (follow) {
      // Frame BOTH you and the whole route ONCE on Start (and on recenter). No continuous
      // follow — you can pan/zoom freely; the blue dot still moves as you go.
      if (fittedRef.current !== fitKey && fitPoints && fitPoints.length && window.google) {
        const b = new window.google.maps.LatLngBounds();
        fitPoints.forEach(p => b.extend(p));
        b.extend(follow);
        map.fitBounds(b, 64);
        fittedRef.current = fitKey;
      }
      return;
    }
    if (isRouting) { return; }
    else if (activeBlock && !pinDropped) { map.panTo(activeBlock.startPoint); map.setZoom(14); }
    else if (pinDropped && coords) { map.panTo(coords); map.setZoom(18); }
  }, [map, coords, pinDropped, activeBlock, isRouting, follow, fitPoints, fitKey]);
  // Explicit pan request (address search, recenter-after-submit) — one-time per key, and
  // LAST so it overrides the route fit above on the post-submit "continue route" path.
  useEffect(() => {
    if (!map || !panReq || panReq.key === panHandledRef.current) return;
    panHandledRef.current = panReq.key;
    map.panTo({ lat: panReq.lat, lng: panReq.lng }); map.setZoom(18);
  }, [map, panReq]);
  return null;
};
const TerritoryOverlay = ({ activeBlock, activeRouteIndex, progressIndex }) => {
  const map = useMap();
  useMapsLibrary('geometry'); // load geometry lib so route-distance labels compute
  useEffect(() => {
    if (!map || !activeBlock || !window.google) return;
    const overlays = [];
    // clickable:false so taps fall through to the map (drop a job-site pin inside the block).
    const drawArea = (pts) => { const p = new window.google.maps.Polygon({ paths: pts, clickable: false, strokeColor: activeBlock.color, strokeOpacity: 0.8, strokeWeight: 3, fillColor: activeBlock.color, fillOpacity: 0.10 }); p.setMap(map); overlays.push(p); };
    // Remaining = green, passed = navy blue (distinct from the live-dot blue); both white-cased
    // so they stay visible on satellite. clickable:false so taps reach the map.
    const drawLine = (pts, active) => {
      const color = active ? '#10b981' : '#1e3a8a';
      const w = active ? 6 : 5;
      const casing = new window.google.maps.Polyline({ path: pts, geodesic: true, clickable: false, strokeColor: '#ffffff', strokeOpacity: 0.95, strokeWeight: w + 4, zIndex: 1 });
      casing.setMap(map); overlays.push(casing);
      // White chevrons spaced along the line, pointing point1→2→…→N (the drawn sweep
      // direction) so the collector knows which way to drive. Follows the path order.
      const arrows = [{ icon: { path: window.google.maps.SymbolPath.FORWARD_CLOSED_ARROW, scale: 3.6, strokeColor: '#0b1220', strokeWeight: 1, fillColor: '#ffffff', fillOpacity: 1 }, offset: '0', repeat: '55px' }];
      const l = new window.google.maps.Polyline({ path: pts, geodesic: true, clickable: false, strokeColor: color, strokeOpacity: 1, strokeWeight: w, zIndex: 2, icons: arrows });
      l.setMap(map); overlays.push(l);
    };
    const isActive = (i) => activeRouteIndex == null || activeRouteIndex === i;
    if (Array.isArray(activeBlock.routes)) {
      // Current schema: block = area polygon, routes = [{name, points}]. Also tolerate the
      // earlier [[...]] shape where a route was a bare points array.
      if (activeBlock.block && activeBlock.block.length) drawArea(activeBlock.block);
      activeBlock.routes.forEach((r, i) => {
        const pts = routePtsOf(r);
        if (!pts.length) return;
        // While working this route, split it: covered portion (start→progress) grey, the
        // remaining portion (progress→end) bright green.
        if (isActive(i) && progressIndex != null && progressIndex > 0 && progressIndex < pts.length - 1) {
          drawLine(pts.slice(0, progressIndex + 1), false);
          drawLine(pts.slice(progressIndex), true);
        } else {
          drawLine(pts, isActive(i));
        }
      });
    } else if (Array.isArray(activeBlock.block)) {
      // Interim schema: block + single `route`.
      if (activeBlock.block.length) drawArea(activeBlock.block);
      if (activeBlock.route && activeBlock.route.length) drawLine(activeBlock.route, true);
    } else if (activeBlock.route && activeBlock.route.length) {
      // Legacy record: 'route' was the block outline — render it as the area.
      drawArea(activeBlock.route);
    }
    return () => overlays.forEach(o => o.setMap(null));
  }, [map, activeBlock, activeRouteIndex, progressIndex]);
  return null;
};
// (DirectionsOverlay removed — in-app DirectionsService is deprecated. Navigation now opens
//  the Google Maps app via a deep link to the route's start point. See "Navigate to Start".)

// One row in the collector's "My Submitted Leads" list. Editable only while the lead is
// still pre-evaluation (status 'new_lead'); once the manager processes it, it's locked so
// a collector edit can't override an approved record.
const SubmittedLeadRow = ({ lead, onOpen }) => {
  const editable = (lead.status || 'new_lead') === 'new_lead';
  const nPhotos = (lead.photos || []).length;
  return (
    <div className="border border-gray-200 rounded-lg p-2 flex items-center gap-2">
      <div className="min-w-0 flex-1">
        <div className="text-xs font-bold text-gray-800 truncate">{lead.address || 'No address'}</div>
        <div className="text-[10px] text-gray-500 truncate">{(lead.phase || 'p0').toUpperCase()} · {nPhotos} photo{nPhotos === 1 ? '' : 's'}{lead.siteStatus === 'stopped' ? ' · ⛔ stopped' : ''}</div>
      </div>
      {editable
        ? <button onClick={() => onOpen(lead)} className="px-3 py-1.5 rounded bg-blue-600 text-white text-xs font-bold shrink-0">Edit</button>
        : <span className="text-[10px] font-bold text-gray-400 border border-gray-200 rounded px-2 py-1 shrink-0">In evaluation · locked</span>}
    </div>
  );
};

const DataCollectionModule = ({ goBack, activeBlocks }) => {
  const [view, setView] = useState('dashboard'); const [loading, setLoading] = useState(false); const [loadingAI, setLoadingAI] = useState(false); const [isLocating, setIsLocating] = useState(false); const [pinDropped, setPinDropped] = useState(false); const [activeBlock, setActiveBlock] = useState(null); const [activeRouteIndex, setActiveRouteIndex] = useState(null);
  const [isRecording, setIsRecording] = useState(false);
  const [submitError, setSubmitError] = useState(''); // visible save-failure banner (alert() is unreliable on mobile Safari)
  const [converting, setConverting] = useState(false); // HEIC→JPEG conversion in progress

  // ── Route-session (DRAWN vs WORKED) ──────────────────────────────────────────
  const [collectorName, setCollectorName] = useState(() => { try { return (auth.currentUser && auth.currentUser.displayName) || localStorage.getItem('fdc_collector_name') || ''; } catch (e) { return ''; } });
  const updateName = (v) => { setCollectorName(v); try { localStorage.setItem('fdc_collector_name', v); } catch (e) {} };
  const [sessions, setSessions] = useState([]);      // all route_session docs (for badges)
  const [activeSession, setActiveSession] = useState(null); // the session being worked now
  const [leads, setLeads] = useState([]);            // leads (for "my leads" + earnings counts)
  const [updateEvents, setUpdateEvents] = useState([]); // lead_update_events (for updated count)
  const [rates, setRates] = useState(SEED_RATES);    // live pay rates from config
  const [detail, setDetail] = useState(null);        // a "my submitted lead" open in the full editor
  useEffect(() => {
    const unsub = onSnapshot(collection(db, "route_session"),
      (snap) => setSessions(snap.docs.map(d => ({ id: d.id, ...d.data() }))),
      (err) => console.error("Failed to load route sessions:", err));
    const ul = onSnapshot(collection(db, "leads"), s => setLeads(s.docs.map(d => ({ id: d.id, ...d.data() }))), e => console.error("leads load:", e));
    const ue = onSnapshot(collection(db, "lead_update_events"), s => setUpdateEvents(s.docs.map(d => ({ id: d.id, ...d.data() }))), e => console.error("update events load:", e));
    const ur = onSnapshot(doc(db, "ai_config", "collector_rates"), s => { if (s.exists()) setRates({ ...SEED_RATES, ...s.data() }); }, e => console.error("rates load:", e));
    return () => { unsub(); ul(); ue(); ur(); };
  }, []);

  // Saved session for the route currently selected by THIS collector (if any).
  const savedSession = (activeBlock && activeRouteIndex != null)
    ? sessions.find(s => s.block_id === activeBlock.id && s.routeIndex === activeRouteIndex && s.collector_name === collectorName)
    : null;
  const persistSession = (id, patch) => setDoc(doc(db, "route_session", id), { ...patch, updatedAt: serverTimestamp() }, { merge: true });

  const startSession = async () => {
    if (!collectorName.trim()) { alert("Enter your name first (on the dashboard)."); return; }
    if (!activeBlock || activeRouteIndex == null) return;
    // Deliberate start: confirm, so GPS tracking only begins when the collector means it.
    if (!window.confirm(`Start GPS tracking for Route ${activeRouteIndex + 1} from here?`)) return;
    // Request location permission RIGHT HERE inside the tap (iOS Safari ignores geolocation
    // asked for later from an effect, so the prompt never shows). This pops the prompt now;
    // the watchPosition effect then tracks continuously once it's granted.
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => setLiveLocation({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
        (err) => alert("Location is needed to track the route — please allow location access. (" + err.message + ")"),
        { enableHighAccuracy: true }
      );
    } else {
      alert("This browser doesn't support GPS location.");
    }
    const id = sessionKey(collectorName, activeBlock.id, activeRouteIndex);
    const base = { id, block_id: activeBlock.id, routeIndex: activeRouteIndex, collector_name: collectorName.trim(), status: 'in_progress', progressIndex: 0, kmPassed: 0, leadsGenerated: 0 };
    await setDoc(doc(db, "route_session", id), { ...base, startedAt: serverTimestamp(), updatedAt: serverTimestamp(), completedAt: null }, { merge: true });
    setActiveSession(base);
  };
  const resumeSession = () => {
    if (!savedSession) return; // the saved stop point becomes the new start point
    setActiveSession({ id: savedSession.id, block_id: savedSession.block_id, routeIndex: savedSession.routeIndex, collector_name: savedSession.collector_name, status: 'in_progress', progressIndex: savedSession.progressIndex || 0, kmPassed: savedSession.kmPassed || 0, leadsGenerated: savedSession.leadsGenerated || 0 });
  };
  const stopSession = async () => {
    if (!activeSession) return;
    await persistSession(activeSession.id, { progressIndex: activeSession.progressIndex, kmPassed: activeSession.kmPassed, leadsGenerated: activeSession.leadsGenerated, status: 'in_progress' });
    setActiveSession(null);
  };
  const completeSession = async () => {
    if (!activeSession) return;
    await persistSession(activeSession.id, { progressIndex: activeSession.progressIndex, kmPassed: activeSession.kmPassed, leadsGenerated: activeSession.leadsGenerated, status: 'done', completedAt: serverTimestamp() });
    setActiveSession(null);
  };

  // ── LIVE GPS TRACKING ────────────────────────────────────────────────────────
  // While a session is active, watch the device location: show "you are here" on the
  // route and auto-advance progress/km via the SAME advanceAlongRoute used by manual taps.
  const [liveLocation, setLiveLocation] = useState(null);
  const [recenterTick, setRecenterTick] = useState(0); // bump to re-frame route + you
  const [searchAddr, setSearchAddr] = useState('');     // address-search input
  const [panReq, setPanReq] = useState(null);           // {lat,lng,key} — camera pan request
  const doAddressSearch = async () => {
    if (!searchAddr.trim()) return;
    setIsLocating(true);
    try {
      const loc = await geocodeAddress(searchAddr);
      setFormData(prev => ({ ...prev, coords: { lat: loc.lat, lng: loc.lng }, address: loc.address }));
      setPinDropped(true);
      setPanReq({ lat: loc.lat, lng: loc.lng, key: (panReq ? panReq.key : 0) + 1 });
    } catch (e) { console.error(e); alert(e.message); } finally { setIsLocating(false); }
  };
  const sessionRef = useRef(null); sessionRef.current = activeSession;          // latest session
  const activeBlockRef = useRef(null); activeBlockRef.current = activeBlock;     // latest block
  const lastFixRef = useRef(null);  // last ACCEPTED fix { lat, lng, t } — for jump rejection
  useEffect(() => {
    if (!activeSession) { setLiveLocation(null); return; }
    if (!navigator.geolocation) { console.warn("Geolocation not supported."); return; }
    lastFixRef.current = null; // reset smoothing baseline when the worked session changes
    const watchId = navigator.geolocation.watchPosition(
      (pos) => {
        const acc = pos.coords.accuracy;
        // (1) Drop poor-accuracy fixes — these are what leap the position 100s of metres.
        if (typeof acc === 'number' && acc > GPS_ACCURACY_GATE_M) return;
        const coord = { lat: pos.coords.latitude, lng: pos.coords.longitude };
        const now = pos.timestamp || Date.now();
        const g = window.google && window.google.maps && window.google.maps.geometry;
        const last = lastFixRef.current;
        if (last && g) {
          const meters = g.spherical.computeDistanceBetween(new window.google.maps.LatLng(last.lat, last.lng), new window.google.maps.LatLng(coord.lat, coord.lng));
          const dt = (now - last.t) / 1000;
          // (2) Implausibly fast between fixes ⇒ phantom leap. Skip; wait for a better fix.
          if (dt > 0 && meters > GPS_MIN_MOVE_M && (meters / dt) > GPS_MAX_WALK_SPEED_MS) return;
        }
        lastFixRef.current = { lat: coord.lat, lng: coord.lng, t: now };
        setLiveLocation(coord);
        const s = sessionRef.current; const blk = activeBlockRef.current;
        if (!s || !blk || !Array.isArray(blk.routes)) return;
        const pts = routePtsOf(blk.routes[s.routeIndex]);
        const { progressIndex, kmPassed } = advanceAlongRoute(pts, coord, s.progressIndex);
        if (progressIndex !== s.progressIndex) {
          setActiveSession({ ...s, progressIndex, kmPassed });
          persistSession(s.id, { progressIndex, kmPassed });
        }
      },
      (err) => console.warn("GPS watch error:", err.message),
      { enableHighAccuracy: true, maximumAge: 4000, timeout: 20000 }
    );
    return () => navigator.geolocation.clearWatch(watchId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSession ? activeSession.id : null]);


  const [draftSupply, setDraftSupply] = useState({ trade: '', company: '', contact: '', phone: '', email: '', website: '', officeLocation: '', interaction: 'Visual (Truck/Sign)' });

  const [formData, setFormData] = useState({
    initialComment: '', address: '', coords: { lat: 43.766, lng: -79.423 }, phase: 'p0',
    siteStatus: 'active', // 'active' | 'stopped' — real-world site state, separate from phase
    cityData: { permit: '', value: '', status: '' },
    demand: { businessName: '', website: '', officeLocation: '', rating: '', contacts: [{ name: '', role: 'Site Super', phone: '', email: '' }] }, 
    supply: [],
    photos: [], audioUrl: '',
    enrichmentData: { ocr: '' }
  });

  const galleryInput = useRef(null);
  // In-app multi-shot camera (keep shooting, then add several at once)
  const videoRef = useRef(null); const streamRef = useRef(null); const canvasRef = useRef(null);
  const [camOpen, setCamOpen] = useState(false);
  const [camShots, setCamShots] = useState([]); // [{ blob, preview }] captured this session
  useEffect(() => { if (camOpen && streamRef.current && videoRef.current) { videoRef.current.srcObject = streamRef.current; videoRef.current.play().catch(() => {}); } }, [camOpen]);

  // 🎙️ VOICE NOTE via MediaRecorder (Web Speech API isn't supported on iOS Safari).
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const [audioStatus, setAudioStatus] = useState('');

  const handleMapTap = async (e) => {
    if (!e.detail.latLng) return;
    const lat = e.detail.latLng.lat; const lng = e.detail.latLng.lng;
    setIsLocating(true); setFormData(prev => ({ ...prev, coords: { lat, lng } })); setPinDropped(true);
    // If a route session is active, this site-tap advances progress along the drawn route
    // (same code path a GPS feed will use later).
    if (activeSession && activeBlock && activeRouteIndex != null) {
      const pts = routePtsOf(activeBlock.routes && activeBlock.routes[activeRouteIndex]);
      const { progressIndex, kmPassed } = advanceAlongRoute(pts, { lat, lng }, activeSession.progressIndex);
      if (progressIndex !== activeSession.progressIndex) {
        const updated = { ...activeSession, progressIndex, kmPassed };
        setActiveSession(updated);
        persistSession(activeSession.id, { progressIndex, kmPassed });
      }
    }
    try { const address = await reverseGeocode(lat, lng); setFormData(prev => ({ ...prev, address })); } catch (error) { console.error(error); alert(error.message); } finally { setIsLocating(false); }
  };
  const locateAndPin = () => { setIsLocating(true); if (navigator.geolocation) { navigator.geolocation.getCurrentPosition( async (pos) => { const currentLoc = { lat: pos.coords.latitude, lng: pos.coords.longitude }; setFormData(prev => ({ ...prev, coords: currentLoc })); setPinDropped(true); try { const address = await reverseGeocode(currentLoc.lat, currentLoc.lng); setFormData(prev => ({ ...prev, address })); } catch (error) { console.error(error); alert(error.message); } finally { setIsLocating(false); } }, (err) => { alert("Please enable GPS permissions."); setIsLocating(false); }, { enableHighAccuracy: true } ); } };
  // After submitting a lead, the collector has physically moved on — re-center the map on
  // their CURRENT live GPS so it follows them to the next site (Change A). No pin dropped.
  const recenterToLiveGPS = () => {
    // Pan immediately to the last known live position so the map centers on the collector
    // even before a fresh fix returns (the success→continue path mounts a fresh map).
    if (liveLocation) setPanReq(p => ({ lat: liveLocation.lat, lng: liveLocation.lng, key: (p ? p.key : 0) + 1 }));
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (pos) => { const lat = pos.coords.latitude, lng = pos.coords.longitude; setLiveLocation({ lat, lng }); setPanReq(p => ({ lat, lng, key: (p ? p.key : 0) + 1 })); },
      (err) => console.warn('recenter GPS failed:', err),
      { enableHighAccuracy: true, maximumAge: 0, timeout: 15000 }
    );
  };
  
  // ONE flat photo pile on the lead. Each entry = { url, name, takenAt } (image + metadata).
  // HOOK (enrichment phase): photos are stored UNSORTED — AI categorizes them
  // (Phase / Businesses / Other-Deficiency) AFTER submit, later. Capture only here.
  const uploadOnePhoto = async (data, name) => {
    let blob = data;
    let finalName = name;
    let contentType = (data && data.type) || 'image/jpeg';
    // HEIC/HEIF → JPEG in the browser, so the stored file renders everywhere AND Vision
    // can OCR it. Convert FIRST; only the converted JPEG is uploaded and referenced.
    if (isHeic(data, name)) {
      setConverting(true);
      try {
        const converted = await heic2any({ blob: data, toType: 'image/jpeg', quality: 0.85 });
        blob = Array.isArray(converted) ? converted[0] : converted;
        finalName = name.replace(/\.(heic|heif)$/i, '');
        if (!/\.jpe?g$/i.test(finalName)) finalName += '.jpg';
        contentType = 'image/jpeg';
      } finally { setConverting(false); }
    }
    const fileRef = ref(storage, `field_uploads/${finalName}`);
    const snapshot = await uploadBytes(fileRef, blob, { contentType });
    const url = await getDownloadURL(snapshot.ref);
    return { url, name: finalName, takenAt: new Date().toISOString() };
  };
  // Bulk gallery upload (multi-select existing images; HEIC handled in uploadOnePhoto).
  const handlePhotoUpload = async (e) => {
    if (e.target.files && e.target.files.length > 0) {
      setLoadingAI(true);
      const files = Array.from(e.target.files);
      const added = [];
      const failed = [];
      for (const file of files) {
        try { added.push(await uploadOnePhoto(file, `${Date.now()}_${file.name}`)); }
        catch (error) { console.error("Photo upload/convert error:", file.name, error); failed.push(file.name); }
      }
      if (added.length) setFormData(prev => ({ ...prev, photos: [...prev.photos, ...added] }));
      setLoadingAI(false);
      if (failed.length) setSubmitError(`Couldn't add ${failed.length} photo(s) (conversion/upload failed): ${failed.join(', ')}`);
      e.target.value = '';
    }
  };
  // In-app camera: open (permission in tap), shoot many, then upload all on Done.
  const stopCamStream = () => { if (streamRef.current) { streamRef.current.getTracks().forEach(t => t.stop()); streamRef.current = null; } };
  const openCamera = async () => {
    if (!navigator.mediaDevices) { alert("Camera isn't supported on this browser."); return; }
    try {
      // Request a WIDE site shot: high resolution rear camera, no forced crop/zoom (Fix #3 —
      // the capture was too tight). A wider frame captures the whole building and helps OCR.
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: { ideal: 'environment' }, width: { ideal: 1920 }, height: { ideal: 1080 } }, audio: false });
      // Some phones open the rear camera with a >1 zoom — reset it to the widest the lens allows.
      try {
        const track = stream.getVideoTracks()[0];
        const caps = track && track.getCapabilities && track.getCapabilities();
        if (caps && caps.zoom && typeof caps.zoom.min === 'number') await track.applyConstraints({ advanced: [{ zoom: caps.zoom.min }] });
      } catch (zoomErr) { /* zoom control not supported on this device — ignore */ }
      streamRef.current = stream; setCamShots([]); setCamOpen(true);
    } catch (e) { console.error(e); alert("Camera permission is needed to take photos."); }
  };
  const captureShot = () => {
    const v = videoRef.current; if (!v) return;
    const c = canvasRef.current || document.createElement('canvas');
    c.width = v.videoWidth || 1280; c.height = v.videoHeight || 720;
    c.getContext('2d').drawImage(v, 0, 0, c.width, c.height);
    c.toBlob((blob) => { if (blob) setCamShots(prev => [...prev, { blob, preview: URL.createObjectURL(blob) }]); }, 'image/jpeg', 0.85);
  };
  const cancelCamera = () => { stopCamStream(); camShots.forEach(s => URL.revokeObjectURL(s.preview)); setCamShots([]); setCamOpen(false); };
  const finishCamera = async () => {
    stopCamStream(); setCamOpen(false);
    const shots = camShots; setCamShots([]);
    if (!shots.length) return;
    setLoadingAI(true);
    const added = [];
    let failed = 0;
    for (let i = 0; i < shots.length; i++) {
      try { added.push(await uploadOnePhoto(shots[i].blob, `photo-${Date.now()}-${i}.jpg`)); }
      catch (e) { console.error("Camera upload error:", e); failed++; }
      URL.revokeObjectURL(shots[i].preview);
    }
    if (added.length) setFormData(prev => ({ ...prev, photos: [...prev.photos, ...added] }));
    setLoadingAI(false);
    if (failed) setSubmitError(`Couldn't upload ${failed} photo(s) from the camera.`);
  };

  const handleAddSupply = () => {
    if (!draftSupply.trade || !draftSupply.company) return;
    setFormData(prev => ({ ...prev, supply: [...prev.supply, draftSupply] }));
    setDraftSupply({ trade: '', company: '', contact: '', phone: '', email: '', website: '', officeLocation: '', interaction: 'Visual (Truck/Sign)' }); 
  };

  const addDemandContact = () => { setFormData(prev => ({ ...prev, demand: { ...prev.demand, contacts: [...prev.demand.contacts, { name: '', role: '', phone: '', email: '' }] } })); };
  const updateDemandContact = (index, field, value) => { const newContacts = [...formData.demand.contacts]; newContacts[index][field] = value; setFormData(prev => ({ ...prev, demand: { ...prev.demand, contacts: newContacts } })); };

  // 🎙️ Record a voice note with MediaRecorder; getUserMedia is called INSIDE the tap
  // (iOS Safari requires the permission request to be in the user gesture). On stop, the
  // audio uploads to Storage and its URL is saved on the lead (formData.audioUrl).
  // HOOK (enrichment phase): send audioUrl to Whisper to transcribe into notes — NOT here.
  const toggleRecording = async () => {
    if (isRecording) {
      try { if (mediaRecorderRef.current) mediaRecorderRef.current.stop(); } catch (e) {}
      setIsRecording(false);
      return;
    }
    if (!navigator.mediaDevices || !window.MediaRecorder) { alert("Audio recording isn't supported on this browser."); return; }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mime = window.MediaRecorder.isTypeSupported('audio/webm') ? 'audio/webm' : (window.MediaRecorder.isTypeSupported('audio/mp4') ? 'audio/mp4' : '');
      const mr = mime ? new window.MediaRecorder(stream, { mimeType: mime }) : new window.MediaRecorder(stream);
      audioChunksRef.current = [];
      mr.ondataavailable = (ev) => { if (ev.data && ev.data.size > 0) audioChunksRef.current.push(ev.data); };
      mr.onstop = async () => {
        stream.getTracks().forEach(t => t.stop());
        const blob = new Blob(audioChunksRef.current, { type: mr.mimeType || 'audio/webm' });
        setAudioStatus('Saving…');
        try {
          const ext = (mr.mimeType || '').includes('mp4') ? 'm4a' : 'webm';
          const fileRef = ref(storage, `field_audio/${Date.now()}.${ext}`);
          const snap = await uploadBytes(fileRef, blob);
          const url = await getDownloadURL(snap.ref);
          setFormData(prev => ({ ...prev, audioUrl: url }));
          setAudioStatus('Voice note saved ✓');
        } catch (e) { console.error("Audio upload failed:", e); setAudioStatus('Upload failed — try again'); }
      };
      mediaRecorderRef.current = mr;
      mr.start();
      setIsRecording(true);
      setAudioStatus('Recording…');
    } catch (e) { console.error("Mic error:", e); alert("Microphone permission is needed to record a voice note."); }
  };

  // 🔥 REAL FIREBASE DATABASE SAVE 🔥
  const handleSubmit = async () => {
    setSubmitError('');
    // Don't write the lead while photos are still uploading to Storage — otherwise the
    // doc gets written WITHOUT the in-flight photos (or with a half-built photos array).
    if (loadingAI) { setSubmitError('Photos are still uploading — wait a second, then submit.'); return; }
    if (camOpen) { setSubmitError('Finish or cancel the camera first (tap “Done”).'); return; }
    setLoading(true);
    try {
      // Tag the lead to its route session so it stays associated with the route across
      // stop/resume; bump the session's leadsGenerated count.
      const docRef = await addDoc(collection(db, "leads"), {
        ...formData,
        status: "new_lead",
        phaseSystem: CANONICAL_PHASE_VERSION, // collected under the canonical phase list
        zoneName: activeBlock?.name || "Unassigned Map Pin",
        block_id: activeSession ? activeSession.block_id : (activeBlock?.id || null),
        routeIndex: activeSession ? activeSession.routeIndex : null,
        routeSessionId: activeSession ? activeSession.id : null,
        // Who collected it — stamped on every lead (on-session or off) for attribution.
        collectorUid: (auth.currentUser && auth.currentUser.uid) || null,
        collector_name: collectorName || null,
        submittedBy: collectorName || null, // earnings attribution key (leads generated)
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      // Only treat it as saved if Firestore actually returned a committed doc reference.
      if (!docRef || !docRef.id) throw new Error("Write did not return a document id.");
      console.log("Lead saved:", docRef.id, `(${(formData.photos || []).length} photos)`);
      if (activeSession) {
        const leadsGenerated = (activeSession.leadsGenerated || 0) + 1;
        setActiveSession({ ...activeSession, leadsGenerated });
        persistSession(activeSession.id, { leadsGenerated });
      }
      setLoading(false);
      setView('success'); // advance ONLY after a confirmed write
    } catch (error) {
      // Surface the real failure: log it AND show a persistent banner (mobile Safari
      // silently drops alert() called after an await), and do NOT advance.
      console.error("Lead save failed:", error);
      setSubmitError(`Couldn't save lead: ${error?.message || error}`);
      setLoading(false);
    }
  };

  if (view === 'success') return (<div className="flex flex-col items-center justify-center h-full p-8 text-center bg-white"><CheckCircle size={64} className="text-green-500 mb-4"/><h2 className="text-2xl font-bold">Data Secured</h2>{activeSession && <p className="text-gray-500 mt-2 font-bold">Route {activeSession.routeIndex + 1} · {(activeSession.kmPassed || 0).toFixed(1)} km · {activeSession.leadsGenerated || 0} leads</p>}<button onClick={() => { setPinDropped(false); setAudioStatus(''); setSubmitError(''); setFormData(p => ({...p, address: '', initialComment: '', phase: 'p0', siteStatus: 'active', cityData: {permit:'', value:'', status:''}, demand: { businessName: '', website: '', officeLocation: '', rating: '', contacts: [{ name: '', role: 'Site Super', phone: '', email: '' }] }, supply: [], photos: [], audioUrl: '' })); setView('map'); recenterToLiveGPS(); }} className="px-8 py-4 bg-gray-900 text-white rounded-xl font-bold mt-6">{activeSession ? 'Continue Route — Next Site' : 'Back to Map'}</button>{activeSession && <p className="text-[11px] text-gray-400 mt-3">Returns you to your active route to keep sweeping.</p>}</div>);
  const firstName = (collectorName || 'Collector').split(' ')[0];
  const initials = (collectorName || 'MJ').split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();
  // Lifetime totals across this collector's sessions (shown in Profile).
  const mySessions = sessions.filter(s => s.collector_name === collectorName);
  const totalKm = mySessions.reduce((a, s) => a + (s.kmPassed || 0), 0);
  const totalLeads = mySessions.reduce((a, s) => a + (s.leadsGenerated || 0), 0);
  const doneCount = mySessions.filter(s => s.status === 'done').length;
  const inProgressCount = mySessions.filter(s => s.status === 'in_progress').length;
  // Earnings (from work we control). Counts use lead attribution (submittedBy / update events).
  const myLeads = leads.filter(l => (l.submittedBy || l.collector_name) === collectorName);
  const myGenerated = myLeads.length;
  const myUpdated = updateEvents.filter(u => u.collectorName === collectorName).length;
  const myEarnings = computeEarnings({ km: totalKm, generated: myGenerated, updated: myUpdated }, rates);

  // HOME — only the active section (Routes) for now; profile chip opens Profile.
  if (view === 'dashboard') return (
    <div className="h-full bg-gray-50 flex flex-col">
      <div className="flex-1 overflow-y-auto p-6">
        <div className="mb-4"><BrandLogo height={26} /></div>
        <h1 className="text-3xl font-black text-gray-900">Hello, {firstName}</h1>
        <p className="text-gray-500 mb-6">Field Collector</p>
        <button onClick={() => setView('map')} className="w-full bg-blue-600 text-white rounded-2xl shadow-[0_10px_25px_-5px_rgba(37,99,235,0.4)] flex flex-col items-center justify-center h-48 transition-transform active:scale-95"><Navigation size={44} className="mb-3" /><span className="text-2xl font-bold">Routes</span><span className="text-blue-200 text-sm mt-1">Open your assigned routes</span></button>
      </div>
      <div className="shrink-0 bg-white border-t border-gray-200 px-6 py-3 flex items-center justify-between">
        <button onClick={() => setView('map')} className="flex items-center gap-2 text-gray-600 font-bold text-sm"><MapPin size={20} className="text-blue-600" /> Map</button>
        <button onClick={() => setView('profile')} title="Profile" className="flex items-center gap-2"><div className="text-right leading-tight"><div className="text-xs font-bold text-gray-700 max-w-[90px] truncate">{firstName}</div><div className="text-[10px] text-gray-400 font-bold">Profile</div></div><div className="w-10 h-10 rounded-full bg-gray-900 text-white text-xs font-bold flex items-center justify-center">{initials}</div></button>
      </div>
    </div>
  );

  // PROFILE — inbox, balance (placeholder), sign out.
  if (view === 'profile') return (
    <div className="h-full bg-gray-50 flex flex-col">
      <div className="shrink-0 bg-white border-b border-gray-200 px-4 py-3 flex items-center">
        <button onClick={() => setView('dashboard')} className="text-gray-500 font-bold flex items-center text-sm"><ChevronRight className="rotate-180 mr-1" size={18} /> Home</button>
        <span className="font-bold text-gray-800 mx-auto pr-12">Profile</span>
      </div>
      <div className="flex-1 overflow-y-auto p-5 space-y-5">
        <div className="flex items-center gap-3">
          <div className="w-14 h-14 rounded-full bg-gray-900 text-white text-lg font-bold flex items-center justify-center">{initials}</div>
          <div className="min-w-0"><div className="font-black text-gray-900 text-lg truncate">{collectorName || 'Collector'}</div><div className="text-xs text-gray-500 truncate">{(auth.currentUser && auth.currentUser.email) || ''}</div></div>
        </div>
        <div className="bg-white rounded-2xl border border-gray-200 p-4">
          <span className="text-xs font-bold text-gray-400 uppercase tracking-wider">Your totals</span>
          <div className="grid grid-cols-2 gap-3 mt-2">
            <div className="bg-blue-50 rounded-xl p-3 text-center"><div className="text-2xl font-black text-blue-700">{totalKm.toFixed(1)}</div><div className="text-[11px] font-bold text-blue-500 uppercase">km worked</div></div>
            <div className="bg-green-50 rounded-xl p-3 text-center"><div className="text-2xl font-black text-green-700">{totalLeads}</div><div className="text-[11px] font-bold text-green-600 uppercase">leads</div></div>
          </div>
          <p className="text-[11px] text-gray-500 mt-2 text-center">{doneCount} route{doneCount === 1 ? '' : 's'} done · {inProgressCount} in progress</p>
        </div>
        <div className="bg-white rounded-2xl border border-gray-200 p-4">
          <span className="text-xs font-bold text-gray-400 uppercase tracking-wider">Blocks &amp; routes (published)</span>
          <div className="mt-2"><BlockRouteStats blocks={activeBlocks} dark={false} /></div>
        </div>
        <div className="bg-white rounded-2xl border border-gray-200 p-4">
          <span className="text-xs font-bold text-gray-400 uppercase tracking-wider">Earnings</span>
          <div className="text-3xl font-black text-green-600 mt-1">{money(myEarnings.total)}</div>
          <div className="mt-2 space-y-1 text-[12px] text-gray-600">
            <div className="flex justify-between"><span>{totalKm.toFixed(1)} km × {money(rates.perKm)}</span><b className="text-gray-800">{money(myEarnings.kmPay)}</b></div>
            <div className="flex justify-between"><span>{myGenerated} lead{myGenerated === 1 ? '' : 's'} generated × {money(rates.perLeadGenerated)}</span><b className="text-gray-800">{money(myEarnings.genPay)}</b></div>
            <div className="flex justify-between"><span>{myUpdated} update{myUpdated === 1 ? '' : 's'} × {money(rates.perLeadUpdated)}</span><b className="text-gray-800">{money(myEarnings.updPay)}</b></div>
          </div>
          <p className="text-[10px] text-gray-400 mt-2">Distance comes from GPS tracking (known ~800 m jump bug — per-km pay is only as accurate as the distance until that's fixed).</p>
        </div>
        <div className="bg-white rounded-2xl border border-gray-200 p-4">
          <span className="text-xs font-bold text-gray-400 uppercase tracking-wider">My submitted leads</span>
          {myLeads.length === 0 ? <p className="text-sm text-gray-400 text-center py-3">No leads submitted yet.</p> : (
            <div className="mt-2 space-y-2">
              {[...myLeads].sort((a, b) => ((a.status || 'new_lead') === 'new_lead' ? 0 : 1) - ((b.status || 'new_lead') === 'new_lead' ? 0 : 1)).slice(0, 30).map(l => <SubmittedLeadRow key={l.id} lead={l} onOpen={(lead) => setDetail(lead)} />)}
              {myLeads.length > 30 && <p className="text-[10px] text-gray-400 text-center">Showing 30 of {myLeads.length}.</p>}
            </div>
          )}
          <p className="text-[10px] text-gray-400 mt-2">Fix your own submissions before the manager evaluates them — tap Edit to open the full form. Once approved, a lead locks. Edits count toward your "leads updated" earnings.</p>
        </div>
        <div className="bg-white rounded-2xl border border-gray-200 p-4">
          <span className="text-xs font-bold text-gray-400 uppercase tracking-wider flex items-center"><MessageCircle size={14} className="mr-1" /> Inbox</span>
          <p className="text-sm text-gray-400 text-center py-4">No messages yet.</p>
        </div>
        <button onClick={doSignOut} className="w-full py-3.5 rounded-xl font-bold bg-gray-900 text-white">Sign out</button>
      </div>
      {detail && (
        <RecordDetail
          key={detail.id}
          kind="lead"
          record={detail}
          photos={detail.photos || []}
          actorName={collectorName}
          onClose={() => setDetail(null)}
        />
      )}
    </div>
  );

  // Collector drill-down: blocks → that block's routes → selected route's start/stop point.
  const blockRoutes = activeBlock && Array.isArray(activeBlock.routes) ? activeBlock.routes : [];
  const curRoutePts = activeRouteIndex != null ? routePtsOf(blockRoutes[activeRouteIndex]) : [];
  const workingThisRoute = !!(activeSession && activeSession.block_id === activeBlock?.id && activeSession.routeIndex === activeRouteIndex);
  const workingProgress = workingThisRoute ? activeSession.progressIndex : null;
  // The start marker sits at the current stop point while working (= the resume point).
  const selectedStart = (workingThisRoute && curRoutePts[activeSession.progressIndex])
    ? curRoutePts[activeSession.progressIndex]
    : (curRoutePts[0] || (activeBlock && activeBlock.startPoint));
  // The upcoming route point — a glanceable "walk this way" indicator (Fix #4).
  const nextIdx = curRoutePts.length > 1
    ? (workingThisRoute ? Math.min((activeSession.progressIndex || 0) + 1, curRoutePts.length - 1) : (activeRouteIndex != null ? 1 : -1))
    : -1;
  const nextPoint = nextIdx > 0 ? curRoutePts[nextIdx] : null;
  // This collector's session for route i (drives the chip status badges).
  const sessionFor = (i) => sessions.find(s => s.block_id === activeBlock?.id && s.routeIndex === i && s.collector_name === collectorName);
  // Live distance from your GPS to the route's start/stop point (until counting begins).
  const metersToStart = (liveLocation && selectedStart && window.google && window.google.maps.geometry)
    ? Math.round(window.google.maps.geometry.spherical.computeDistanceBetween(new window.google.maps.LatLng(liveLocation.lat, liveLocation.lng), new window.google.maps.LatLng(selectedStart.lat, selectedStart.lng)))
    : null;

  if (view === 'map') return (
    <APIProvider apiKey={GOOGLE_MAPS_API_KEY}>
      <div className="absolute inset-0 z-0 bg-white flex flex-col">
        {!activeBlock ? (
          <div className="bg-gray-900 text-white p-3 z-10 shadow-md shrink-0 flex items-center overflow-x-auto whitespace-nowrap hide-scrollbar"><button onClick={() => setView('dashboard')} className="text-xs font-bold text-gray-300 hover:text-white flex items-center mr-3 shrink-0"><ChevronRight size={14} className="rotate-180 mr-1" /> Home</button><span className="text-xs font-bold text-gray-400 mr-4 uppercase tracking-wider flex items-center"><MapPin size={14} className="mr-1"/> Blocks</span>{activeBlocks.length === 0 && <span className="text-xs text-gray-500 italic">No territories published yet</span>}{activeBlocks.map(block => ( <button key={block.id} onClick={() => { setActiveBlock(block); setActiveRouteIndex(null); }} className="mr-2 px-4 py-2 rounded-full text-sm font-bold border bg-gray-800 border-gray-700 hover:bg-gray-700 transition-all">{block.name} · {(Array.isArray(block.routes) ? block.routes.length : 0)} route{(Array.isArray(block.routes) && block.routes.length === 1) ? '' : 's'}</button> ))}</div>
        ) : (
          <div className="bg-gray-900 text-white p-3 z-10 shadow-md shrink-0">
            <div className="flex items-center mb-2"><button onClick={() => { setActiveBlock(null); setActiveRouteIndex(null); }} className="text-xs font-bold text-gray-300 hover:text-white flex items-center mr-3"><ChevronRight size={14} className="rotate-180 mr-1" /> Blocks</button><span className="font-bold text-sm">{activeBlock.name}</span></div>
            <div className="flex flex-col gap-1.5 max-h-40 overflow-y-auto">
              {blockRoutes.length === 0 && <span className="text-xs text-gray-500 italic">No routes in this block</span>}
              {blockRoutes.map((r, i) => { const ss = sessionFor(i); return (
                <button key={i} onClick={() => setActiveRouteIndex(i)} className={`w-full text-left px-3 py-2.5 rounded-lg text-sm font-bold border transition-all ${activeRouteIndex === i ? 'bg-green-600 border-green-600 text-white' : 'bg-gray-800 border-gray-700 hover:bg-gray-700 text-gray-200'}`}>
                  <span>{routeLabelNamed(i, r)}</span>
                  {ss && ss.status === 'in_progress' && <span className="block text-xs font-bold text-amber-300 mt-0.5">● In progress · {(ss.kmPassed || 0).toFixed(1)} km · {ss.leadsGenerated || 0} leads</span>}
                  {ss && ss.status === 'done' && <span className="block text-xs font-bold text-green-300 mt-0.5">✓ Done</span>}
                </button> ); })}
            </div>
          </div>
        )}
        <div className="flex-1 relative min-h-0">
          <Map defaultCenter={{ lat: 43.766, lng: -79.423 }} defaultZoom={13} mapId="blue_jays_field_map" gestureHandling="greedy" disableDefaultUI={false} onClick={handleMapTap}>
            <MapCameraUpdater coords={formData.coords} pinDropped={pinDropped} activeBlock={activeBlock} isRouting={false} follow={liveLocation} fitPoints={workingThisRoute ? curRoutePts : null} fitKey={workingThisRoute ? `${activeSession.id}#${recenterTick}` : null} panReq={panReq} />
            <TerritoryOverlay activeBlock={activeBlock} activeRouteIndex={activeRouteIndex} progressIndex={workingProgress} />
            {activeBlock && selectedStart && ( <AdvancedMarker position={selectedStart}><div className="bg-green-600 text-white px-3 py-1 rounded-md font-bold text-xs shadow-lg border-2 border-white mb-8">{workingThisRoute ? `HERE · Route ${activeRouteIndex + 1}` : (activeRouteIndex != null ? `START · Route ${activeRouteIndex + 1}` : 'START HERE')}</div></AdvancedMarker> )}
            {activeBlock && nextPoint && ( <AdvancedMarker position={nextPoint}><div className="bg-emerald-600 text-white px-2 py-1 rounded-md font-bold text-[10px] shadow-lg border-2 border-white flex items-center gap-1"><Navigation size={12} /> NEXT →</div></AdvancedMarker> )}
            {liveLocation && ( <AdvancedMarker position={liveLocation}><div className="w-5 h-5 bg-blue-600 rounded-full border-2 border-white" style={{ boxShadow: '0 0 0 6px rgba(37,99,235,0.25)' }} /></AdvancedMarker> )}
            {!activeBlock && activeBlocks.map((block) => ( <AdvancedMarker key={block.id} position={block.center} onClick={() => { setActiveBlock(block); setActiveRouteIndex(null); }}><div className="px-3 py-1.5 bg-gray-900 text-white font-bold text-xs rounded-full border-2 border-white shadow-xl hover:scale-105 transition-transform whitespace-nowrap max-w-[160px] truncate">{block.name}</div></AdvancedMarker> ))}
            {pinDropped && ( <AdvancedMarker position={formData.coords} draggable={true} onDragEnd={async (e) => { const lat = e.latLng.lat(); const lng = e.latLng.lng(); setFormData(prev => ({ ...prev, coords: { lat, lng } })); try { const address = await reverseGeocode(lat, lng); setFormData(prev => ({ ...prev, address })); } catch (err) { console.error(err); } }}><div className="bg-red-600 text-white p-2 rounded-full shadow-lg border-2 border-white"><MapPin size={24} /></div></AdvancedMarker> )}
          </Map>
          <button onClick={() => { if (workingThisRoute) setRecenterTick(t => t + 1); else locateAndPin(); }} title={workingThisRoute ? 'Recenter on route + you' : 'Find me'} className="absolute bottom-6 right-6 bg-white p-4 rounded-full shadow-2xl border border-gray-200 text-blue-600 hover:bg-blue-50 z-10">{isLocating ? <Loader2 className="animate-spin" size={24} /> : <Crosshair size={24} />}</button>
        </div>
        <div className="p-4 bg-white border-t border-gray-200 z-10 shrink-0 overflow-y-auto max-h-[55vh]">
          {/* Change F: the moment a pin is set, the address + Confirm button are the FIRST,
              immediately-visible element (no scrolling). Search drops below it. */}
          {pinDropped ? (
            <div className="mb-3">
              <div className="text-xs mb-2 flex items-center gap-1 min-w-0"><MapPin size={14} className="text-red-500 shrink-0" /><span className="font-bold text-gray-800 truncate">{formData.address || 'Pin set'}</span></div>
              <button onClick={() => setView('form')} className="w-full py-4 rounded-xl font-bold text-white shadow-md flex items-center justify-center transition-all bg-gray-900 hover:bg-black"><CheckCircle size={18} className="mr-2" /> Confirm Job Site Location</button>
            </div>
          ) : (
            <div className="text-xs mb-2 flex items-center gap-1 min-w-0"><MapPin size={14} className="text-blue-500 shrink-0" /><span className="text-gray-500">Tap the map or search to set the job site</span></div>
          )}
          <div className="flex gap-2 mb-1">
            <input value={searchAddr} onChange={e => setSearchAddr(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') doAddressSearch(); }} placeholder="Search address…" className="flex-1 min-w-0 bg-gray-50 border border-gray-300 rounded-lg px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-blue-500" />
            <button onClick={doAddressSearch} className="shrink-0 px-3 rounded-lg bg-gray-200 hover:bg-gray-300 text-gray-700 flex items-center">{isLocating ? <Loader2 size={18} className="animate-spin" /> : <SearchIcon size={18} />}</button>
          </div>

          {/* TRACKING — compact, below */}
          {activeRouteIndex != null && (
            <div className="mt-3 pt-3 border-t border-gray-100">
              {workingThisRoute ? (
                <div className="flex items-center gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-bold text-green-700">▶ Route {activeRouteIndex + 1} · {(activeSession.kmPassed || 0).toFixed(1)} km · {activeSession.leadsGenerated || 0} leads</div>
                    <div className="text-[11px] font-bold truncate">{!liveLocation ? <span className="text-amber-600">Waiting for GPS — allow location</span> : (activeSession.progressIndex === 0 && metersToStart != null) ? <span className="text-blue-600">{metersToStart} m to start</span> : <span className="text-green-600">Live tracking on</span>}</div>
                  </div>
                  <button onClick={stopSession} className="shrink-0 px-3 py-2 rounded-lg font-bold bg-gray-800 text-white text-xs">Stop</button>
                  <button onClick={completeSession} className="shrink-0 px-3 py-2 rounded-lg font-bold bg-green-600 text-white text-xs">Done</button>
                </div>
              ) : savedSession && savedSession.status === 'in_progress' ? (
                <div className="flex items-center gap-2"><div className="flex-1 text-xs text-gray-600 font-bold truncate">Paused · {(savedSession.kmPassed || 0).toFixed(1)} km · {savedSession.leadsGenerated || 0} leads</div><button onClick={resumeSession} className="shrink-0 px-3 py-2 rounded-lg font-bold bg-blue-600 text-white text-xs flex items-center"><Navigation size={14} className="mr-1" /> Resume</button></div>
              ) : savedSession && savedSession.status === 'done' ? (
                <div className="text-xs font-bold text-gray-600 flex items-center"><CheckCircle size={14} className="mr-1 text-green-600" /> Route complete · {(savedSession.kmPassed || 0).toFixed(1)} km · {savedSession.leadsGenerated || 0} leads</div>
              ) : (
                <button onClick={startSession} className="w-full py-2.5 rounded-lg font-bold bg-green-600 text-white text-sm flex items-center justify-center"><Navigation size={16} className="mr-1.5" /> Start Route {activeRouteIndex + 1}</button>
              )}
              {selectedStart && <button onClick={() => window.open(`https://www.google.com/maps/dir/?api=1&destination=${selectedStart.lat},${selectedStart.lng}&travelmode=driving`, '_blank')} className="w-full mt-2 py-2 rounded-lg font-bold bg-gray-100 hover:bg-gray-200 text-gray-700 text-xs flex items-center justify-center"><Navigation size={14} className="mr-1.5" /> Navigate to Start (Google Maps)</button>}
            </div>
          )}
        </div>
      </div>
    </APIProvider>
  );

  return (
    <div className="absolute inset-0 z-10 bg-white flex flex-col animate-fadeIn">
       {/* In-app multi-shot camera overlay: shoot several, then Done uploads them all. */}
       {camOpen && (
         <div className="fixed inset-0 z-50 bg-black flex flex-col">
           <video ref={videoRef} autoPlay playsInline muted className="flex-1 w-full object-contain bg-black" />
           {camShots.length > 0 && (
             <div className="flex gap-2 p-2 bg-black/80 overflow-x-auto">
               {camShots.map((s, i) => <img key={i} src={s.preview} alt="" className="w-14 h-14 object-cover rounded border border-white/40 shrink-0" />)}
             </div>
           )}
           <div className="p-5 bg-black flex items-center justify-between">
             <button onClick={cancelCamera} className="text-white font-bold px-4 py-2">Cancel</button>
             <button onClick={captureShot} className="w-16 h-16 rounded-full bg-white border-4 border-gray-400 active:scale-95" aria-label="Capture" />
             <button onClick={finishCamera} className="text-white font-bold px-4 py-2">Done ({camShots.length})</button>
           </div>
         </div>
       )}
       <div className="p-4 border-b flex justify-between items-start bg-white shadow-sm sticky top-0 z-20">
         <button onClick={() => setView('map')} className="text-gray-500 font-bold flex items-center mt-1"><ChevronRight className="rotate-180 mr-1"/> Map</button>
         <div className="flex flex-col items-end">
           <span className="font-bold text-gray-800 text-xs truncate max-w-[200px] text-right">{formData.address || "Unknown Location"}</span>
           <span className={`text-[10px] font-bold px-2 py-0.5 rounded mt-1 ${activeBlock ? 'bg-orange-100 text-orange-700 border border-orange-200' : 'bg-gray-100 text-gray-500 border border-gray-200'}`}>{activeBlock ? activeBlock.name : 'No Zone Assigned'}</span>
         </div>
       </div>
       
       <div className="flex-1 overflow-y-auto p-4 space-y-6 pb-32">
         {/* Cleaned City Data - No more fake permits */}
         <div className="bg-orange-50 p-4 rounded-xl border border-orange-100"><div className="flex justify-between items-center mb-3"><label className="text-xs font-bold text-orange-700 uppercase flex items-center"><Database size={14} className="mr-1"/> 1. City Data</label></div><div className="grid grid-cols-2 gap-3 text-sm"><div className="bg-white p-2 rounded border border-orange-100"><span className="text-xs text-orange-400 block">Permit #</span><span className="text-gray-400 italic">Pending API...</span></div><div className="bg-white p-2 rounded border border-orange-100"><span className="text-xs text-orange-400 block">Value</span><span className="text-gray-400 italic">Pending API...</span></div></div></div>
         
         <div className="bg-gray-50 p-4 rounded-xl border border-gray-200 relative">
             {loadingAI && (
               <div className="absolute inset-0 bg-white/80 z-10 flex flex-col items-center justify-center rounded-xl backdrop-blur-sm">
                  <Loader2 className="animate-spin text-blue-600 mb-2" size={32} />
                  <span className="text-sm font-bold text-blue-800">Uploading to Storage...</span>
               </div>
             )}
             
             <div className="flex justify-between items-center mb-3"><label className="text-xs font-bold text-gray-400 uppercase flex items-center">2. Photos ({formData.photos.length})</label></div>

             {/* One flat photo pile. "Take photos" = in-app multi-shot camera; "Gallery" =
                 bulk multi-select. Stored unsorted — AI categorizes later (enrichment). */}
             <input type="file" accept="image/*" multiple ref={galleryInput} className="hidden" onChange={handlePhotoUpload} />
             <div className="grid grid-cols-2 gap-2">
                <button onClick={openCamera} className="h-16 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center text-sm font-bold text-gray-600 bg-white"><Camera size={18} className="mr-2"/> Take photos</button>
                <button onClick={() => galleryInput.current.click()} className="h-16 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center text-sm font-bold text-gray-600 bg-white"><Plus size={18} className="mr-2"/> Gallery</button>
             </div>
             {formData.photos.length > 0 && (
               <div className="flex gap-2 mt-3 overflow-x-auto">
                 {formData.photos.map((p, i) => (
                   <div key={i} className="relative shrink-0">
                     <img src={p.url} alt={p.name || ''} className="w-16 h-16 object-cover rounded-lg border border-gray-200" />
                     <button onClick={() => setFormData(prev => ({ ...prev, photos: prev.photos.filter((_, idx) => idx !== i) }))} className="absolute -top-1.5 -right-1.5 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center"><X size={12} /></button>
                   </div>
                 ))}
               </div>
             )}
          </div>

          <div className="space-y-3">
             <label className="text-xs font-bold text-indigo-600 uppercase block flex items-center"><Layers size={14} className="mr-1"/> Phase Details</label>
             <select className="w-full p-3 bg-white border border-gray-200 rounded-lg text-sm font-bold text-gray-700 focus:outline-none focus:ring-2 focus:ring-indigo-500" value={formData.phase} onChange={e => setFormData({...formData, phase: e.target.value})}>
               {PHASES.map(([v, t]) => <option key={v} value={v}>{v.toUpperCase()} · {t}</option>)}
             </select>
             {/* Site Stopped — stalled/abandoned. Separate from phase (a stopped site keeps its phase). */}
             <button type="button" onClick={() => setFormData({...formData, siteStatus: formData.siteStatus === 'stopped' ? 'active' : 'stopped'})}
               className={`w-full p-3 rounded-lg text-sm font-bold border flex items-center justify-center gap-2 ${formData.siteStatus === 'stopped' ? 'bg-red-50 border-red-300 text-red-700' : 'bg-white border-gray-200 text-gray-600'}`}>
               <Ban size={14} /> {formData.siteStatus === 'stopped' ? 'Site marked STOPPED (stalled/abandoned)' : 'Mark site Stopped'}
             </button>
          </div>
          
          <div className="space-y-3 bg-blue-50 p-4 rounded-xl border border-blue-100">
             <label className="text-xs font-bold text-blue-700 uppercase block flex items-center"><Building size={14} className="mr-1"/> 3. Demand Side (Builders/Owners)</label>
             <input className="w-full p-3 bg-white border border-blue-200 rounded-lg text-sm" placeholder="Company / Builder Name" value={formData.demand.businessName} onChange={e => setFormData({...formData, demand: {...formData.demand, businessName: e.target.value}})} />
             
             <div className="grid grid-cols-2 gap-2">
               <div className="flex items-center bg-white border border-blue-200 rounded-lg px-2 overflow-hidden focus-within:ring-2 focus-within:ring-blue-500">
                  <Globe size={14} className="text-gray-400" />
                  <input className="w-full p-2 bg-transparent text-sm outline-none" placeholder="Website" value={formData.demand.website} onChange={e => setFormData({...formData, demand: {...formData.demand, website: e.target.value}})} />
               </div>
               <div className="flex items-center bg-white border border-blue-200 rounded-lg px-2 overflow-hidden focus-within:ring-2 focus-within:ring-blue-500">
                  <MapPin size={14} className="text-gray-400" />
                  <input className="w-full p-2 bg-transparent text-sm outline-none" placeholder="HQ Address" value={formData.demand.officeLocation} onChange={e => setFormData({...formData, demand: {...formData.demand, officeLocation: e.target.value}})} />
               </div>
             </div>

             <div className="mt-4 space-y-3">
               <label className="text-xs font-bold text-blue-600 uppercase">Personnel In Charge</label>
               {formData.demand.contacts.map((contact, index) => (
                 <div key={index} className="bg-white p-3 rounded-lg border border-blue-200 relative">
                   {index > 0 && <button onClick={() => { const newC = [...formData.demand.contacts]; newC.splice(index, 1); setFormData({...formData, demand: {...formData.demand, contacts: newC}}); }} className="absolute top-2 right-2 text-gray-400 hover:text-red-500"><X size={16}/></button>}
                   <div className="grid grid-cols-2 gap-2 mb-2"><input className="p-2 border border-gray-200 rounded text-sm" placeholder="Name" value={contact.name} onChange={e => updateDemandContact(index, 'name', e.target.value)} /><input className="p-2 border border-gray-200 rounded text-sm" placeholder="Role (e.g. Site Super)" value={contact.role} onChange={e => updateDemandContact(index, 'role', e.target.value)} /></div>
                   <div className="grid grid-cols-2 gap-2"><input className="p-2 border border-gray-200 rounded text-sm" placeholder="Phone" value={contact.phone} onChange={e => updateDemandContact(index, 'phone', e.target.value)} /><input className="p-2 border border-gray-200 rounded text-sm" placeholder="Email" value={contact.email} onChange={e => updateDemandContact(index, 'email', e.target.value)} /></div>
                 </div>
               ))}
               <button onClick={addDemandContact} className="text-xs font-bold text-blue-600 flex items-center mt-2 hover:underline"><Plus size={14} className="mr-1"/> Add Another Contact</button>
             </div>
          </div>
          
          <div className="space-y-3 bg-orange-50 p-4 rounded-xl border border-orange-100">
            <label className="text-xs font-bold text-orange-700 uppercase block flex items-center"><Hammer size={14} className="mr-1"/> 4. Supply Side (Contractors on Site)</label>
            <div className="bg-white p-3 rounded-lg border border-orange-200 space-y-2">
              <select className="w-full p-2 border border-gray-200 rounded text-sm bg-gray-50 text-gray-700 outline-none" value={draftSupply.interaction} onChange={e => setDraftSupply({...draftSupply, interaction: e.target.value})}>
                <option value="Visual (Truck/Sign)">Interaction: Visual Only (Saw Truck/Sign)</option>
                <option value="In-Person (Conversation)">Interaction: In-Person Conversation</option>
                <option value="Active Supplier">Interaction: Active Current Supplier</option>
              </select>
              <div className="grid grid-cols-2 gap-2"><input className="p-2 border border-gray-200 rounded text-sm bg-gray-50" placeholder="Trade (e.g. Plumber)" value={draftSupply.trade} onChange={e => setDraftSupply({...draftSupply, trade: e.target.value})} /><input className="p-2 border border-gray-200 rounded text-sm bg-gray-50" placeholder="Company Name" value={draftSupply.company} onChange={e => setDraftSupply({...draftSupply, company: e.target.value})} /></div>
              <div className="grid grid-cols-2 gap-2">
                 <div className="flex items-center bg-gray-50 border border-gray-200 rounded px-2"><Globe size={14} className="text-gray-400" /><input className="w-full p-2 bg-transparent text-sm outline-none" placeholder="Website" value={draftSupply.website} onChange={e => setDraftSupply({...draftSupply, website: e.target.value})} /></div>
                 <div className="flex items-center bg-gray-50 border border-gray-200 rounded px-2"><MapPin size={14} className="text-gray-400" /><input className="w-full p-2 bg-transparent text-sm outline-none" placeholder="Office Location" value={draftSupply.officeLocation} onChange={e => setDraftSupply({...draftSupply, officeLocation: e.target.value})} /></div>
              </div>
              <div className="grid grid-cols-2 gap-2"><input className="p-2 border border-gray-200 rounded text-sm bg-gray-50" placeholder="Contact Person" value={draftSupply.contact} onChange={e => setDraftSupply({...draftSupply, contact: e.target.value})} /><input className="p-2 border border-gray-200 rounded text-sm bg-gray-50" placeholder="Phone" value={draftSupply.phone} onChange={e => setDraftSupply({...draftSupply, phone: e.target.value})} /></div>
              <div className="flex items-center bg-gray-50 border border-gray-200 rounded px-2 mb-2"><Mail size={14} className="text-gray-400" /><input className="w-full p-2 bg-transparent text-sm outline-none" placeholder="Email Address" value={draftSupply.email} onChange={e => setDraftSupply({...draftSupply, email: e.target.value})} /></div>

              <button onClick={handleAddSupply} className="w-full bg-orange-600 text-white font-bold py-2 rounded text-sm hover:bg-orange-700 transition-colors">Add Contractor to Site</button>
            </div>
            
            <div className="space-y-2 mt-3">
              {formData.supply.map((c, i) => (
                <div key={i} className="flex justify-between p-3 bg-white rounded-lg border border-orange-200 text-sm shadow-sm relative pr-8">
                  <div>
                    <span className="text-orange-900 block font-bold">{c.trade}: {c.company}</span>
                    <span className="text-orange-600 text-[10px] font-bold uppercase tracking-wider">{c.interaction}</span>
                    {(c.website || c.officeLocation) && <span className="text-gray-500 text-xs block mt-1">{c.website} • {c.officeLocation}</span>}
                    {(c.contact || c.phone) && <span className="text-gray-500 text-xs block">{c.contact} • {c.phone} • {c.email}</span>}
                  </div>
                  <X size={16} className="text-gray-400 absolute top-3 right-3 hover:text-red-500 cursor-pointer" onClick={() => setFormData(p => ({...p, supply: p.supply.filter((_, idx) => idx !== i)}))}/>
                </div>
              ))}
            </div>
          </div>

          <div className="space-y-3 pb-4">
             <label className="text-xs font-bold text-teal-600 uppercase block flex items-center justify-between">
               <span className="flex items-center"><MessageCircle size={14} className="mr-1"/> Field Notes & Audio</span>
               {/* Quick Stopped toggle right by the note — same siteStatus flag as under Phase. */}
               <button type="button" onClick={() => setFormData({...formData, siteStatus: formData.siteStatus === 'stopped' ? 'active' : 'stopped'})} className={`flex items-center gap-1 px-2 py-1 rounded-md text-[11px] font-bold border normal-case ${formData.siteStatus === 'stopped' ? 'bg-red-50 border-red-300 text-red-700' : 'bg-white border-gray-200 text-gray-500'}`}><Ban size={12} /> {formData.siteStatus === 'stopped' ? 'Stopped' : 'Mark Stopped'}</button>
             </label>
             <div className="relative">
               <textarea className="w-full p-3 pb-12 bg-white border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500" rows="4" placeholder="Type notes, or tap the mic to record a voice note..." value={formData.initialComment} onChange={e => setFormData({...formData, initialComment: e.target.value})}></textarea>
               <button onClick={toggleRecording} className={`absolute bottom-3 right-3 p-2 rounded-full shadow-md transition-all ${isRecording ? 'bg-red-500 text-white animate-pulse' : 'bg-gray-100 text-gray-600 hover:bg-teal-50 hover:text-teal-600'}`}><Mic size={18} /></button>
             </div>
             {isRecording && <p className="text-[11px] text-red-500 font-bold animate-pulse">🎙️ Recording… tap the mic to stop & save</p>}
             {!isRecording && audioStatus && <p className="text-[11px] font-bold text-teal-600">{audioStatus}</p>}
             {formData.audioUrl && !isRecording && <audio controls src={formData.audioUrl} className="w-full mt-1 h-9" />}
          </div>
       </div>
       
       <div className="p-4 border-t bg-white fixed bottom-0 left-0 right-0 z-30">
         {submitError && <div className="mb-2 px-3 py-2 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm font-bold flex items-start"><X size={16} className="mr-1.5 mt-0.5 shrink-0" /><span>{submitError}</span></div>}
         {converting && <div className="mb-2 px-3 py-2 rounded-lg bg-amber-50 border border-amber-200 text-amber-700 text-xs font-bold flex items-center"><RefreshCw size={14} className="animate-spin mr-1.5" /> Converting iPhone photos to JPEG…</div>}
         {loadingAI && !converting && <div className="mb-2 px-3 py-2 rounded-lg bg-amber-50 border border-amber-200 text-amber-700 text-xs font-bold flex items-center"><RefreshCw size={14} className="animate-spin mr-1.5" /> Uploading photos… don’t submit yet.</div>}
         <button onClick={handleSubmit} disabled={loading || loadingAI || converting} className="w-full bg-gray-900 text-white font-bold py-4 rounded-xl shadow-lg flex items-center justify-center disabled:opacity-50">{loading ? <RefreshCw className="animate-spin mr-2"/> : <Save className="mr-2"/>} {loading ? 'Pushing to Database...' : ((loadingAI || converting) ? 'Waiting for photos…' : 'Submit Entry')}</button>
       </div>
    </div>
  );
};

export default function BlueJaysOnAirApp({ forceRole, onExit }) {
  const [role, setRole] = useState(forceRole || null);
  const [activeBlocks, setActiveBlocks] = useState([]);

  // Load published territories from Firestore in real time. Both the manager and
  // the collector read from the same collection, so a freshly published territory
  // appears on every device without a refresh, and survives reloads.
  useEffect(() => {
    const unsub = onSnapshot(
      collection(db, "territories"),
      (snap) => setActiveBlocks(snap.docs.map(d => ({ id: d.id, ...d.data() }))),
      (err) => console.error("Failed to load territories:", err)
    );
    return () => unsub();
  }, []);

  if (!role) {
    return (
      <div className="h-full w-full overflow-y-auto bg-gray-50">
        <div className="flex flex-col items-center justify-center min-h-full w-full p-6">
        <div className="mb-6"><BrandLogo height={48} /></div>
        <p className="text-gray-500 mb-12">Select your operating mode</p>
        <div className="w-full max-w-sm space-y-4">
          <button onClick={() => setRole('collector')} className="w-full bg-white border border-gray-200 p-6 rounded-2xl shadow-sm hover:shadow-md hover:border-blue-400 transition-all flex items-center text-left group"><div className="w-12 h-12 bg-blue-50 text-blue-600 rounded-full flex items-center justify-center mr-4 group-hover:bg-blue-600 group-hover:text-white transition-colors"><MapPin size={24} /></div><div><h3 className="font-bold text-gray-900 text-lg">Data Collector</h3><p className="text-sm text-gray-500">Field mapping & entry</p></div></button>
          <button onClick={() => setRole('manager')} className="w-full bg-white border border-gray-200 p-6 rounded-2xl shadow-sm hover:shadow-md hover:border-orange-400 transition-all flex items-center text-left group"><div className="w-12 h-12 bg-orange-50 text-orange-600 rounded-full flex items-center justify-center mr-4 group-hover:bg-orange-600 group-hover:text-white transition-colors"><Shield size={24} /></div><div><h3 className="font-bold text-gray-900 text-lg">Manager Workspace</h3><p className="text-sm text-gray-500">Routes · Collection · Evaluation · Market</p></div></button>
        </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full w-full bg-gray-100 font-sans text-gray-800 overflow-hidden">
      <div className="flex-1 flex flex-col overflow-hidden relative">
        {role === 'collector' ? <DataCollectionModule goBack={onExit || (() => setRole(null))} activeBlocks={activeBlocks} /> : <ManagerWorkspace goBack={onExit || (() => setRole(null))} jumpToCollector={() => setRole('collector')} activeBlocks={activeBlocks} />}
      </div>
    </div>
  );
}